// StarWest — Sistema operativo de la empresa
import { useState, useEffect, Fragment } from "react";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useAuth, api } from "./auth";
import { EMPRESA, cargarLogoPdf } from "./empresa";
import VerPagos from "./modulos/VerPagos";
import DocumentosOrden from "./modulos/DocumentosOrden";
import Pedidos from "./modulos/Pedidos";
import Clientes from "./modulos/Clientes";
import Inventario from "./modulos/Inventario";
import { STATUS_OC, statusOC } from "./modulos/statusOC";

// ─── UTILIDADES ───────────────────────────────────────────────────────────────
const STORAGE_KEY = "starwest_quotes_v1";
const mxn = (n) =>
  !isFinite(n) || isNaN(n)
    ? "—"
    : Number(n).toLocaleString("es-MX", {
        style: "currency",
        currency: "MXN",
        minimumFractionDigits: 2,
      });
// ─── PARSEO DE LIBRO EXCEL DE OCs ─────────────────────────────────────────────
// Detecta hojas que son OCs (excluye RESUMEN, MACHOTE, CASH FLOW, Proveedores)
const HOJAS_NO_OC = ["RESUMEN", "MACHOTE", "MACHOTE V2", "PROVEEDORES", "CASH FLOW"];

// Lee una celda del arreglo de filas por índice de fila/columna (0-based).
const leerCelda = (rows, r, c) => {
  const v = rows[r]?.[c];
  return v === undefined || v === null ? "" : v;
};
const formatearFechaCelda = (v) => {
  if (!v && v !== 0) return "";
  const d = v instanceof Date ? v : new Date(v);
  return isNaN(d) ? "" : d.toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const parseLibroOCs = (workbook) => {
  const articulos = [];
  for (const sheetName of workbook.SheetNames) {
    const upper = sheetName.toUpperCase();
    if (HOJAS_NO_OC.some((n) => upper.includes(n))) continue;
    const ws = workbook.Sheets[sheetName];
    if (!ws) continue;
    // Convert to array of arrays
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
    if (rows.length < 5) continue;

    // Encabezado de la orden: mismas celdas fijas que usa el machote v2 / las macros
    // de Excel (G2, C4, E4, G4, I4, C5, E5, G5, I5, C6, I6).
    let noOrden = String(leerCelda(rows, 1, 6) || "").trim();
    let fecha = formatearFechaCelda(leerCelda(rows, 3, 2));
    let manejadoPor = String(leerCelda(rows, 3, 4) || "").trim();
    let incoterm = String(leerCelda(rows, 3, 6) || "").trim();
    let anticipo = "";
    const advanceRaw = leerCelda(rows, 3, 8);
    if (advanceRaw !== "" && !isNaN(+advanceRaw)) {
      anticipo = +advanceRaw <= 1 ? +advanceRaw * 100 : +advanceRaw;
    }
    let puerto = String(leerCelda(rows, 4, 2) || "").trim();
    let consignee = String(leerCelda(rows, 4, 4) || "").trim();
    let notify = String(leerCelda(rows, 4, 6) || "").trim();
    let contenedorCbm = 0;
    const cbmRaw = leerCelda(rows, 4, 8);
    if (cbmRaw !== "" && !isNaN(+cbmRaw)) contenedorCbm = +cbmRaw;
    let pago = String(leerCelda(rows, 5, 2) || "").trim();
    let shippingDate = formatearFechaCelda(leerCelda(rows, 5, 8));

    // Respaldo por si la hoja no sigue el machote estándar: busca por etiquetas de texto
    // (solo rellena lo que no se haya podido leer por celda fija).
    if (!noOrden || !fecha || !contenedorCbm || !shippingDate) {
      for (let i = 0; i < Math.min(rows.length, 10); i++) {
        const row = rows[i] || [];
        for (let j = 0; j < row.length; j++) {
          const cell = String(row[j] || "").trim();
          if (!noOrden && cell.match(/No\.?\s*ORDEN|P\.O\.\s*NUMBER/i)) {
            const v = row[j + 1] || rows[i + 1]?.[j];
            if (v) noOrden = String(v).trim();
          }
          if (!fecha && cell.match(/^FECHA$/i)) {
            fecha = formatearFechaCelda(row[j + 1]);
          }
          if (!contenedorCbm && cell.match(/CONTENEDOR.*CBM|CBM.*CONTENEDOR/i)) {
            const v = row[j + 1];
            if (v && !isNaN(+v)) contenedorCbm = +v;
          }
          if (!shippingDate && cell.match(/SHIPPING\s*DATE/i)) {
            shippingDate = formatearFechaCelda(row[j + 1]);
          }
        }
      }
    }
    if (!noOrden) noOrden = sheetName;

    // Normaliza unidad (MTS/METERS → metros, KGS/KG → kg)
    const normUnidad = (u) => (/^K/i.test(String(u || "")) ? "kg" : "metros");

    // Find articles by row starting with ART_N in col A
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i] || [];
      const firstCell = String(row[0] || "").trim().toUpperCase();
      if (!firstCell.startsWith("ART_")) continue;

      // Article header row: ART_N | ARTÍCULO N | ITEM NAME | | | CLIENTE | client | REF. | ref
      const itemName = String(row[2] || "").trim();
      const cliente = String(row[6] || "").trim();
      const ref = String(row[8] || "").trim();

      // Data row (2 filas debajo del ART_): SUPPLIER | COMPOSITION | GSM | WIDTH | UNIT | PRICE/UNIT | CAP.CONTENEDOR
      const dataRow = rows[i + 2] || [];
      const supplier = String(dataRow[1] || "").trim();
      const composition = String(dataRow[2] || "").trim();
      const gsmRaw = String(dataRow[3] || "").trim();
      const widthRaw = String(dataRow[4] || "").trim();
      const unit = String(dataRow[5] || "").trim();
      const precioUnitario = +dataRow[6] || 0;
      let capContenedor = +dataRow[7] || 0;

      // Extract numeric parts (e.g. "260 GSM" → 260, "160 CMS" → 160)
      const gsm = parseFloat(gsmRaw) || 0;
      const width = parseFloat(widthRaw) || 0;
      const unidad = normUnidad(unit);

      // Fila de encabezados de color (3 filas debajo del ART_): # | DESIGN/DSN | COLOR | QTTY | UNIT | PRICE (USD) | INCOT. | TOTAL (USD)
      // Filas siguientes: un renglón por color hasta encontrar el SUB_ de este artículo.
      // Se ignoran los renglones numerados vacíos (plantilla sin llenar).
      const colores = [];
      let subRowIdx = -1;
      for (let j = i + 4; j < Math.min(rows.length, i + 60); j++) {
        const r = rows[j] || [];
        const first = String(r[0] || "").trim().toUpperCase();
        if (first.startsWith("SUB_")) {
          subRowIdx = j;
          break;
        }
        if (first.startsWith("ART_")) break; // no debería pasar, pero por seguridad
        const colorVal = String(r[3] || "").trim();
        const cantidadVal = +r[4] || 0;
        if (!colorVal && !cantidadVal) continue; // renglón plantilla vacío
        colores.push({
          numero: r[1] || colores.length + 1,
          diseno: String(r[2] || "").trim(),
          color: colorVal,
          cantidad: cantidadVal,
          unidad: r[5] ? normUnidad(r[5]) : unidad,
          precio: +r[6] || precioUnitario,
          incoterm: String(r[7] || "").trim() || incoterm || "FOB",
        });
        if (!capContenedor && r[8] === undefined) {
          // nada — placeholder para futura lógica si se requiere
        }
      }
      // Si por alguna razón no se detectó ningún color pero sí hay subtotal, usa el
      // subtotal como un único renglón de color (compatibilidad con formatos viejos).
      if (colores.length === 0 && subRowIdx >= 0) {
        const subRow = rows[subRowIdx] || [];
        const cantidadSub = +subRow[4] || 0;
        if (cantidadSub > 0) {
          colores.push({
            numero: 1,
            diseno: "",
            color: "",
            cantidad: cantidadSub,
            unidad,
            precio: precioUnitario,
            incoterm: incoterm || "FOB",
          });
        }
      }

      const cantidadTotal = colores.reduce((s, c) => s + (+c.cantidad || 0), 0);
      const totalUSD = colores.reduce((s, c) => s + (+c.cantidad || 0) * (+c.precio || 0), 0);
      const fobPromedio = cantidadTotal > 0 ? totalUSD / cantidadTotal : precioUnitario;

      if (itemName && supplier) {
        articulos.push({
          _key: `${sheetName}::${itemName}::${supplier}`,
          hoja: sheetName,
          noOrden,
          fecha,
          shippingDate,
          contenedorCbm,
          manejadoPor,
          incoterm,
          anticipo,
          puerto,
          consignee,
          notify,
          pago,
          cliente,
          proveedor: supplier,
          tela: itemName,
          ref,
          composicion: composition,
          peso: gsm,
          ancho: width,
          unidad,
          precioUnitario,
          capContenedor,
          colores,
          // Campos "planos" derivados — se mantienen por compatibilidad con vistas
          // que aún no distinguen color por color (lista de Resumen, respaldo Excel).
          cantidad: cantidadTotal,
          fobUSD: fobPromedio,
        });
      }
    }
  }
  return articulos;
};

// ─── CUENTAS POR PAGAR (libro de facturas por proveedor) ─────────────────────
// Misma lógica que la hoja "PROVEEDORES" del libro de macros: fórmulas exactas
// verificadas celda por celda (Total Real, C1, Vence, Pagado, Pendiente, Estado).
// Solo se conservan en el backend los campos "crudos" (cantidades, fechas, pagos);
// todo lo derivado (incluyendo Estado) se calcula aquí para que reaccione al
// instante cuando se registra un pago dentro de la app.
const parseFechaDDMMYYYY = (s) => {
  if (!s) return null;
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(String(s).trim());
  if (!m) return null;
  const d = new Date(+m[3], +m[2] - 1, +m[1]);
  return isNaN(d.getTime()) ? null : d;
};

// Replica exacta de las fórmulas de TablaProveedores en la hoja PROVEEDORES:
//   Total Pedido = Cant.Pedida * Precio
//   Total Real   = SI Cant.Embarcada>0 ENTONCES Cant.Embarcada*Precio SINO Total Pedido
//   C1 (1%)      = Total Real * 0.01
//   Vence        = F.Embarque + Plazo (días), si ambos existen
//   Pagado       = Monto1 + Monto2 + Monto3
//   Pendiente    = Total Real - Anticipo - C1 - Pagado
//   Estado       = PAGADA si Pendiente<=0.01 y (Pagado+Anticipo)>0
//                  si no, PARCIAL si Pagado>0
//                  si no, VENCIDA si Vence existe y ya pasó
//                  si no, AL DÍA
const calcularFacturaCxP = (f) => {
  const cantPedida = +f.cantPedida || 0;
  const cantEmbarcada = +f.cantEmbarcada || 0;
  const precio = +f.precio || 0;
  const anticipo = +f.anticipo || 0;
  const plazo = +f.plazo || 0;
  const monto1 = +f.monto1 || 0;
  const monto2 = +f.monto2 || 0;
  const monto3 = +f.monto3 || 0;

  const totalPedido = cantPedida * precio;
  const totalReal = cantEmbarcada > 0 ? cantEmbarcada * precio : totalPedido;
  const c1 = totalReal * 0.01;
  const pagado = monto1 + monto2 + monto3;
  const pendiente = totalReal - anticipo - c1 - pagado;

  const fEmbarqueDate = parseFechaDDMMYYYY(f.fEmbarque);
  let vence = null;
  if (fEmbarqueDate && plazo) {
    vence = new Date(fEmbarqueDate.getTime());
    vence.setDate(vence.getDate() + plazo);
  }
  const hoyMidnight = new Date();
  hoyMidnight.setHours(0, 0, 0, 0);

  let estado;
  if (pendiente <= 0.01 && pagado + anticipo > 0) {
    estado = "PAGADA";
  } else if (pagado > 0) {
    estado = "PARCIAL";
  } else if (vence && vence < hoyMidnight) {
    estado = "VENCIDA";
  } else {
    estado = "AL DÍA";
  }

  return {
    ...f,
    totalPedido,
    totalReal,
    c1,
    pagado,
    pendiente,
    vence,
    venceStr: vence ? formatearFechaCelda(vence) : "",
    estado,
  };
};

// Encabezados de la hoja PROVEEDORES → nombre de campo interno (por si el usuario
// reordena columnas, se busca por texto de encabezado, no por posición fija).
// ─── MEZCLA Y SINCRONIZACIÓN DEL LIBRO DE OCs ────────────────────────────────
// Regla de mezcla POR ORDEN (hoja): las órdenes que vienen en el Excel se
// reemplazan completas y las que no vienen (p. ej. capturadas en "Nueva OC") se
// conservan tal cual.
const hojaDeArticulo = (a) => (a.hoja || a.noOrden || "").trim();

const aplicarLibroOCs = async (itemsLibro, fileName, extra = {}) => {
  const actual = await api.get("/oc-workbook").catch(() => null);
  const existentes = Array.isArray(actual?.articulos) ? actual.articulos : [];
  // Lo que se canceló o eliminó desde la app no lo deshace el Excel.
  const mayus = (x) => String(x || "").trim().toUpperCase();
  const eliminadas = new Set((actual?.ocs_eliminadas || []).map(mayus));
  const canceladas = new Set((actual?.ocs_canceladas || []).map(mayus));
  const items = itemsLibro
    .filter((a) => !eliminadas.has(mayus(hojaDeArticulo(a))) && !eliminadas.has(mayus(a.noOrden)))
    .map((a) => (canceladas.has(mayus(hojaDeArticulo(a))) || canceladas.has(mayus(a.noOrden)) ? { ...a, cancelado: true } : a));
  const hojasExistentes = new Set(existentes.map(hojaDeArticulo).filter(Boolean));
  const hojasNuevas = new Set(items.map(hojaDeArticulo).filter(Boolean));
  const conservados = existentes.filter((a) => !hojasNuevas.has(hojaDeArticulo(a)));
  const merged = [...conservados, ...items];
  await api.post("/oc-workbook", { articulos: merged, file_name: fileName, ...extra });
  const nuevas = [...hojasNuevas].filter((h) => !hojasExistentes.has(h)).length;
  return { merged, fileName, nuevas, actualizadas: hojasNuevas.size - nuevas, leidos: items.length };
};

// El libro vive en Dropbox: el backend lo descarga del link compartido y solo
// manda el contenido si cambió desde la última vez que se aplicó (hash). Si ya
// hay una sincronización en curso, se reutiliza en vez de lanzar otra.
let syncDropboxEnCurso = null;
const sincronizarLibroDropbox = ({ forzar = false } = {}) => {
  if (syncDropboxEnCurso) return syncDropboxEnCurso;
  syncDropboxEnCurso = (async () => {
    const actual = await api.get("/oc-workbook").catch(() => null);
    if (!actual?.dropbox_url) return { configurado: false, cambio: false };
    const conocido = forzar ? "" : actual.dropbox_hash || "";
    const r = await api.get(`/oc-workbook/dropbox/archivo?conocido=${encodeURIComponent(conocido)}`);
    if (!r.cambio) return { configurado: true, cambio: false };
    const wb = XLSX.read(r.contenido_b64, { type: "base64", cellDates: true });
    const items = parseLibroOCs(wb);
    if (items.length === 0) throw new Error("el archivo de Dropbox no tiene hojas de OC (ART_ / SUB_)");
    const res = await aplicarLibroOCs(items, r.file_name, { dropbox_hash: r.hash });
    return { configurado: true, cambio: true, ...res };
  })().finally(() => {
    syncDropboxEnCurso = null;
  });
  return syncDropboxEnCurso;
};

const ENCABEZADOS_CXP = [
  ["proveedor", /^PROVEEDOR$/i],
  ["orderNo", /^Order\s*No\.?$/i],
  ["subProveedor", /^Sub-?\s*Proveedor$/i],
  ["tela", /^Tela$/i],
  ["cliente", /^Cliente$/i],
  ["fPedido", /^F\.?\s*Pedido$/i],
  ["fEmbarque", /^F\.?\s*Embarque$/i],
  ["cantPedida", /^Cant\.?\s*Pedida$/i],
  ["unidad", /^Unidad$/i],
  ["cantEmbarcada", /^Cant\.?\s*Embarcada$/i],
  ["precio", /^Precio$/i],
  ["anticipo", /^Anticipo$/i],
  ["fAnticipo", /^F\.?\s*Anticipo$/i],
  ["plazo", /^Plazo$/i],
  ["fPago1", /^F\.?\s*Pago\s*1$/i],
  ["monto1", /^Monto\s*1$/i],
  ["fPago2", /^F\.?\s*Pago\s*2$/i],
  ["monto2", /^Monto\s*2$/i],
  ["fPago3", /^F\.?\s*Pago\s*3$/i],
  ["monto3", /^Monto\s*3$/i],
  ["refProveedor", /^Ref\.?\s*Proveedor$/i],
  ["ocOriginal", /^OC\s*Original$/i],
];

// Lee la hoja PROVEEDORES del libro de macros y regresa solo las facturas
// "activas": Parcial, Vencida o Al día (excluye las ya Pagadas), tal como pidió
// el usuario. El Estado se recalcula aquí mismo (misma fórmula del Excel) para
// no depender del valor cacheado que traiga el archivo.
const parseLibroCxP = (workbook) => {
  const hoja = workbook.SheetNames.find((n) => n.trim().toUpperCase() === "PROVEEDORES");
  if (!hoja) return [];
  const ws = workbook.Sheets[hoja];
  if (!ws) return [];
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });

  let headerIdx = -1;
  for (let i = 0; i < Math.min(rows.length, 20); i++) {
    const row = rows[i] || [];
    const hasProveedor = row.some((c) => /^PROVEEDOR$/i.test(String(c || "").trim()));
    const hasOrderNo = row.some((c) => /^Order\s*No\.?$/i.test(String(c || "").trim()));
    if (hasProveedor && hasOrderNo) {
      headerIdx = i;
      break;
    }
  }
  if (headerIdx < 0) return [];

  const headerRow = rows[headerIdx] || [];
  const col = {};
  headerRow.forEach((cell, idx) => {
    const txt = String(cell || "").trim();
    for (const [key, re] of ENCABEZADOS_CXP) {
      if (col[key] === undefined && re.test(txt)) col[key] = idx;
    }
  });
  if (col.proveedor === undefined || col.orderNo === undefined) return [];

  const get = (row, key) => (col[key] !== undefined ? row[col[key]] : "");
  const num = (v) => (v === "" || v === undefined || v === null || isNaN(+v) ? 0 : +v);
  const txt = (v) => String(v ?? "").trim();

  const facturas = [];
  for (let i = headerIdx + 1; i < rows.length; i++) {
    const row = rows[i] || [];
    const proveedor = txt(get(row, "proveedor"));
    const orderNo = txt(get(row, "orderNo"));
    if (!proveedor || !orderNo) continue;
    const tela = txt(get(row, "tela"));
    const factura = {
      _key: `${orderNo}::${tela}::${proveedor}::${i}`,
      fuente: "PROVEEDORES (Excel)",
      proveedor,
      orderNo,
      subProveedor: txt(get(row, "subProveedor")),
      tela,
      cliente: txt(get(row, "cliente")),
      fPedido: formatearFechaCelda(get(row, "fPedido")),
      fEmbarque: formatearFechaCelda(get(row, "fEmbarque")),
      cantPedida: num(get(row, "cantPedida")),
      unidad: txt(get(row, "unidad")),
      cantEmbarcada: num(get(row, "cantEmbarcada")),
      precio: num(get(row, "precio")),
      anticipo: num(get(row, "anticipo")),
      fAnticipo: formatearFechaCelda(get(row, "fAnticipo")),
      plazo: num(get(row, "plazo")),
      fPago1: formatearFechaCelda(get(row, "fPago1")),
      monto1: num(get(row, "monto1")),
      fPago2: formatearFechaCelda(get(row, "fPago2")),
      monto2: num(get(row, "monto2")),
      fPago3: formatearFechaCelda(get(row, "fPago3")),
      monto3: num(get(row, "monto3")),
      refProveedor: txt(get(row, "refProveedor")),
      ocOriginal: txt(get(row, "ocOriginal")),
    };
    const calc = calcularFacturaCxP(factura);
    if (calc.estado === "PAGADA") continue; // solo Parcial, Vencida, Al día
    facturas.push(factura);
  }
  return facturas;
};

const facturaCxPVacia = () => ({
  _key: "",
  fuente: "Manual",
  proveedor: "",
  orderNo: "",
  subProveedor: "",
  tela: "",
  cliente: "",
  fPedido: "",
  fEmbarque: "",
  cantPedida: "",
  unidad: "MTS",
  cantEmbarcada: "",
  precio: "",
  anticipo: "",
  fAnticipo: "",
  plazo: "",
  fPago1: "",
  monto1: "",
  fPago2: "",
  monto2: "",
  fPago3: "",
  monto3: "",
  refProveedor: "",
  ocOriginal: "",
});

const ESTADO_ESTILO_CXP = {
  PAGADA: "bg-emerald-50 text-emerald-700 border-emerald-200",
  PARCIAL: "bg-amber-50 text-amber-700 border-amber-200",
  VENCIDA: "bg-rose-50 text-rose-700 border-rose-200",
  "AL DÍA": "bg-sky-50 text-sky-700 border-sky-200",
};

const ImportarLibroOC = ({ onImport, onBulkImport, quotes }) => {
  const [open, setOpen] = useState(false);
  const [articulos, setArticulos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [fileName, setFileName] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [bulking, setBulking] = useState(false);
  const [bulkMsg, setBulkMsg] = useState("");

  // Auto-cargar libro guardado del backend al montar
  useEffect(() => {
    (async () => {
      try {
        const data = await api.get("/oc-workbook");
        if (data && Array.isArray(data.articulos) && data.articulos.length > 0) {
          setArticulos(data.articulos);
          setFileName(data.file_name || "libro guardado");
          setUpdatedAt(data.updated_at || null);
        }
      } catch (e) {
        // silencioso
      }
      // Si el libro está conectado a Dropbox, trae los cambios automáticamente
      try {
        const r = await sincronizarLibroDropbox();
        if (r?.cambio) {
          setArticulos(r.merged);
          setFileName(r.fileName || "libro de Dropbox");
          setUpdatedAt(new Date().toISOString());
        }
      } catch (e) {
        console.error("No se pudo sincronizar el libro con Dropbox:", e);
      }
    })();
  }, []);

  // Combina lo recién leído del Excel con lo que ya esté guardado, en vez de
  // reemplazar todo el libro — así no se pierden órdenes creadas manualmente en
  // la app (o subidas en otro archivo) que no vengan en este Excel.
  //
  // El reemplazo se hace POR ORDEN (hoja), no artículo por artículo por _key:
  // si una orden ya existía, se descartan TODOS sus artículos guardados y se
  // reemplazan por los que traiga el Excel para esa misma orden. Antes se
  // mezclaba por _key (hoja+tela+proveedor) y si en Excel renombrabas una tela/
  // proveedor, borrabas un artículo o cambiabas colores, el artículo viejo se
  // quedaba como "fantasma" en vez de corregirse — por eso los cambios a
  // órdenes ya existentes no se veían reflejados aunque las nuevas sí cargaban.
  const persistir = async (items, name) => {
    try {
      const actual = await api.get("/oc-workbook").catch(() => null);
      const existentes = Array.isArray(actual?.articulos) ? actual.articulos : [];
      const hojasNuevas = new Set(items.map((n) => (n.hoja || n.noOrden || "").trim()).filter(Boolean));
      const conservados = existentes.filter((a) => !hojasNuevas.has((a.hoja || a.noOrden || "").trim()));
      const merged = [...conservados, ...items];
      const saved = await api.post("/oc-workbook", {
        articulos: merged,
        file_name: name,
      });
      if (saved?.updated_at) setUpdatedAt(saved.updated_at);
      return merged;
    } catch (e) {
      console.error("No se pudo guardar el libro:", e);
      return items;
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    setFileName(file.name);
    setBulkMsg("");
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array", cellDates: true });
      const items = parseLibroOCs(wb);
      if (items.length === 0) {
        setArticulos(items);
        alert("No se detectaron órdenes de compra en el archivo. Revisa que el formato tenga hojas con ART_ y SUB_.");
      } else {
        const merged = await persistir(items, file.name);
        setArticulos(merged);
      }
    } catch (err) {
      alert("Error al leer el archivo: " + err.message);
    }
    setLoading(false);
  };

  const handleBulkImport = async () => {
    if (!onBulkImport || articulos.length === 0 || bulking) return;
    setBulking(true);
    setBulkMsg("");
    const { nuevos, omitidos, invalidos } = filtrarArticulosParaCotizar(articulos, quotes);
    const result = await onBulkImport(nuevos);
    setBulking(false);
    const partes = [`✅ ${result?.added ?? nuevos.length} cotizaciones nuevas cargadas`];
    if (omitidos > 0) partes.push(`${omitidos} ya existían`);
    if (invalidos > 0) partes.push(`${invalidos} sin datos suficientes`);
    setBulkMsg(partes.join(" · "));
  };

  const filtered = search
    ? articulos.filter((a) =>
        [a.noOrden, a.cliente, a.proveedor, a.tela, a.ref].some((v) =>
          (v || "").toLowerCase().includes(search.toLowerCase())
        )
      )
    : articulos;

  const pick = (art) => {
    onImport(art);
    setOpen(false);
  };

  const fmtUpdated = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch {
      return "";
    }
  };

  const tieneLibroGuardado = articulos.length > 0 && !!updatedAt;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="importar-oc-btn"
        className="w-full py-2.5 rounded-xl bg-white border-2 border-dashed border-teal-400 text-teal-700 text-sm font-medium hover:bg-teal-50 transition-colors flex items-center justify-center gap-2"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
        </svg>
        {tieneLibroGuardado
          ? `Libro guardado · ${articulos.length} OCs`
          : "Importar desde libro de OCs"}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-3"
          onClick={() => setOpen(false)}
          data-testid="importar-oc-modal"
        >
          <div
            className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-teal-700 uppercase tracking-wider">Libro de OCs</p>
                <p className="text-sm text-slate-600">
                  {tieneLibroGuardado
                    ? `📚 ${articulos.length} artículos · actualizado ${fmtUpdated(updatedAt)}`
                    : "Sube tu libro Excel (.xlsx o .xlsm)"}
                </p>
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600 text-2xl leading-none px-2">×</button>
            </div>

            <div className="p-4 border-b border-slate-100 space-y-3">
              <label className="cursor-pointer flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                <span className="text-2xl">📁</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800">
                    {loading
                      ? "Procesando…"
                      : tieneLibroGuardado
                        ? "Reemplazar libro"
                        : fileName || "Elegir archivo Excel"}
                  </p>
                  <p className="text-xs text-slate-500">
                    {articulos.length > 0
                      ? `${articulos.length} artículos · ${fileName || "libro guardado"}`
                      : ".xlsx o .xlsm con hojas de OC"}
                  </p>
                </div>
                <input
                  type="file"
                  accept=".xlsx,.xlsm,.xls"
                  onChange={handleFile}
                  className="hidden"
                  data-testid="importar-oc-file"
                />
              </label>

              {articulos.length > 0 && onBulkImport && (
                <button
                  type="button"
                  onClick={handleBulkImport}
                  disabled={bulking}
                  data-testid="importar-oc-bulk-btn"
                  className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${
                    bulking
                      ? "bg-slate-200 text-slate-500 cursor-wait"
                      : "bg-teal-600 text-white hover:bg-teal-700"
                  }`}
                >
                  {bulking ? "Cargando cotizaciones…" : `⬆️ Subir todas al cotizador (${articulos.length})`}
                </button>
              )}

              {bulkMsg && (
                <p
                  data-testid="importar-oc-bulk-msg"
                  className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2"
                >
                  {bulkMsg}
                </p>
              )}
            </div>

            {articulos.length > 0 && (
              <>
                <div className="p-3 border-b border-slate-100">
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar OC, cliente, proveedor, tela…"
                    data-testid="importar-oc-search"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                  />
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-1">
                  {filtered.map((a) => (
                    <button
                      key={a._key}
                      type="button"
                      onClick={() => pick(a)}
                      data-testid={`importar-oc-item-${a._key}`}
                      className="w-full text-left p-3 rounded-lg hover:bg-teal-50 border border-transparent hover:border-teal-200 transition-colors"
                    >
                      <div className="flex justify-between items-start gap-2">
                        <p className="text-sm font-semibold text-slate-800 truncate">{a.tela}</p>
                        <span className="text-[10px] font-medium text-teal-700 bg-teal-50 px-2 py-0.5 rounded whitespace-nowrap">{a.noOrden}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 truncate">
                        {a.cliente || "—"} · {a.proveedor}
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {a.cantidad?.toLocaleString("es-MX")} {a.unidad} · USD {a.fobUSD} · {a.peso} g/m² · {a.ancho} cm
                      </p>
                    </button>
                  ))}
                  {filtered.length === 0 && (
                    <p className="text-center text-sm text-slate-400 py-6">Sin resultados</p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const hoy = () =>
  new Date().toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

// Defaults del cotizador (editables en cada cotización).
const COSTO_IMPORTACION_DEFAULT = 280000;
const FLETE_DEFAULT = 100000;
const TASA_FIN_DEFAULT = "16";
const DIAS_FIN_DEFAULT = "180";

// Cantidad sobre la que se reparte el costo: la capacidad del contenedor cuando
// se capturó; si no, la cantidad del pedido.
const cantidadParaCosto = (capContenedor, cantidad) => (+capContenedor > 0 ? +capContenedor : +cantidad || 0);

// Convierte un artículo del libro de OCs a una cotización Simple lista para guardar.
// Usa los defaults del cotizador (TC 17.50, impuestos 280k, flete 100k, utilidad 25%).
const articuloAQuote = (a, seq = 0) => {
  const fob = +a.fobUSD || 0;
  const qty = cantidadParaCosto(a.capContenedor, a.cantidad);
  const tc = 17.5;
  const ci = COSTO_IMPORTACION_DEFAULT;
  const ft = FLETE_DEFAULT;
  const util = 25;
  const peso = +a.peso || 0;
  const ancho = +a.ancho || 0;
  const fobMXN = fob * qty * tc;
  const total = fobMXN + ci + ft;
  const cxu = qty > 0 ? total / qty : 0;
  const rendimiento =
    a.unidad === "kg" && peso > 0 && ancho > 0
      ? 100000 / (peso * ancho)
      : 0;
  const cxMetro = rendimiento > 0 ? cxu / rendimiento : 0;
  const pv = util > 0 && util < 100 ? cxu / (1 - util / 100) : 0;
  const pvMetro = rendimiento > 0 && pv > 0 ? pv / rendimiento : 0;
  return {
    id: Date.now() + seq,
    fecha: hoy(),
    modo: "Simple",
    nombre: a.cliente || "",
    numeroOrden: a.noOrden || "",
    tela: a.tela ? `${a.tela}${a.ref ? " · " + a.ref : ""}` : "",
    itemProveedor: a.ref || "",
    peso,
    ancho,
    composicion: a.composicion || "",
    proveedor: a.proveedor || "",
    contenedor: "40'HC",
    unidad: a.unidad || "kg",
    cantidad: +a.cantidad || 0,
    fobUSD: fob,
    tipoCambio: tc,
    fobTotalMXN: fobMXN,
    costoImportacion: ci,
    fleteTerrestre: ft,
    financiamiento: false,
    tasaAnual: 0,
    dias: 0,
    financiamientoMXN: 0,
    totalCosto: total,
    costoPorUnidad: cxu,
    rendimiento,
    costoPorMetro: cxMetro,
    utilidad: util,
    precioVenta: pv,
    precioVentaMetro: pvMetro,
    tcArribo: null,
    capContenedor: +a.capContenedor || null,
    precioVentaManual: null,
    costoMostrar: a.unidad === "kg" ? "kg" : "metro",
    status: "Borrador",
  };
};

// Misma clave de "ya existe" que usa el importador de libro de OCs del cotizador,
// para no crear una cotización duplicada de un artículo que ya tiene una.
const dedupKeyCotizacion = (numeroOrden, itemProveedor, tela) =>
  `${(numeroOrden || "").trim().toLowerCase()}::${(itemProveedor || "").trim().toLowerCase()}::${(tela || "").trim().toLowerCase()}`;

// Filtra los artículos que sí se pueden convertir en cotización (datos mínimos
// completos) y que todavía no tengan una cotización creada.
const filtrarArticulosParaCotizar = (articulos, quotes) => {
  const existingKeys = new Set(
    (quotes || []).map((q) => dedupKeyCotizacion(q.numeroOrden, q.itemProveedor, (q.tela || "").split(" · ")[0]))
  );
  const nuevos = [];
  let omitidos = 0;
  let invalidos = 0;
  for (const a of articulos || []) {
    const key = dedupKeyCotizacion(a.noOrden, a.ref, a.tela);
    if (existingKeys.has(key)) {
      omitidos++;
      continue;
    }
    if (!a.cliente || !a.proveedor || !a.tela || !(+a.cantidad > 0) || !(+a.fobUSD > 0)) {
      invalidos++;
      continue;
    }
    nuevos.push(a);
    existingKeys.add(key);
  }
  return { nuevos, omitidos, invalidos };
};

// ─── PDF GENERATOR ────────────────────────────────────────────────────────────
async function crearPDFDoc(q) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const W = doc.internal.pageSize.getWidth();
  let y = 40;

  // Logo de la empresa (versión comprimida JPEG para PDF ligero)
  try {
    const base64 = await cargarLogoPdf();
    doc.setFillColor(0, 0, 0);
    doc.rect(0, 0, W, 110, "F");
    doc.addImage(base64, "JPEG", W / 2 - 110, 15, 220, 80, undefined, "FAST");
  } catch (e) {
    doc.setFillColor(0, 0, 0);
    doc.rect(0, 0, W, 110, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text(EMPRESA.razonSocial, W / 2, 55, { align: "center" });
    if (EMPRESA.lema) {
      doc.setFontSize(11);
      doc.setFont("helvetica", "italic");
      doc.text(EMPRESA.lema, W / 2, 80, { align: "center" });
    }
  }

  y = 140;
  doc.setTextColor(15, 118, 110);
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("COTIZACIÓN", W / 2, y, { align: "center" });
  y += 30;

  const info = [
    ["Fecha", q.fecha || "—"],
    ["Cliente", q.nombre || "—"],
    ["No. Orden", q.numeroOrden || "—"],
    ["Proveedor", q.proveedor || "—"],
    ["Tela", q.tela || "—"],
    ["Ancho", q.ancho ? `${q.ancho} cm` : "—"],
    ["Peso", q.peso ? `${q.peso} g/m²` : "—"],
    ["Composición", q.composicion || "—"],
  ];
  autoTable(doc, {
    startY: y,
    body: info,
    theme: "grid",
    styles: { fontSize: 11, cellPadding: 8 },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 160, fillColor: [240, 253, 250], textColor: [15, 118, 110] },
      1: { textColor: [30, 41, 59] },
    },
    margin: { left: 50, right: 50 },
  });
  y = doc.lastAutoTable.finalY + 25;

  doc.setTextColor(15, 118, 110);
  doc.setFontSize(13);
  doc.setFont("helvetica", "bold");
  doc.text("Costo", 50, y);
  y += 5;

  const costos = [];
  const preferMetro = q.costoMostrar === "metro" && q.costoPorMetro > 0;
  if (preferMetro) {
    costos.push(["Costo / metro", mxn(q.costoPorMetro)]);
  } else if (q.unidad === "kg" && q.costoPorUnidad > 0) {
    costos.push(["Costo / kg", mxn(q.costoPorUnidad)]);
  } else if (q.costoPorUnidad > 0) {
    costos.push([`Costo / ${q.unidad === "metros" ? "metro" : "unidad"}`, mxn(q.costoPorUnidad)]);
  }
  if (q.precioVentaManual && +q.precioVentaManual > 0) {
    costos.push([
      preferMetro ? "Precio de venta / metro" : `Precio de venta / ${q.unidad === "metros" ? "metro" : "kg"}`,
      mxn(+q.precioVentaManual),
    ]);
  }
  if (costos.length === 0) {
    costos.push(["Costo", "—"]);
  }

  autoTable(doc, {
    startY: y + 6,
    body: costos,
    theme: "grid",
    styles: { fontSize: 12, cellPadding: 10 },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 200, fillColor: [236, 253, 245], textColor: [4, 120, 87] },
      1: { halign: "right", textColor: [4, 120, 87], fontStyle: "bold", fontSize: 13 },
    },
    margin: { left: 50, right: 50 },
  });

  const pageH = doc.internal.pageSize.getHeight();
  doc.setFontSize(8);
  doc.setTextColor(148, 163, 184);
  doc.setFont("helvetica", "italic");
  doc.text(`${EMPRESA.razonSocial} · Generado ${hoy()}`, W / 2, pageH - 25, { align: "center" });

  return doc;
}

function nombreArchivoPDF(q) {
  const limpiar = (t) => String(t || "").replace(/[<>:"/\\|?*]/g, "").replace(/\s+/g, " ").trim();
  const parts = [limpiar(q.numeroOrden), limpiar(q.tela)].filter(Boolean);
  return (parts.join(" ") || `cotizacion_${q.id}`) + ".pdf";
}

async function descargarPDFCotizacion(q) {
  const doc = await crearPDFDoc(q);
  doc.save(nombreArchivoPDF(q));
}

async function compartirPDFWhatsApp(q) {
  const doc = await crearPDFDoc(q);
  const filename = nombreArchivoPDF(q);
  const blob = doc.output("blob");
  const file = new File([blob], filename, { type: "application/pdf" });

  const shareText = `Cotización ${EMPRESA.nombre} · ${q.nombre || ""}${q.numeroOrden ? " · OC " + q.numeroOrden : ""}`;

  // Detectar mobile (iOS/Android) para usar share nativo que sí incluye WhatsApp
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  if (
    isMobile &&
    typeof navigator.canShare === "function" &&
    navigator.canShare({ files: [file] })
  ) {
    try {
      await navigator.share({
        files: [file],
        title: `Cotización ${q.numeroOrden || ""}`.trim(),
        text: shareText,
      });
      return;
    } catch (e) {
      if (e.name === "AbortError") return;
    }
  }

  // Desktop (o mobile sin share): descarga el PDF y abre WhatsApp Web directo
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 2000);

  // Espera breve para que la descarga inicie antes de abrir WhatsApp Web
  setTimeout(() => {
    const waUrl = `https://web.whatsapp.com/send?text=${encodeURIComponent(shareText)}`;
    window.open(waUrl, "_blank", "noopener,noreferrer");
  }, 400);
}

// ─── PDF: ORDEN DE COMPRA (varios artículos en una sola orden) ───────────────
// Dibuja una página completa de "ORDEN DE COMPRA" (header + tabla de artículos + total)
// sobre la página ACTUAL de un doc de jsPDF ya creado. Se reutiliza tanto para el PDF
// de una sola orden como para cada sección del Resumen General (varias órdenes en un PDF).
// ─── HELPERS DE ARTÍCULOS/COLORES (compartidos por PDF, Resumen y captura) ────
// Cancelado en el Excel (cliente "CANCELADO") o con el botón ⛔ de Órdenes de compra.
const esArticuloCancelado = (it) => it.cancelado === true || (it.cliente || "").trim().toUpperCase() === "CANCELADO";

// Devuelve el desglose por color de un artículo. Si viene de un registro viejo
// (capturado antes de tener desglose por color) arma un único renglón sintético
// a partir de los campos planos, para que PDF/edición sigan funcionando igual.
const coloresDe = (it) =>
  Array.isArray(it.colores) && it.colores.length > 0
    ? it.colores
    : [
        {
          numero: 1,
          diseno: "",
          color: "",
          cantidad: it.cantidad || 0,
          unidad: it.unidad || "metros",
          precio: it.fobUSD || 0,
          incoterm: it.incoterm || "FOB",
        },
      ];

// Total en USD de un artículo (suma de sus colores).
const totalArticuloUSD = (it) =>
  coloresDe(it).reduce((s, c) => s + (+c.cantidad || 0) * (+c.precio || 0), 0);

// Total en USD de una orden, excluyendo artículos marcados como CANCELADO
// (misma regla que el SUMIFS de la macro ActualizarResumen).
const totalOrdenUSD = (items) =>
  (items || []).reduce((s, it) => (esArticuloCancelado(it) ? s : s + totalArticuloUSD(it)), 0);

async function dibujarPaginaOrden(doc, orden, items) {
  const W = doc.internal.pageSize.getWidth();

  // Sin el copete/logo de la empresa: esta orden se descarga/envía tal cual a
  // proveedores, así que no debe llevar la marca de la empresa.
  // Título a la izquierda + No. de Orden grande a la derecha, arriba de la página
  // (mismo acomodo que "ORDEN DE COMPRA / PURCHASE ORDER" · "No. ORDEN / P.O. NUMBER" del Excel).
  let y = 56;
  doc.setTextColor(15, 118, 110);
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("ORDEN DE COMPRA / PURCHASE ORDER", 40, y);

  doc.setFontSize(8.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(100, 116, 139);
  doc.text("No. DE ORDEN / P.O. NUMBER", W - 40, y - 16, { align: "right" });
  doc.setFontSize(20);
  doc.setTextColor(15, 118, 110);
  doc.text(orden.noOrden || "—", W - 40, y + 6, { align: "right" });

  y += 26;

  const esCancelado = esArticuloCancelado;

  // Datos del copete en 2 columnas (bilingüe ES/EN, como tu Excel) para que no
  // ocupe media página antes de llegar al detalle de artículos.
  const filasInfo = [
    ["Fecha / Date", orden.fecha || "—", "Manejado por / Handled by", orden.manejadoPor || "—"],
    ["Incoterm", orden.incoterm || "—", "Anticipo / Adv. Payment", orden.anticipo ? `${orden.anticipo}%` : "—"],
    ["Puerto / Arriving Port", orden.puerto || "—", "Consignee", orden.consignee || "—"],
    ["Notify", orden.notify || "—", "Forma de pago / Payment Cond.", orden.pago || "—"],
    [
      "Fecha de embarque / Shipping Date",
      orden.shippingDate || "—",
      "Contenedor / Container (CBM)",
      orden.contenedorCbm ? String(orden.contenedorCbm) : "—",
    ],
  ];
  autoTable(doc, {
    startY: y,
    body: filasInfo,
    theme: "grid",
    styles: { fontSize: 7.8, cellPadding: 5.5, overflow: "linebreak" },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 118, fillColor: [240, 253, 250], textColor: [15, 118, 110] },
      1: { cellWidth: 128, textColor: [30, 41, 59] },
      2: { fontStyle: "bold", cellWidth: 118, fillColor: [240, 253, 250], textColor: [15, 118, 110] },
      3: { textColor: [30, 41, 59] },
    },
    margin: { left: 40, right: 40 },
  });
  y = doc.lastAutoTable.finalY + 18;

  // ── Un bloque por artículo (tela), con su desglose por color — mismo orden
  // visual que el machote de Excel: nombre de tela/cliente/ref, datos del
  // proveedor, tabla de colores y subtotal del artículo.
  const PAGE_H = doc.internal.pageSize.getHeight();
  const asegurarEspacio = (necesario) => {
    if (y + necesario > PAGE_H - 40) {
      doc.addPage();
      y = 40;
    }
  };

  let totalUSD = 0;
  const hayCancelados = items.some(esCancelado);

  for (const it of items) {
    const cancelado = esCancelado(it);
    const colores = coloresDe(it);
    const totalArt = totalArticuloUSD(it);
    if (!cancelado) totalUSD += totalArt;

    asegurarEspacio(90);

    doc.setFontSize(10.5);
    doc.setFont("helvetica", "bold");
    if (cancelado) doc.setTextColor(190, 18, 60);
    else doc.setTextColor(15, 118, 110);
    doc.text(`${it.tela || "Artículo"}${cancelado ? "   ⛔ CANCELADO" : ""}`, 30, y + 12);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(71, 85, 105);
    doc.text(`Cliente: ${it.cliente || "—"}    ·    Ref.: ${it.ref || "—"}`, 30, y + 26);
    y += 34;

    autoTable(doc, {
      startY: y,
      head: [["Proveedor", "Composición", "Peso g/m²", "Ancho cm", "Unidad", "Precio/Unid. USD", "Cap. Contenedor"]],
      body: [
        [
          it.proveedor || "—",
          it.composicion || "—",
          it.peso ? String(it.peso) : "—",
          it.ancho ? String(it.ancho) : "—",
          it.unidad || "—",
          it.precioUnitario || it.fobUSD ? (+(it.precioUnitario || it.fobUSD)).toFixed(2) : "—",
          it.capContenedor ? String(it.capContenedor) : "—",
        ],
      ],
      theme: "grid",
      styles: { fontSize: 7.5, cellPadding: 4, overflow: "linebreak" },
      headStyles: { fillColor: [226, 232, 240], textColor: [51, 65, 85], fontStyle: "bold", halign: "center" },
      margin: { left: 30, right: 30 },
    });
    y = doc.lastAutoTable.finalY + 3;

    const bodyColores = colores.map((c, idx) => {
      const sub = (+c.cantidad || 0) * (+c.precio || 0);
      return [
        String(c.numero ?? idx + 1),
        c.diseno || "—",
        c.color || "—",
        c.cantidad ? (+c.cantidad).toLocaleString("es-MX") : "—",
        c.unidad || it.unidad || "—",
        c.precio ? (+c.precio).toFixed(2) : "—",
        c.incoterm || "—",
        sub ? sub.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—",
      ];
    });

    autoTable(doc, {
      startY: y,
      head: [["#", "Diseño", "Color", "Cant.", "Unid.", "Precio USD", "Incot.", "Total USD"]],
      body: bodyColores,
      theme: "grid",
      styles: { fontSize: 8, cellPadding: 4 },
      headStyles: { fillColor: [15, 118, 110], textColor: [255, 255, 255], fontStyle: "bold", halign: "center" },
      columnStyles: {
        3: { halign: "right" },
        5: { halign: "right" },
        7: { halign: "right", fontStyle: "bold" },
      },
      didParseCell: (data) => {
        if (cancelado && data.section === "body") {
          data.cell.styles.textColor = [190, 18, 60];
          data.cell.styles.fillColor = [255, 228, 230];
        }
      },
      margin: { left: 30, right: 30 },
    });
    y = doc.lastAutoTable.finalY;

    autoTable(doc, {
      startY: y,
      body: [[`Subtotal · ${it.tela || "artículo"}`, totalArt.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })]],
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 5, fontStyle: "bold" },
      columnStyles: {
        0: { fillColor: [241, 245, 249], textColor: [51, 65, 85] },
        1: { halign: "right", cellWidth: 110, textColor: cancelado ? [190, 18, 60] : [4, 120, 87] },
      },
      margin: { left: 30, right: 30 },
    });
    y = doc.lastAutoTable.finalY + 16;
  }

  asegurarEspacio(50);
  autoTable(doc, {
    startY: y,
    body: [["TOTAL USD" + (hayCancelados ? " (sin cancelados)" : ""), totalUSD.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })]],
    theme: "grid",
    styles: { fontSize: 12, cellPadding: 10, fontStyle: "bold" },
    columnStyles: {
      0: { cellWidth: 200, fillColor: [236, 253, 245], textColor: [4, 120, 87] },
      1: { halign: "right", textColor: [4, 120, 87], fontSize: 13 },
    },
    margin: { left: 30, right: 30 },
  });
}

function aplicarPiePagina(doc) {
  const W = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const pageCount = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.setFont("helvetica", "italic");
    doc.text(`${EMPRESA.razonSocial} · Generado ${hoy()}`, W / 2, pageH - 25, { align: "center" });
  }
}

async function crearPDFOrdenCompra(orden, items) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  await dibujarPaginaOrden(doc, orden, items);
  aplicarPiePagina(doc);
  return doc;
}

function nombreArchivoOC(orden) {
  const numero = (orden.noOrden || "").replace(/[<>:"/\\|?*]/g, "").trim();
  return (numero ? `OC ${numero}` : `orden_compra_${Date.now()}`) + ".pdf";
}

async function descargarPDFOrdenCompra(orden, items) {
  const doc = await crearPDFOrdenCompra(orden, items);
  doc.save(nombreArchivoOC(orden));
}

// ─── EXCEL: ORDEN DE COMPRA EN FORMATO "MACHOTE" ─────────────────────────────
// Reproduce el mismo layout/estilo del machote v2 de Excel (marcadores ART_n /
// SUB_n / BTN_ADD / TOTALES en la columna A, mismos colores, fuentes, merges y
// formatos de número) para que una orden capturada en la app se pueda exportar
// exactamente en el mismo formato que las órdenes históricas del libro maestro,
// y para que ese mismo libro (y su propio parseLibroOCs) puedan releerla sin
// problema si se reimporta.
const FUENTE_OC = "Segoe UI";
const COLOR_OC = {
  azul: "FF1A4A8A",
  amarilloClaro: "FFFFFCE8",
  azulClaro: "FFEAF1FA",
  verdeClaro: "FFEDF7ED",
  verdeTexto: "FF1E5E1E",
  grisEtiqueta: "FF6E6E6E",
  grisEtiquetaClaro: "FF7A7A7A",
  grisFondo: "FFFAFAFA",
  grisNumero: "FF9A9A9A",
  gris555: "FF555555",
  comision: "FF7A5000",
  blanco: "FFFFFFFF",
  negro: "FF000000",
  azulLiga: "FF0000FF",
};

function sanitizeHojaExcel(nombre) {
  const limpio = String(nombre || "")
    .replace(/[[\]*/\\?:]/g, " ")
    .trim();
  return (limpio || `OC ${Date.now()}`).slice(0, 31);
}

async function construirLibroExcelOrden(orden, items) {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet(sanitizeHojaExcel(orden.noOrden), {
    views: [{ showGridLines: false }],
  });
  ws.columns = [
    { width: 2.33 }, // A (marcadores, oculto a la vista)
    { width: 20 }, // B
    { width: 8.43 }, // C
    { width: 18.66 }, // D
    { width: 13.66 }, // E
    { width: 9.33 }, // F
    { width: 15.33 }, // G
    { width: 16 }, // H
    { width: 18.66 }, // I
    { width: 2.33 }, // J
  ];

  // Fila 1-3 en blanco por si el propio libro maestro necesita conservar el
  // hipervínculo "← RESUMEN" que trae el machote original al copiarlo a mano.

  const marcador = (celda, texto) => {
    celda.value = texto;
    celda.font = { name: FUENTE_OC, size: 1, color: { argb: COLOR_OC.blanco } };
  };
  const etiqueta = (celda, texto, opts = {}) => {
    celda.value = texto;
    celda.font = {
      name: FUENTE_OC,
      size: opts.size || 9,
      bold: true,
      color: { argb: opts.color || COLOR_OC.grisEtiqueta },
    };
    celda.alignment = { horizontal: opts.align || "right" };
  };
  const valorCampo = (celda, valor, opts = {}) => {
    celda.value = valor === "" || valor === undefined || valor === null ? null : valor;
    celda.font = {
      name: FUENTE_OC,
      size: 11,
      bold: !!opts.bold,
      color: { argb: opts.color || COLOR_OC.negro },
    };
    celda.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLOR_OC.amarilloClaro } };
    celda.alignment = { horizontal: opts.align || "left", vertical: "center" };
    celda.border = {
      top: { style: "thin" },
      bottom: { style: "thin" },
      left: { style: "thin" },
      right: { style: "thin" },
    };
    if (opts.numFmt) celda.numFmt = opts.numFmt;
  };

  let r = 1;
  etiqueta(ws.getCell(`G${r}`), "No. ORDEN  /  P.O. NUMBER", {
    align: "center",
    color: COLOR_OC.grisEtiquetaClaro,
  });
  ws.mergeCells(`G${r}:I${r}`);
  r++; // fila 2: título + no. de orden
  const cTitulo = ws.getCell(`B${r}`);
  cTitulo.value = "ORDEN DE COMPRA   /   PURCHASE ORDER";
  cTitulo.font = { name: FUENTE_OC, size: 17, bold: true, color: { argb: COLOR_OC.azul } };
  cTitulo.alignment = { vertical: "center" };
  cTitulo.border = { bottom: { style: "thick" } };
  ws.mergeCells(`B${r}:F${r}`);
  const cNoOrden = ws.getCell(`G${r}`);
  cNoOrden.value = orden.noOrden || "";
  cNoOrden.font = { name: FUENTE_OC, size: 20, bold: true, color: { argb: COLOR_OC.azul } };
  cNoOrden.alignment = { horizontal: "center", vertical: "center" };
  cNoOrden.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLOR_OC.amarilloClaro } };
  cNoOrden.border = {
    top: { style: "medium" },
    bottom: { style: "thick" },
    left: { style: "medium" },
    right: { style: "medium" },
  };
  ws.mergeCells(`G${r}:I${r}`);
  ws.getRow(r).height = 34;
  r += 2; // deja la fila 3 en blanco → r = 4

  const fechaVal = parseFechaDDMMYYYY(orden.fecha) || orden.fecha || "";
  const shipVal = parseFechaDDMMYYYY(orden.shippingDate) || orden.shippingDate || "";

  etiqueta(ws.getCell(`B${r}`), "FECHA");
  valorCampo(ws.getCell(`C${r}`), fechaVal, {
    numFmt: fechaVal instanceof Date ? "d\\-mmm\\-yy" : "General",
  });
  etiqueta(ws.getCell(`D${r}`), "HANDLED BY");
  valorCampo(ws.getCell(`E${r}`), orden.manejadoPor || "");
  etiqueta(ws.getCell(`F${r}`), "INCOTERM");
  valorCampo(ws.getCell(`G${r}`), orden.incoterm || "FOB");
  etiqueta(ws.getCell(`H${r}`), "ADV. PAYMENT");
  valorCampo(ws.getCell(`I${r}`), (+orden.anticipo || 0) / 100, { numFmt: "0%" });
  r++; // fila 5

  etiqueta(ws.getCell(`B${r}`), "ARRIVING PORT");
  valorCampo(ws.getCell(`C${r}`), orden.puerto || "");
  etiqueta(ws.getCell(`D${r}`), "CONSIGNEE");
  valorCampo(ws.getCell(`E${r}`), orden.consignee || "");
  etiqueta(ws.getCell(`F${r}`), "NOTIFY");
  valorCampo(ws.getCell(`G${r}`), orden.notify || "");
  etiqueta(ws.getCell(`H${r}`), "CONTENEDOR (CBM)");
  valorCampo(ws.getCell(`I${r}`), +orden.contenedorCbm || 0, {
    align: "center",
    numFmt: '0" CBM"',
  });
  r++; // fila 6

  etiqueta(ws.getCell(`B${r}`), "PAYMENT CONDITION");
  valorCampo(ws.getCell(`C${r}`), orden.pago || "");
  ws.mergeCells(`C${r}:G${r}`);
  etiqueta(ws.getCell(`H${r}`), "SHIPPING DATE");
  valorCampo(ws.getCell(`I${r}`), shipVal, {
    numFmt: shipVal instanceof Date ? "d\\-mmm\\-yy" : "General",
  });
  r += 2; // deja la fila 7 en blanco → r = 8

  const cArticulos = ws.getCell(`B${r}`);
  cArticulos.value = "ARTÍCULOS   /   ITEMS";
  cArticulos.font = { name: FUENTE_OC, size: 9, bold: true, color: { argb: COLOR_OC.grisEtiqueta } };
  ws.mergeCells(`B${r}:I${r}`);
  r++; // r = 9, primer ART_

  const filasArt = [];
  items.forEach((it, idx) => {
    const n = idx + 1;
    const colores = coloresDe(it);
    const rArt = r;
    const rDataHead = r + 1;
    const rDataVal = r + 2;
    const rColHead = r + 3;
    const rColStart = r + 4;
    const rSub = rColStart + colores.length;

    // ART_n
    marcador(ws.getCell(`A${rArt}`), `ART_${n}`);
    const cArt = ws.getCell(`B${rArt}`);
    cArt.value = `ARTÍCULO ${n}`;
    cArt.font = { name: FUENTE_OC, size: 11, bold: true, color: { argb: COLOR_OC.blanco } };
    cArt.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLOR_OC.azul } };
    cArt.alignment = { horizontal: "center" };
    cArt.border = { top: { style: "thick" }, bottom: { style: "thin" }, left: { style: "thick" } };
    const cNombre = ws.getCell(`C${rArt}`);
    cNombre.value = it.tela || null;
    cNombre.font = { name: FUENTE_OC, size: 15, bold: true, color: { argb: COLOR_OC.azul } };
    cNombre.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLOR_OC.amarilloClaro } };
    cNombre.alignment = { vertical: "center" };
    cNombre.border = { top: { style: "thick" }, bottom: { style: "thin" } };
    ws.mergeCells(`C${rArt}:E${rArt}`);
    ws.getCell(`D${rArt}`).border = { top: { style: "thick" }, bottom: { style: "thin" } };
    ws.getCell(`E${rArt}`).border = { top: { style: "thick" }, bottom: { style: "thin" } };
    etiqueta(ws.getCell(`F${rArt}`), "CLIENTE");
    ws.getCell(`F${rArt}`).border = { top: { style: "thick" }, bottom: { style: "thin" } };
    const cCliente = ws.getCell(`G${rArt}`);
    cCliente.value = it.cliente || null;
    cCliente.font = { name: FUENTE_OC, size: 11, color: { argb: COLOR_OC.negro } };
    cCliente.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLOR_OC.amarilloClaro } };
    cCliente.border = { top: { style: "thick" }, bottom: { style: "thin" } };
    etiqueta(ws.getCell(`H${rArt}`), "REF.");
    ws.getCell(`H${rArt}`).border = { top: { style: "thick" }, bottom: { style: "thin" } };
    const cRef = ws.getCell(`I${rArt}`);
    cRef.value = it.ref || null;
    cRef.font = { name: FUENTE_OC, size: 11, color: { argb: COLOR_OC.negro } };
    cRef.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLOR_OC.amarilloClaro } };
    cRef.border = { top: { style: "thick" }, bottom: { style: "thin" }, right: { style: "thick" } };
    ws.getRow(rArt).height = 26;

    // Fila de encabezados de datos del proveedor
    const encDatos = ["SUPPLIER", "COMPOSITION", "GSM", "WIDTH", "UNIT", "PRICE/UNIT", "CAP. CONTENEDOR  (misma unidad)"];
    ["B", "C", "D", "E", "F", "G", "H"].forEach((col, i2) => {
      const c = ws.getCell(`${col}${rDataHead}`);
      c.value = encDatos[i2];
      c.font = { name: FUENTE_OC, size: 9, bold: true, color: { argb: COLOR_OC.grisEtiqueta } };
      c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLOR_OC.grisFondo } };
      c.alignment = { horizontal: "center" };
      c.border = {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: col === "B" ? "thick" : "thin" },
        right: { style: "thin" },
      };
    });
    ws.mergeCells(`H${rDataHead}:I${rDataHead}`);

    // Fila de valores del proveedor
    const unidadTxt = (u) => (u === "kg" ? "Kg" : "Mts");
    const capCont = +it.capContenedor || 0;
    const dataVals = {
      B: it.proveedor || "",
      C: it.composicion || "",
      D: it.peso ? `${it.peso} GSM` : "",
      E: it.ancho ? `${it.ancho} CMS` : "",
      F: unidadTxt(it.unidad),
      G: +it.precioUnitario || 0,
      H: capCont > 0 ? capCont : "",
    };
    ["B", "C", "D", "E", "F", "G", "H"].forEach((col) => {
      const c = ws.getCell(`${col}${rDataVal}`);
      c.value = dataVals[col] === "" ? null : dataVals[col];
      c.font = {
        name: FUENTE_OC,
        size: 11,
        bold: col === "H",
        color: { argb: col === "H" ? COLOR_OC.azulLiga : COLOR_OC.negro },
      };
      c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLOR_OC.amarilloClaro } };
      c.alignment = { horizontal: "center" };
      c.border = {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: col === "B" ? "thick" : "thin" },
        right: { style: "thin" },
      };
      if (col === "G") c.numFmt = '"USD "#,##0.00';
      if (col === "H") c.numFmt = "#,##0";
    });
    ws.mergeCells(`H${rDataVal}:I${rDataVal}`);

    // Fila de encabezados de color
    const encColor = ["#", "DESIGN / DSN", "COLOR", "QTTY", "UNIT", "PRICE (USD)", "INCOT.", "TOTAL (USD)"];
    ["B", "C", "D", "E", "F", "G", "H", "I"].forEach((col, i2) => {
      const c = ws.getCell(`${col}${rColHead}`);
      c.value = encColor[i2];
      c.font = { name: FUENTE_OC, size: 9, bold: true, color: { argb: COLOR_OC.blanco } };
      c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLOR_OC.azul } };
      c.alignment = { horizontal: "center" };
      c.border = {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: col === "B" ? "thick" : "thin" },
        right: { style: col === "I" ? "thick" : "thin" },
      };
    });

    // Filas de color
    colores.forEach((c, ci) => {
      const rr = rColStart + ci;
      const total = (+c.cantidad || 0) * (+c.precio || 0);
      const vals = {
        B: c.numero ?? ci + 1,
        C: c.diseno || "",
        D: c.color || "",
        E: +c.cantidad || 0,
        F: unidadTxt(c.unidad || it.unidad),
        G: +c.precio || 0,
        H: c.incoterm || it.incoterm || "FOB",
        I: total,
      };
      ["B", "C", "D", "E", "F", "G", "H", "I"].forEach((col) => {
        const cell = ws.getCell(`${col}${rr}`);
        cell.value = vals[col] === "" ? null : vals[col];
        cell.font = {
          name: FUENTE_OC,
          size: 11,
          color: { argb: col === "B" ? COLOR_OC.grisNumero : COLOR_OC.negro },
        };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLOR_OC.blanco } };
        cell.alignment = {
          horizontal: col === "C" || col === "D" ? "left" : col === "I" ? "right" : "center",
          vertical: "center",
        };
        cell.border = {
          top: { style: "thin" },
          bottom: { style: "thin" },
          left: { style: col === "B" ? "thick" : "thin" },
          right: { style: col === "I" ? "thick" : "thin" },
        };
        if (col === "G" || col === "I") cell.numFmt = '"USD "#,##0.00';
      });
    });

    // SUB_n
    const cantidadTotal = colores.reduce((s, c) => s + (+c.cantidad || 0), 0);
    const totalArt = colores.reduce((s, c) => s + (+c.cantidad || 0) * (+c.precio || 0), 0);
    const cbmOcupados = capCont > 0 ? (cantidadTotal / capCont) * (+orden.contenedorCbm || 0) : "";
    const pctLleno = capCont > 0 ? cantidadTotal / capCont : "";
    marcador(ws.getCell(`A${rSub}`), `SUB_${n}`);
    const subCelda = (col, val, opts = {}) => {
      const c = ws.getCell(`${col}${rSub}`);
      c.value = val === "" ? null : val;
      c.font = { name: FUENTE_OC, size: 11, bold: true, color: { argb: COLOR_OC.azul } };
      c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLOR_OC.azulClaro } };
      c.alignment = { horizontal: opts.align || "center" };
      c.border = {
        top: { style: "thin" },
        bottom: { style: "thick" },
        left: { style: opts.left || "thin" },
        right: { style: opts.right || "thin" },
      };
      if (opts.numFmt) c.numFmt = opts.numFmt;
    };
    subCelda("B", `Subtotal  Artículo ${n}`, { align: "left", left: "thick" });
    subCelda("E", cantidadTotal, { numFmt: "#,##0" });
    subCelda("F", unidadTxt(it.unidad));
    subCelda("G", cbmOcupados, { numFmt: '0.0" CBM"' });
    subCelda("H", pctLleno, { numFmt: "0.0%" });
    subCelda("I", totalArt, { align: "right", right: "thick", numFmt: '"USD "#,##0.00' });

    filasArt.push({ cbmOcupados: cbmOcupados === "" ? 0 : cbmOcupados, cantidadTotal, totalArt });

    r = rSub + 2; // deja 1 fila en blanco antes del siguiente artículo (o BTN_ADD)
  });

  // BTN_ADD (decorativo — se conserva para compatibilidad con el libro maestro)
  const rBtn = r;
  marcador(ws.getCell(`A${rBtn}`), "BTN_ADD");
  const cBtn = ws.getCell(`B${rBtn}`);
  cBtn.value = "➕  AGREGAR ARTÍCULO";
  cBtn.font = { name: FUENTE_OC, size: 11, bold: true, color: { argb: COLOR_OC.verdeTexto } };
  cBtn.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLOR_OC.verdeClaro } };
  cBtn.alignment = { horizontal: "center" };
  cBtn.border = {
    top: { style: "medium" },
    bottom: { style: "medium" },
    left: { style: "medium" },
    right: { style: "medium" },
  };
  ws.mergeCells(`B${rBtn}:C${rBtn}`);
  ws.getRow(rBtn).height = 24;
  r = rBtn + 2; // deja 1 fila en blanco → TOTALES

  // TOTALES
  const rTot = r;
  marcador(ws.getCell(`A${rTot}`), "TOTALES");
  const totalCantidad = filasArt.reduce((s, f) => s + f.cantidadTotal, 0);
  const cbmOcupadosTotal = filasArt.reduce((s, f) => s + (+f.cbmOcupados || 0), 0);
  const totalBruto = filasArt.reduce((s, f) => s + f.totalArt, 0);
  const comision = totalBruto * 0.01;
  const totalNeto = totalBruto - comision;
  const pctContenedorLleno = (+orden.contenedorCbm || 0) > 0 ? cbmOcupadosTotal / (+orden.contenedorCbm || 1) : "";

  const filaTotales = (offset, label, val, opts = {}) => {
    const rr = rTot + offset;
    const cLbl = ws.getCell(`B${rr}`);
    cLbl.value = label;
    cLbl.font = {
      name: FUENTE_OC,
      size: opts.size || 10,
      bold: !!opts.boldLbl,
      italic: !!opts.italicLbl,
      color: { argb: opts.colorLbl || COLOR_OC.gris555 },
    };
    cLbl.alignment = { horizontal: "right" };
    if (opts.borderBottomLbl) cLbl.border = { bottom: { style: "thin" } };
    ws.mergeCells(`B${rr}:G${rr}`);
    const cVal = ws.getCell(`H${rr}`);
    cVal.value = val === "" ? null : val;
    cVal.font = {
      name: FUENTE_OC,
      size: opts.valSize || 12,
      bold: !!opts.boldVal,
      color: { argb: opts.colorVal || COLOR_OC.negro },
    };
    cVal.alignment = { horizontal: "right" };
    cVal.border = opts.valBorder || {
      top: { style: "thin" },
      bottom: { style: "thin" },
      left: { style: "thin" },
      right: { style: "thin" },
    };
    if (opts.fillVal) cVal.fill = { type: "pattern", pattern: "solid", fgColor: { argb: opts.fillVal } };
    if (opts.numFmt) cVal.numFmt = opts.numFmt;
    ws.mergeCells(`H${rr}:I${rr}`);
  };

  filaTotales(0, "TOTAL CANTIDAD  /  TOTAL QUANTITY", totalCantidad, { numFmt: "#,##0" });
  filaTotales(1, "CBM OCUPADOS  /  OCCUPIED CBM", cbmOcupadosTotal, {
    colorLbl: COLOR_OC.verdeTexto,
    boldVal: true,
    colorVal: COLOR_OC.verdeTexto,
    numFmt: '0.0" CBM"',
  });
  filaTotales(2, "% CONTENEDOR LLENO  /  CONTAINER FILLED", pctContenedorLleno, {
    colorLbl: COLOR_OC.verdeTexto,
    boldVal: true,
    colorVal: COLOR_OC.verdeTexto,
    numFmt: "0.0%",
  });
  filaTotales(3, "TOTAL BRUTO  /  GROSS TOTAL (USD)", totalBruto, {
    borderBottomLbl: true,
    numFmt: '"USD "#,##0.00',
  });
  filaTotales(4, "COMMISSION 1% (C1)", comision, {
    italicLbl: true,
    borderBottomLbl: true,
    colorVal: COLOR_OC.comision,
    numFmt: '"USD "#,##0.00',
  });
  filaTotales(5, "TOTAL NETO  /  NET TOTAL (USD)", totalNeto, {
    size: 12,
    boldLbl: true,
    colorLbl: COLOR_OC.azul,
    borderBottomLbl: true,
    valSize: 14,
    boldVal: true,
    colorVal: COLOR_OC.azul,
    fillVal: COLOR_OC.azulClaro,
    valBorder: {
      top: { style: "medium" },
      bottom: { style: "medium" },
      left: { style: "medium" },
      right: { style: "medium" },
    },
    numFmt: '"USD "#,##0.00',
  });
  r = rTot + 7; // 6 líneas de TOTALES + 1 fila en blanco

  // CONDICIONES ESPECIALES
  const rCond = r;
  const cCondTitulo = ws.getCell(`B${rCond}`);
  cCondTitulo.value = "CONDICIONES ESPECIALES   /   SPECIAL CONDITIONS";
  cCondTitulo.font = { name: FUENTE_OC, size: 9, bold: true, color: { argb: COLOR_OC.grisEtiqueta } };
  cCondTitulo.border = { bottom: { style: "thin" } };
  ws.mergeCells(`B${rCond}:I${rCond}`);

  const condiciones = [
    "1% COMMISSION (C1) WILL BE DEDUCTED FROM THE BALANCE PAYMENT",
    "BULK MUST MATCH REQUIRED WEIGHT AND WIDTH",
    "3 SETS OF SHIPPING SAMPLES NEEDED",
    "80-100 MTS ROLLS",
  ];
  condiciones.forEach((texto, i2) => {
    const rr = rCond + 1 + i2;
    const cChk = ws.getCell(`B${rr}`);
    cChk.value = "✓";
    cChk.font = { name: FUENTE_OC, size: 11, bold: true, color: { argb: COLOR_OC.verdeTexto } };
    cChk.alignment = { horizontal: "center" };
    const cTxt = ws.getCell(`C${rr}`);
    cTxt.value = texto;
    cTxt.font = { name: FUENTE_OC, size: 10, color: { argb: COLOR_OC.negro } };
    if (i2 === 0) cTxt.border = { top: { style: "thin" } };
    ws.mergeCells(`C${rr}:I${rr}`);
  });
  r = rCond + 1 + condiciones.length + 1; // + 1 fila en blanco

  // Firmas
  const rFirmaLbl = r;
  const cElaboro = ws.getCell(`B${rFirmaLbl}`);
  cElaboro.value = "ELABORÓ / PREPARED BY";
  cElaboro.font = { name: FUENTE_OC, size: 9, bold: true, color: { argb: COLOR_OC.grisEtiqueta } };
  cElaboro.alignment = { horizontal: "center" };
  ws.mergeCells(`B${rFirmaLbl}:D${rFirmaLbl}`);
  const cAutorizo = ws.getCell(`F${rFirmaLbl}`);
  cAutorizo.value = "AUTORIZÓ / AUTHORIZED BY";
  cAutorizo.font = { name: FUENTE_OC, size: 9, bold: true, color: { argb: COLOR_OC.grisEtiqueta } };
  cAutorizo.alignment = { horizontal: "center" };
  ws.mergeCells(`F${rFirmaLbl}:H${rFirmaLbl}`);

  const rLinea = rFirmaLbl + 1;
  ws.getCell(`B${rLinea}`).border = { bottom: { style: "thin" } };
  ws.mergeCells(`B${rLinea}:D${rLinea}`);
  ws.getCell(`F${rLinea}`).border = { bottom: { style: "thin" } };
  ws.mergeCells(`F${rLinea}:H${rLinea}`);

  const rFecha = rLinea + 1;
  const cFecha1 = ws.getCell(`B${rFecha}`);
  cFecha1.value = "Fecha: _______________";
  cFecha1.font = { name: FUENTE_OC, size: 9, color: { argb: COLOR_OC.grisEtiqueta } };
  cFecha1.alignment = { horizontal: "center" };
  cFecha1.border = { top: { style: "thin" } };
  ws.mergeCells(`B${rFecha}:D${rFecha}`);
  const cFecha2 = ws.getCell(`F${rFecha}`);
  cFecha2.value = "Fecha: _______________";
  cFecha2.font = { name: FUENTE_OC, size: 9, color: { argb: COLOR_OC.grisEtiqueta } };
  cFecha2.alignment = { horizontal: "center" };
  cFecha2.border = { top: { style: "thin" } };
  ws.mergeCells(`F${rFecha}:H${rFecha}`);

  return wb;
}

function nombreArchivoExcelOC(orden) {
  const numero = (orden.noOrden || "").replace(/[<>:"/\\|?*]/g, "").trim();
  return (numero ? `OC ${numero}` : `orden_compra_${Date.now()}`) + ".xlsx";
}

async function descargarExcelOrdenCompra(orden, items) {
  const wb = await construirLibroExcelOrden(orden, items);
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nombreArchivoExcelOC(orden);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

async function compartirPDFOrdenCompraWhatsApp(orden, items) {
  const doc = await crearPDFOrdenCompra(orden, items);
  const filename = nombreArchivoOC(orden);
  const blob = doc.output("blob");
  const file = new File([blob], filename, { type: "application/pdf" });
  const shareText = `Orden de Compra ${EMPRESA.nombre}${orden.noOrden ? " · " + orden.noOrden : ""}`;
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  if (isMobile && typeof navigator.canShare === "function" && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: `Orden de Compra ${orden.noOrden || ""}`.trim(), text: shareText });
      return;
    } catch (e) {
      if (e.name === "AbortError") return;
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 2000);
  setTimeout(() => {
    const waUrl = `https://web.whatsapp.com/send?text=${encodeURIComponent(shareText)}`;
    window.open(waUrl, "_blank", "noopener,noreferrer");
  }, 400);
}

// ─── RESUMEN GENERAL (agrupa artículos por No. de Orden, como la hoja RESUMEN del Excel) ──
function agruparPorOrden(articulos) {
  const map = new Map();
  for (const a of articulos || []) {
    const key = a.noOrden || a.hoja || "SIN-ORDEN";
    if (!map.has(key)) {
      map.set(key, {
        noOrden: key,
        fecha: a.fecha || "",
        shippingDate: a.shippingDate || "",
        contenedorCbm: a.contenedorCbm || 0,
        manejadoPor: a.manejadoPor || "",
        incoterm: a.incoterm || "",
        anticipo: a.anticipo || "",
        puerto: a.puerto || "",
        consignee: a.consignee || "",
        notify: a.notify || "",
        pago: a.pago || "",
        items: [],
      });
    }
    map.get(key).items.push(a);
  }
  // Ordena por el consecutivo global del número de orden (ej. "478SWJET-17" → 478),
  // de mayor a menor, igual que se van generando las órdenes.
  return Array.from(map.values()).sort((x, y) => numeroConsecutivo(y.noOrden) - numeroConsecutivo(x.noOrden));
}

// Extrae el consecutivo global (los dígitos al inicio) de un número de orden,
// ej. "478SWJET-17" → 478. Si no encuentra dígitos, regresa -1 para que quede al final.
function numeroConsecutivo(noOrden) {
  const m = String(noOrden || "").match(/^\s*(\d+)/);
  return m ? parseInt(m[1], 10) : -1;
}

async function crearPDFResumenGeneral(grupos) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const W = doc.internal.pageSize.getWidth();

  try {
    const base64 = await cargarLogoPdf();
    doc.setFillColor(0, 0, 0);
    doc.rect(0, 0, W, 110, "F");
    doc.addImage(base64, "JPEG", W / 2 - 110, 15, 220, 80, undefined, "FAST");
  } catch (e) {
    doc.setFillColor(0, 0, 0);
    doc.rect(0, 0, W, 110, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text(EMPRESA.razonSocial, W / 2, 55, { align: "center" });
  }

  let y = 140;
  doc.setTextColor(15, 118, 110);
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("RESUMEN GENERAL DE ÓRDENES DE COMPRA", W / 2, y, { align: "center", maxWidth: W - 80 });
  y += 30;

  let granTotal = 0;
  const filas = grupos.map((g) => {
    const totalG = totalOrdenUSD(g.items);
    granTotal += totalG;
    const proveedores = Array.from(new Set(g.items.map((i) => i.proveedor).filter(Boolean)));
    const provTxt = proveedores.slice(0, 2).join(", ") + (proveedores.length > 2 ? ` +${proveedores.length - 2}` : "");
    return [g.noOrden, g.shippingDate || "—", String(g.items.length), provTxt || "—", totalG.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })];
  });

  autoTable(doc, {
    startY: y,
    head: [["No. Orden", "Embarque", "Artículos", "Proveedores", "Total USD"]],
    body: filas,
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 6 },
    headStyles: { fillColor: [15, 118, 110], textColor: [255, 255, 255], fontStyle: "bold" },
    columnStyles: { 4: { halign: "right", fontStyle: "bold" } },
    margin: { left: 40, right: 40 },
  });
  y = doc.lastAutoTable.finalY + 20;

  autoTable(doc, {
    startY: y,
    body: [["GRAN TOTAL USD", granTotal.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })]],
    theme: "grid",
    styles: { fontSize: 13, cellPadding: 10, fontStyle: "bold" },
    columnStyles: {
      0: { cellWidth: 220, fillColor: [236, 253, 245], textColor: [4, 120, 87] },
      1: { halign: "right", textColor: [4, 120, 87], fontSize: 14 },
    },
    margin: { left: 40, right: 40 },
  });

  // Una página detallada (formato Orden de Compra completo) por cada orden
  for (const g of grupos) {
    doc.addPage();
    await dibujarPaginaOrden(doc, g, g.items);
  }

  aplicarPiePagina(doc);
  return doc;
}

async function descargarPDFResumenGeneral(grupos) {
  const doc = await crearPDFResumenGeneral(grupos);
  doc.save(`Resumen general de OCs ${hoy().replace(/\//g, "-")}.pdf`);
}

// Exporta TODOS los artículos guardados a un .xlsx plano (una fila por artículo,
// todas las columnas) — solo como respaldo, no reemplaza tu libro de macros.
// Respaldo detallado: un renglón por CADA COLOR de cada artículo (igual de
// detallado que el libro de Excel), no solo un renglón agregado por artículo.
function exportarBackupExcel(articulos) {
  const columnasOrden = [
    ["noOrden", "No. Orden"],
    ["fecha", "Fecha"],
    ["manejadoPor", "Manejado por"],
    ["incoterm", "Incoterm"],
    ["anticipo", "Anticipo %"],
    ["puerto", "Puerto"],
    ["consignee", "Consignee"],
    ["notify", "Notify"],
    ["pago", "Forma de pago"],
    ["shippingDate", "Fecha Embarque"],
    ["contenedorCbm", "Contenedor CBM"],
  ];
  const columnasArticulo = [
    ["cliente", "Cliente"],
    ["proveedor", "Proveedor"],
    ["tela", "Tela"],
    ["ref", "Ref./No. ítem"],
    ["composicion", "Composición"],
    ["peso", "Peso g/m²"],
    ["ancho", "Ancho cm"],
    ["capContenedor", "Cap. Contenedor"],
  ];
  const filas = [];
  for (const a of articulos || []) {
    const cancelado = esArticuloCancelado(a);
    for (const c of coloresDe(a)) {
      const fila = {};
      for (const [key, label] of columnasOrden) fila[label] = a[key] ?? "";
      for (const [key, label] of columnasArticulo) fila[label] = a[key] ?? "";
      fila["#"] = c.numero ?? "";
      fila["Diseño"] = c.diseno || "";
      fila["Color"] = c.color || "";
      fila["Cantidad"] = c.cantidad ?? "";
      fila["Unidad"] = c.unidad || a.unidad || "";
      fila["Precio USD"] = c.precio ?? "";
      fila["Incoterm renglón"] = c.incoterm || "";
      fila["Subtotal USD"] = (+c.cantidad || 0) * (+c.precio || 0);
      fila["Cancelado"] = cancelado ? "SI" : "";
      filas.push(fila);
    }
  }
  const ws = XLSX.utils.json_to_sheet(filas);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Órdenes de Compra");
  XLSX.writeFile(wb, `${EMPRESA.nombre} - Respaldo OCs ${hoy().replace(/\//g, "-")}.xlsx`);
}

// Exporta el libro de Cuentas por Pagar (con todos los campos derivados ya
// calculados) a un .xlsx plano, igual de detallado que la hoja PROVEEDORES.
function exportarCxPExcel(facturasCalc) {
  const filas = (facturasCalc || []).map((f) => ({
    Proveedor: f.proveedor,
    "No. Orden": f.orderNo,
    "Sub-Proveedor": f.subProveedor,
    Tela: f.tela,
    Cliente: f.cliente,
    "F. Pedido": f.fPedido,
    "F. Embarque": f.fEmbarque,
    "Cant. Pedida": f.cantPedida,
    Unidad: f.unidad,
    "Cant. Embarcada": f.cantEmbarcada,
    Precio: f.precio,
    "Total Pedido": f.totalPedido,
    "Total Real": f.totalReal,
    Anticipo: f.anticipo,
    "C1 (1%)": f.c1,
    Plazo: f.plazo,
    Vence: f.venceStr,
    "F. Pago 1": f.fPago1,
    "Monto 1": f.monto1,
    "F. Pago 2": f.fPago2,
    "Monto 2": f.monto2,
    "F. Pago 3": f.fPago3,
    "Monto 3": f.monto3,
    Pagado: f.pagado,
    Pendiente: f.pendiente,
    Estado: f.estado,
    "Ref. Proveedor": f.refProveedor,
    "OC Original": f.ocOriginal,
  }));
  const ws = XLSX.utils.json_to_sheet(filas);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Cuentas por Pagar");
  XLSX.writeFile(wb, `${EMPRESA.nombre} - Cuentas por Pagar ${hoy().replace(/\//g, "-")}.xlsx`);
}

// ─── VISTA RÁPIDA DE UNA ORDEN (sin generar/descargar PDF) ───────────────────
// Mismo contenido que el PDF (copete en 2 columnas, bilingüe ES/EN, No. de Orden
// grande arriba a la derecha) pero como pantalla dentro de la app.
function VistaOrdenModal({ orden, items, onClose }) {
  if (!orden) return null;
  const totalOrden = totalOrdenUSD(items);
  const copete = [
    ["Fecha / Date", orden.fecha || "—", "Manejado por / Handled by", orden.manejadoPor || "—"],
    ["Incoterm", orden.incoterm || "—", "Anticipo / Adv. Payment", orden.anticipo ? `${orden.anticipo}%` : "—"],
    ["Puerto / Arriving Port", orden.puerto || "—", "Consignee", orden.consignee || "—"],
    ["Notify", orden.notify || "—", "Forma de pago / Payment Cond.", orden.pago || "—"],
    [
      "Fecha de embarque / Shipping Date",
      orden.shippingDate || "—",
      "Contenedor / Container (CBM)",
      orden.contenedorCbm ? String(orden.contenedorCbm) : "—",
    ],
  ];

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-start sm:items-center justify-center p-3 overflow-y-auto"
      onClick={onClose}
      data-testid="vista-orden-modal"
    >
      <div
        className="bg-white rounded-2xl w-full max-w-4xl my-6 overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-black text-white relative px-5 py-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            {EMPRESA.logo ? (
              <img src={EMPRESA.logoHorizontal || EMPRESA.logo} alt={EMPRESA.nombre} className="h-10 w-auto object-contain mb-1.5 drop-shadow" />
            ) : (
              <p className="text-lg font-bold tracking-wide mb-1">{EMPRESA.nombre}</p>
            )}
            <p className="text-xs font-bold text-teal-300 uppercase tracking-wide">
              Orden de Compra / Purchase Order
            </p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-[9px] uppercase tracking-wider text-slate-400">No. de Orden / P.O. Number</p>
            <p className="text-xl font-bold text-teal-300 leading-tight">{orden.noOrden || "—"}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="vista-orden-cerrar"
            className="absolute top-2 right-2 text-slate-400 hover:text-white text-2xl leading-none px-2"
          >
            ×
          </button>
        </div>

        <div className="p-5 space-y-4 max-h-[78vh] overflow-y-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs bg-slate-50 rounded-xl p-3">
            {copete.map(([l1, v1, l2, v2], i) => (
              <div key={i} className="contents">
                <div className="flex justify-between border-b border-slate-200 py-1 gap-3">
                  <span className="text-slate-500">{l1}</span>
                  <span className="font-medium text-slate-800 text-right">{v1}</span>
                </div>
                <div className="flex justify-between border-b border-slate-200 py-1 gap-3">
                  <span className="text-slate-500">{l2}</span>
                  <span className="font-medium text-slate-800 text-right">{v2}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-3">
            {(items || []).map((it, idx) => {
              const cancelado = esArticuloCancelado(it);
              const colores = coloresDe(it);
              const totalArt = totalArticuloUSD(it);
              return (
                <div
                  key={idx}
                  className={`rounded-xl border p-3 ${cancelado ? "border-rose-200 bg-rose-50/50" : "border-slate-200"}`}
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                    <p className={`text-sm font-semibold ${cancelado ? "text-rose-700" : "text-teal-700"}`}>
                      {it.tela || "Artículo"}
                      {cancelado ? " · ⛔ CANCELADO" : ""}
                    </p>
                    <p className="text-xs text-slate-500">
                      Cliente: {it.cliente || "—"} · Ref.: {it.ref || "—"}
                    </p>
                  </div>
                  <p className="text-[11px] text-slate-500 mb-2">
                    {it.proveedor || "—"} · {it.composicion || "—"} ·{" "}
                    {it.peso ? `${it.peso} g/m²` : "—"} · {it.ancho ? `${it.ancho} cm` : "—"} · {it.unidad || "—"}{" "}
                    · Precio/Unid. USD{" "}
                    {it.precioUnitario || it.fobUSD ? (+(it.precioUnitario || it.fobUSD)).toFixed(2) : "—"} · Cap.
                    Cont. {it.capContenedor || "—"}
                  </p>
                  <div className="overflow-x-auto -mx-1 px-1">
                    <table className="w-full text-xs border-collapse min-w-[520px]">
                      <thead>
                        <tr className="text-[10px] text-slate-400 uppercase">
                          <th className="text-left py-1 pr-1">#</th>
                          <th className="text-left py-1 pr-1">Diseño</th>
                          <th className="text-left py-1 pr-1">Color</th>
                          <th className="text-right py-1 pr-1">Cant.</th>
                          <th className="text-left py-1 pr-1">Unid.</th>
                          <th className="text-right py-1 pr-1">Precio</th>
                          <th className="text-left py-1 pr-1">Incot.</th>
                          <th className="text-right py-1">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {colores.map((c, cIdx) => (
                          <tr key={cIdx} className="border-t border-slate-100">
                            <td className="py-1 pr-1">{c.numero ?? cIdx + 1}</td>
                            <td className="py-1 pr-1">{c.diseno || "—"}</td>
                            <td className="py-1 pr-1">{c.color || "—"}</td>
                            <td className="py-1 pr-1 text-right">
                              {c.cantidad ? (+c.cantidad).toLocaleString("es-MX") : "—"}
                            </td>
                            <td className="py-1 pr-1">{c.unidad || it.unidad || "—"}</td>
                            <td className="py-1 pr-1 text-right">{c.precio ? (+c.precio).toFixed(2) : "—"}</td>
                            <td className="py-1 pr-1">{c.incoterm || "—"}</td>
                            <td className="py-1 text-right font-medium">
                              {((+c.cantidad || 0) * (+c.precio || 0)).toLocaleString("es-MX", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p
                    className={`text-right text-xs font-semibold mt-1 ${
                      cancelado ? "text-rose-600" : "text-emerald-700"
                    }`}
                  >
                    Subtotal: USD{" "}
                    {totalArt.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              );
            })}
          </div>

          <p className="text-right text-base font-bold text-emerald-700 border-t border-slate-200 pt-3">
            TOTAL USD {totalOrden.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>
      </div>
    </div>
  );
}

// Columnas de la tabla-resumen — mismo orden y nombres que la hoja RESUMEN del Excel.

const COLUMNAS_RESUMEN = [
  { key: "noOrden", label: "No. Orden", tipo: "texto" },
  { key: "_status", label: "Status", tipo: "status" },
  { key: "fecha", label: "Fecha", tipo: "texto" },
  { key: "manejadoPor", label: "Manejado por", tipo: "texto" },
  { key: "cliente", label: "Cliente", tipo: "texto" },
  { key: "proveedor", label: "Proveedor", tipo: "texto" },
  { key: "tela", label: "Artículo / Item", tipo: "texto" },
  { key: "cantidad", label: "Cantidad", tipo: "numero" },
  { key: "unidad", label: "Unidad", tipo: "texto" },
  { key: "fobUSD", label: "Precio USD", tipo: "numero" },
  { key: "shippingDate", label: "F. Embarque", tipo: "texto" },
  { key: "_neto", label: "Neto USD", tipo: "numero" },
  { key: "ref", label: "Ref. Proveedor", tipo: "texto" },
];

function Resumen({ quotes, onEditar }) {
  const [articulos, setArticulos] = useState([]);
  const [cambiandoOrden, setCambiandoOrden] = useState(null); // orden que se está cancelando/eliminando
  // Status de seguimiento por orden: { noOrden: { status, actualizado, historial } }.
  const [ocsStatus, setOcsStatus] = useState({});
  const [filtroStatus, setFiltroStatus] = useState("");
  const statusDe = (noOrden) => ocsStatus[noOrden]?.status || "pendiente";
  const cambiarStatus = async (noOrden, status) => {
    const previo = ocsStatus[noOrden];
    setOcsStatus((m) => ({ ...m, [noOrden]: { ...(m[noOrden] || {}), status } }));
    try {
      const r = await api.post(`/oc-workbook/orden/${encodeURIComponent(noOrden)}/status`, { status });
      setOcsStatus((m) => ({ ...m, [noOrden]: r }));
    } catch (e) {
      setOcsStatus((m) => ({ ...m, [noOrden]: previo }));
      alert("No se pudo cambiar el status. Intenta de nuevo.");
    }
  };
  const [loading, setLoading] = useState(true);
  const [generandoTodo, setGenerandoTodo] = useState(false);
  const [generandoId, setGenerandoId] = useState(null);
  const [generandoExcelId, setGenerandoExcelId] = useState(null);
  const [verOrden, setVerOrden] = useState(null);
  // Documentos de embarque: orden abierta en el modal y conteo por orden para el 📎.
  const [docsDe, setDocsDe] = useState(null);
  const [conteoDocs, setConteoDocs] = useState({});
  // Datos reales de embarque confirmados desde documentos (por orden y artículo).
  const [embarques, setEmbarques] = useState({});
  const cargarEmbarques = async () => {
    try {
      const data = await api.get("/embarques");
      const porOrden = {};
      for (const e of data?.embarques || []) {
        porOrden[e.no_orden] = { orden: e.orden || {}, articulos: Object.fromEntries((e.articulos || []).map((a) => [a.art_key, a])) };
      }
      setEmbarques(porOrden);
    } catch (e) {
      // silencioso
    }
  };
  // Órdenes con varios artículos que el usuario desplegó (o plegó) a mano.
  const [toggleOrdenes, setToggleOrdenes] = useState(() => new Set());
  const cargarConteoDocs = async () => {
    try {
      const data = await api.get("/documentos/resumen");
      setConteoDocs(data?.ordenes || {});
    } catch (e) {
      // silencioso: el 📎 sigue funcionando sin el contador
    }
  };

  const [busqueda, setBusqueda] = useState("");
  const [filtroProveedor, setFiltroProveedor] = useState("");
  const [filtroManejado, setFiltroManejado] = useState("");
  const [ocultarCancelados, setOcultarCancelados] = useState(false);
  const [ordenCol, setOrdenCol] = useState("noOrden");
  const [ordenDir, setOrdenDir] = useState("desc");
  const [actualizando, setActualizando] = useState(false);
  const [msgLibro, setMsgLibro] = useState("");
  const [dropboxUrl, setDropboxUrl] = useState("");
  const [sincronizando, setSincronizando] = useState(false);
  const [editLink, setEditLink] = useState(false);
  const [linkInput, setLinkInput] = useState("");

  useEffect(() => {
    (async () => {
      let debeSincronizar = false;
      try {
        const data = await api.get("/oc-workbook");
        if (data && Array.isArray(data.articulos)) setArticulos(data.articulos);
        setOcsStatus(data?.ocs_status || {});
        if (data?.dropbox_url) {
          setDropboxUrl(data.dropbox_url);
          debeSincronizar = true;
        }
      } catch (e) {
        // silencioso
      }
      setLoading(false);
      if (debeSincronizar) sincronizar(false);
    })();
    cargarConteoDocs();
    cargarEmbarques();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const grupos = agruparPorOrden(articulos);
  const gruposPorNumero = new Map(grupos.map((g) => [g.noOrden, g]));
  const granTotal = grupos.reduce((s, g) => s + totalOrdenUSD(g.items), 0);

  const cotizacionesDe = (noOrden) => (quotes || []).filter((q) => q.numeroOrden === noOrden);

  const descargarUna = async (g) => {
    setGenerandoId(g.noOrden);
    try {
      await descargarPDFOrdenCompra(g, g.items);
    } finally {
      setGenerandoId(null);
    }
  };

  const descargarExcelUna = async (g) => {
    setGenerandoExcelId(g.noOrden);
    try {
      await descargarExcelOrdenCompra(g, g.items);
    } finally {
      setGenerandoExcelId(null);
    }
  };

  const descargarTodas = async () => {
    if (grupos.length === 0) return;
    setGenerandoTodo(true);
    try {
      await descargarPDFResumenGeneral(grupos);
    } finally {
      setGenerandoTodo(false);
    }
  };

  // Sube el libro Excel de OCs y actualiza las órdenes automáticamente.
  // Misma regla que ImportarLibroOC: se reemplaza POR ORDEN (hoja). Las órdenes
  // que vienen en el Excel se sobreescriben completas; las que no vienen
  // (p. ej. capturadas en "Nueva OC") se conservan tal cual.
  const actualizarLibro = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setActualizando(true);
    setMsgLibro("");
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array", cellDates: true });
      const items = parseLibroOCs(wb);
      if (items.length === 0) {
        alert("No se detectaron órdenes de compra en el archivo. Revisa que el formato tenga hojas con ART_ y SUB_.");
        return;
      }
      const r = await aplicarLibroOCs(items, file.name);
      setArticulos(r.merged);
      setMsgLibro(
        `✅ Libro actualizado: ${r.actualizadas} órdenes actualizadas · ${r.nuevas} nuevas · ${r.leidos} artículos leídos`
      );
    } catch (err) {
      alert("Error al actualizar el libro: " + err.message);
    } finally {
      setActualizando(false);
    }
  };

  const sincronizar = async (forzar) => {
    setSincronizando(true);
    try {
      const r = await sincronizarLibroDropbox({ forzar });
      if (r.cambio) {
        setArticulos(r.merged);
        setMsgLibro(
          `☁️ Sincronizado con Dropbox: ${r.actualizadas} órdenes actualizadas · ${r.nuevas} nuevas · ${r.leidos} artículos leídos`
        );
      } else if (forzar) {
        setMsgLibro("☁️ El libro de Dropbox no tiene cambios.");
      }
    } catch (err) {
      setMsgLibro(`⚠️ No se pudo sincronizar con Dropbox: ${err.message}`);
    } finally {
      setSincronizando(false);
    }
  };

  const guardarLink = async () => {
    try {
      const r = await api.post("/oc-workbook/dropbox", { url: linkInput.trim() });
      setDropboxUrl(r.dropbox_url || "");
      setEditLink(false);
      if (r.dropbox_url) {
        await sincronizar(true);
      } else {
        setMsgLibro("Dropbox desconectado. Las órdenes guardadas se quedan como están.");
      }
    } catch (err) {
      alert("No se pudo guardar el link: " + err.message);
    }
  };

  const botonDropbox = dropboxUrl ? (
    <>
      <button
        type="button"
        onClick={() => sincronizar(true)}
        disabled={sincronizando}
        data-testid="resumen-sincronizar-dropbox"
        title="Vuelve a leer el libro desde Dropbox"
        className={`text-xs font-semibold px-3 py-2 rounded-lg whitespace-nowrap border ${
          sincronizando ? "bg-slate-200 text-slate-500 border-slate-200" : "border-sky-300 text-sky-700 hover:bg-sky-50"
        }`}
      >
        {sincronizando ? "Sincronizando…" : "☁️ Sincronizar Dropbox"}
      </button>
      <button
        type="button"
        onClick={() => {
          setLinkInput(dropboxUrl);
          setEditLink((v) => !v);
        }}
        data-testid="resumen-config-dropbox"
        title="Cambiar o quitar el link de Dropbox"
        className="text-xs px-2 py-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"
      >
        ⚙️
      </button>
    </>
  ) : (
    <button
      type="button"
      onClick={() => {
        setLinkInput("");
        setEditLink((v) => !v);
      }}
      data-testid="resumen-conectar-dropbox"
      className="text-xs font-semibold px-3 py-2 rounded-lg whitespace-nowrap border border-sky-300 text-sky-700 hover:bg-sky-50"
    >
      ☁️ Conectar Dropbox
    </button>
  );

  const panelLink = editLink && (
    <div className="bg-white rounded-2xl border border-sky-200 p-4 space-y-2 text-left" data-testid="resumen-panel-dropbox">
      <p className="text-xs text-slate-600">
        Pega el link compartido de tu libro en Dropbox (en Dropbox: clic derecho al archivo → Compartir → Copiar
        vínculo). La app lo lee sola cada vez que la abres. Deja el campo vacío y guarda para desconectarlo.
      </p>
      <div className="flex gap-2 flex-wrap">
        <input
          type="text"
          value={linkInput}
          onChange={(e) => setLinkInput(e.target.value)}
          placeholder="https://www.dropbox.com/scl/fi/..."
          data-testid="resumen-link-dropbox"
          className="flex-1 min-w-[240px] border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/30"
        />
        <button
          type="button"
          onClick={guardarLink}
          data-testid="resumen-guardar-dropbox"
          className="text-xs font-semibold px-3 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800"
        >
          Guardar
        </button>
        <button
          type="button"
          onClick={() => setEditLink(false)}
          className="text-xs px-3 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
        >
          Cancelar
        </button>
      </div>
    </div>
  );

  const colorMsg = msgLibro.startsWith("⚠️") ? "text-amber-700" : "text-emerald-700";

  const botonActualizar = (
    <label
      data-testid="resumen-actualizar-libro"
      title="Sube tu libro Excel de OCs para actualizar las órdenes"
      className={`text-xs font-semibold px-3 py-2 rounded-lg whitespace-nowrap border ${
        actualizando
          ? "bg-slate-200 text-slate-500 border-slate-200 pointer-events-none"
          : "cursor-pointer border-teal-300 text-teal-700 hover:bg-teal-50"
      }`}
    >
      {actualizando ? "Actualizando…" : "🔄 Actualizar libro"}
      <input
        type="file"
        accept=".xlsx,.xlsm,.xls"
        onChange={actualizarLibro}
        disabled={actualizando}
        className="hidden"
      />
    </label>
  );

  if (loading) {
    return <p className="text-center text-sm text-slate-400 py-10">Cargando resumen…</p>;
  }

  if (grupos.length === 0) {
    return (
      <div className="text-center py-10 space-y-3">
        <p className="text-sm text-slate-400">
          Todavía no hay órdenes de compra guardadas. Sube tu libro Excel o captura una en "Nueva OC".
        </p>
        <div className="flex justify-center gap-2 flex-wrap">
          {botonActualizar}
          {botonDropbox}
        </div>
        {panelLink}
        {msgLibro && <p className={`text-xs ${colorMsg}`}>{msgLibro}</p>}
      </div>
    );
  }

  const proveedoresUnicos = Array.from(new Set(articulos.map((a) => a.proveedor).filter(Boolean))).sort();
  const manejadoUnicos = Array.from(new Set(articulos.map((a) => a.manejadoPor).filter(Boolean))).sort();

  const q = busqueda.trim().toLowerCase();
  const filas = articulos
    .filter((a) => !ocultarCancelados || !esArticuloCancelado(a))
    .filter((a) => !filtroProveedor || a.proveedor === filtroProveedor)
    .filter((a) => !filtroManejado || a.manejadoPor === filtroManejado)
    .filter((a) => !filtroStatus || statusDe(a.noOrden) === filtroStatus)
    .filter(
      (a) =>
        !q || [a.noOrden, a.cliente, a.proveedor, a.tela, a.ref].some((v) => (v || "").toLowerCase().includes(q))
    )
    .map((a) => ({ ...a, _neto: totalArticuloUSD(a), _cancelado: esArticuloCancelado(a), _status: statusDe(a.noOrden) }));

  const comparar = (x, y) => {
    let vx = x[ordenCol];
    let vy = y[ordenCol];
    if (ordenCol === "noOrden") {
      vx = numeroConsecutivo(x.noOrden);
      vy = numeroConsecutivo(y.noOrden);
    }
    if (ordenCol === "_status") {
      // En el orden del proceso (Pendiente → Entregada), no alfabético.
      vx = STATUS_OC.findIndex((st) => st.id === statusDe(x.noOrden));
      vy = STATUS_OC.findIndex((st) => st.id === statusDe(y.noOrden));
    }
    const numerico = COLUMNAS_RESUMEN.find((c) => c.key === ordenCol)?.tipo === "numero" || ordenCol === "noOrden" || ordenCol === "_status";
    if (numerico) {
      vx = +vx || 0;
      vy = +vy || 0;
      return ordenDir === "asc" ? vx - vy : vy - vx;
    }
    vx = String(vx || "").toLowerCase();
    vy = String(vy || "").toLowerCase();
    return ordenDir === "asc" ? vx.localeCompare(vy) : vy.localeCompare(vx);
  };
  filas.sort(comparar);

  // Una línea por orden de compra. Las órdenes con varios artículos se resumen
  // en una fila (totales de la orden) y se despliegan con ▸ para ver cada artículo.
  const unicoOVarios = (vals) => {
    const u = [...new Set(vals.filter(Boolean))];
    return u.length === 1 ? u[0] : u.length > 1 ? "Varios" : "";
  };
  const gruposFilas = [];
  const posGrupo = new Map();
  for (const a of filas) {
    if (!posGrupo.has(a.noOrden)) {
      posGrupo.set(a.noOrden, gruposFilas.length);
      gruposFilas.push({ noOrden: a.noOrden, items: [] });
    }
    gruposFilas[posGrupo.get(a.noOrden)].items.push(a);
  }
  for (const gr of gruposFilas) {
    const its = gr.items;
    const vivos = its.filter((x) => !x._cancelado);
    const unidades = [...new Set(its.map((x) => x.unidad).filter(Boolean))];
    Object.assign(gr, {
      fecha: unicoOVarios(its.map((x) => x.fecha)),
      manejadoPor: unicoOVarios(its.map((x) => x.manejadoPor)),
      cliente: unicoOVarios(its.map((x) => x.cliente)),
      proveedor: unicoOVarios(its.map((x) => x.proveedor)),
      tela: its.length > 1 ? `${its.length} artículos` : its[0].tela,
      // Cantidades solo se suman si todos los artículos usan la misma unidad.
      cantidad: unidades.length <= 1 ? vivos.reduce((t, x) => t + (+x.cantidad || 0), 0) : null,
      unidad: unidades.length > 1 ? "Mixta" : unidades[0] || "",
      fobUSD: null,
      shippingDate: unicoOVarios(its.map((x) => x.shippingDate)),
      _neto: vivos.reduce((t, x) => t + x._neto, 0),
      ref: unicoOVarios(its.map((x) => x.ref)),
      _cancelado: its.every((x) => x._cancelado),
    });
  }
  gruposFilas.sort(comparar);
  // "2026-08-13" (documentos) → "13/08/2026"
  const fechaEmbarqueDocs = (noOrden) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(embarques[noOrden]?.orden?.fecha_embarque || "");
    return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
  };
  const alternarOrdenAbierta = (noOrden) =>
    setToggleOrdenes((prev) => {
      const n = new Set(prev);
      n.has(noOrden) ? n.delete(noOrden) : n.add(noOrden);
      return n;
    });

  const totalFiltrado = filas.reduce((s, a) => (a._cancelado ? s : s + a._neto), 0);

  const alternarOrden = (key) => {
    if (ordenCol === key) {
      setOrdenDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setOrdenCol(key);
      setOrdenDir(key === "noOrden" ? "desc" : "asc");
    }
  };

  // Botones de una orden (ver, editar, PDF, Excel, documentos): los mismos para
  // la fila de una orden de un artículo o la fila resumen de varios.
  const accionesOrden = (g) => (
    g && (
                        <>
                          <button
                            type="button"
                            onClick={() => setVerOrden(g)}
                            title="Visualizar"
                            className="text-slate-400 hover:text-teal-600 px-1"
                          >
                            👁
                          </button>
                          {onEditar && (
                            <button
                              type="button"
                              onClick={() => onEditar(g)}
                              title="Editar"
                              className="text-slate-400 hover:text-teal-600 px-1"
                            >
                              ✏️
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => descargarUna(g)}
                            disabled={generandoId === g.noOrden}
                            title="Descargar PDF"
                            className="text-slate-400 hover:text-teal-600 px-1"
                          >
                            {generandoId === g.noOrden ? "…" : "📄"}
                          </button>
                          <button
                            type="button"
                            onClick={() => descargarExcelUna(g)}
                            disabled={generandoExcelId === g.noOrden}
                            title="Descargar Excel (formato machote)"
                            className="text-slate-400 hover:text-teal-600 px-1"
                          >
                            {generandoExcelId === g.noOrden ? "…" : "📊"}
                          </button>
                          {(() => {
                            const c = conteoDocs[g.noOrden] || {};
                            // _archivos = archivos subidos (uno combinado cuenta una vez).
                            const total = c._archivos ?? Object.values(c).reduce((x, n) => x + n, 0);
                            const completos = c.factura && c.packing && c.ficha;
                            return (
                              <button
                                type="button"
                                onClick={() => setDocsDe(g)}
                                title={total ? `Documentos de embarque (${total})` : "Subir documentos de embarque"}
                                data-testid={`resumen-docs-${g.noOrden}`}
                                className={`px-1 ${total ? (completos ? "text-emerald-600" : "text-amber-600") : "text-slate-400"} hover:text-teal-600`}
                              >
                                📎{total > 0 && <sup className="text-[9px] font-bold">{total}</sup>}
                              </button>
                            );
                          })()}
                          {(() => {
                            const enApp = g.items.some((x) => x.cancelado === true);
                            const enExcel = !enApp && g.items.length > 0 && g.items.every(esArticuloCancelado);
                            const ocupado = cambiandoOrden === g.noOrden;
                            return (
                              <>
                                {enApp ? (
                                  <button
                                    type="button"
                                    onClick={() => cambiarEstadoOrden(g, "reactivar")}
                                    disabled={ocupado}
                                    title="Reactivar orden"
                                    data-testid={`resumen-reactivar-${g.noOrden}`}
                                    className="text-rose-500 hover:text-emerald-600 px-1"
                                  >
                                    ↩️
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => cambiarEstadoOrden(g, "cancelar")}
                                    disabled={ocupado || enExcel}
                                    title={enExcel ? "Cancelada en el Excel (cliente CANCELADO)" : "Cancelar orden"}
                                    data-testid={`resumen-cancelar-${g.noOrden}`}
                                    className="text-slate-400 hover:text-rose-600 px-1 disabled:opacity-40"
                                  >
                                    ⛔
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => cambiarEstadoOrden(g, "eliminar")}
                                  disabled={ocupado}
                                  title="Eliminar orden"
                                  data-testid={`resumen-eliminar-${g.noOrden}`}
                                  className="text-slate-400 hover:text-rose-600 px-1"
                                >
                                  🗑
                                </button>
                              </>
                            );
                          })()}
                        </>
                      )
  );

  const limpiarFiltros = () => {
    setBusqueda("");
    setFiltroProveedor("");
    setFiltroManejado("");
    setFiltroStatus("");
    setOcultarCancelados(false);
  };

  // Menú desplegable de status de una orden (de color según la etapa).
  const selectorStatus = (noOrden, cancelado) => {
    if (cancelado)
      return <span className="text-[11px] font-semibold px-2 py-1 rounded-md border bg-rose-50 text-rose-700 border-rose-200 whitespace-nowrap">⛔ Cancelada</span>;
    const st = statusOC(statusDe(noOrden));
    const info = ocsStatus[noOrden];
    const desde = info?.actualizado ? new Date(info.actualizado).toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" }) : "";
    return (
      <select
        value={st.id}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => cambiarStatus(noOrden, e.target.value)}
        data-testid={`resumen-status-${noOrden}`}
        title={desde ? `${st.label} desde ${desde}` : "Cambiar status"}
        className={`text-[11px] font-semibold rounded-md border px-1.5 py-1 cursor-pointer focus:outline-none focus:ring-2 focus:ring-teal-500/30 ${st.cls}`}
      >
        {STATUS_OC.map((x) => (
          <option key={x.id} value={x.id} className="bg-white text-slate-800">
            {x.label}
          </option>
        ))}
      </select>
    );
  };
  const conteoStatus = grupos.reduce((m, g) => {
    if (g.items.every(esArticuloCancelado)) return m;
    const k = statusDe(g.noOrden);
    m[k] = (m[k] || 0) + 1;
    return m;
  }, {});

  // Cancelar / reactivar / eliminar una orden completa (todos sus artículos).
  const cambiarEstadoOrden = async (g, accion) => {
    const n = g.noOrden;
    const textos = {
      cancelar: `¿Cancelar la orden ${n}?\n\nSe queda en el historial marcada como cancelada (⛔) y deja de sumar en los totales. La puedes reactivar después.`,
      reactivar: `¿Reactivar la orden ${n}? Vuelve a sumar en los totales.`,
      eliminar: `¿ELIMINAR la orden ${n} y sus ${g.items.length} artículo(s)?\n\nNo se puede deshacer. Si solo ya no va, mejor usa ⛔ Cancelar para conservar el historial.${
        cotizacionesDe(n).length ? `\n\nOjo: tiene ${cotizacionesDe(n).length} cotización(es) ligada(s); esas no se borran.` : ""
      }`,
    };
    if (!window.confirm(textos[accion])) return;
    setCambiandoOrden(n);
    try {
      const data = await api.post(`/oc-workbook/orden/${encodeURIComponent(n)}/estado`, { accion });
      if (Array.isArray(data?.articulos)) setArticulos(data.articulos);
      if (data?.ocs_status) setOcsStatus(data.ocs_status);
      setMsgLibro(
        accion === "eliminar" ? `🗑 Orden ${n} eliminada.` : accion === "cancelar" ? `⛔ Orden ${n} cancelada.` : `✅ Orden ${n} reactivada.`
      );
    } catch (e) {
      alert("No se pudo cambiar la orden. Intenta de nuevo.");
    }
    setCambiandoOrden(null);
  };

  return (
    <div className="space-y-3 pb-8">
      <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs font-semibold text-teal-700 uppercase tracking-wider">Resumen general</p>
          <p className="text-sm text-slate-600 mt-0.5">
            {grupos.length} órdenes · {articulos.length} artículos · USD{" "}
            {granTotal.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          {msgLibro && (
            <p className={`text-xs mt-1 ${colorMsg}`} data-testid="resumen-msg-libro">
              {msgLibro}
            </p>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {botonDropbox}
          {botonActualizar}
          <button
            type="button"
            onClick={descargarTodas}
            disabled={generandoTodo}
            data-testid="resumen-descargar-todo"
            className={`text-xs font-semibold px-3 py-2 rounded-lg whitespace-nowrap ${
              generandoTodo ? "bg-slate-200 text-slate-500" : "bg-slate-900 text-white hover:bg-slate-800"
            }`}
          >
            {generandoTodo ? "Generando…" : "⬇ PDF de todas"}
          </button>
          <button
            type="button"
            onClick={() => exportarBackupExcel(articulos)}
            data-testid="resumen-exportar-excel"
            className="text-xs font-semibold px-3 py-2 rounded-lg whitespace-nowrap border border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          >
            📗 Exportar a Excel
          </button>
        </div>
      </div>

      {panelLink}

      {/* Barra de filtros, como el AutoFiltro de Excel */}
      <div className="bg-white rounded-2xl border border-slate-200 p-3 flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="🔎 Buscar: orden, cliente, proveedor, tela, ref…"
          data-testid="resumen-buscar"
          className="flex-1 min-w-[220px] border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30"
        />
        <select
          value={filtroProveedor}
          onChange={(e) => setFiltroProveedor(e.target.value)}
          data-testid="resumen-filtro-proveedor"
          className="border border-slate-200 rounded-lg px-2 py-2 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30"
        >
          <option value="">Proveedor: todos</option>
          {proveedoresUnicos.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
        <select
          value={filtroManejado}
          onChange={(e) => setFiltroManejado(e.target.value)}
          data-testid="resumen-filtro-manejado"
          className="border border-slate-200 rounded-lg px-2 py-2 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30"
        >
          <option value="">Manejado por: todos</option>
          {manejadoUnicos.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <select
          value={filtroStatus}
          onChange={(e) => setFiltroStatus(e.target.value)}
          data-testid="resumen-filtro-status"
          className="border border-slate-200 rounded-lg px-2 py-2 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30"
        >
          <option value="">Status: todos</option>
          {STATUS_OC.map((x) => (
            <option key={x.id} value={x.id}>
              {x.label} ({conteoStatus[x.id] || 0})
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-xs text-slate-600 px-1">
          <input
            type="checkbox"
            checked={ocultarCancelados}
            onChange={(e) => setOcultarCancelados(e.target.checked)}
            data-testid="resumen-filtro-cancelados"
          />
          Ocultar cancelados
        </label>
        {(busqueda || filtroProveedor || filtroManejado || filtroStatus || ocultarCancelados) && (
          <button
            type="button"
            onClick={limpiarFiltros}
            data-testid="resumen-limpiar-filtros"
            className="text-xs font-medium text-slate-500 hover:text-rose-600 px-2"
          >
            ✕ Limpiar filtros
          </button>
        )}
        <span className="text-xs text-slate-400 ml-auto whitespace-nowrap">
          {filas.length} de {articulos.length} artículos · USD{" "}
          {totalFiltrado.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      </div>

      {/* Tabla filtrable/ordenable, como la hoja RESUMEN de tu Excel */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-[1100px] w-full border-collapse text-xs">
            <thead>
              <tr className="bg-slate-50">
                {COLUMNAS_RESUMEN.map((c) => (
                  <th
                    key={c.key}
                    onClick={() => alternarOrden(c.key)}
                    data-testid={`resumen-col-${c.key}`}
                    className={`text-left font-semibold text-slate-500 uppercase tracking-wide text-[10px] px-2 py-2 border-b border-slate-200 whitespace-nowrap cursor-pointer select-none hover:text-teal-700 ${
                      c.tipo === "numero" ? "text-right" : ""
                    }`}
                  >
                    {c.label}
                    {ordenCol === c.key ? (ordenDir === "asc" ? " ▲" : " ▼") : ""}
                  </th>
                ))}
                <th className="px-2 py-2 border-b border-slate-200"></th>
              </tr>
            </thead>
            <tbody>
              {gruposFilas.map((gr, gIdx) => {
                const g = gruposPorNumero.get(gr.noOrden);
                const ligadas = cotizacionesDe(gr.noOrden);
                const filaArticulo = (a, idx, sub) => (
                    <tr
                      key={a._key || `${a.noOrden}-${idx}`}
                      data-testid={`resumen-fila-${a.noOrden}-${idx}`}
                      className={`border-b border-slate-100 ${a._cancelado ? "bg-rose-50/50 text-rose-700" : sub ? "bg-slate-50/70" : "hover:bg-slate-50"}`}
                    >
                      <td className="px-2 py-1.5 font-semibold whitespace-nowrap">
                        {sub ? (
                          <span className="pl-4 text-slate-400 font-normal">↳ {a.noOrden}</span>
                        ) : (
                          <>
                            {a.noOrden}
                            {ligadas.length > 0 && <span className="ml-1 text-teal-600" title="Ligada a cotización">🔗</span>}
                          </>
                        )}
                      </td>
                      <td className="px-2 py-1 whitespace-nowrap">{sub ? null : selectorStatus(a.noOrden, a._cancelado)}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{a.fecha || "—"}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{a.manejadoPor || "—"}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        {a.cliente || "—"}
                        {a._cancelado ? " ⛔" : ""}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{a.proveedor || "—"}</td>
                      <td className="px-2 py-1.5">{a.tela || "—"}</td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap">
                        {a.cantidad ? (+a.cantidad).toLocaleString("es-MX") : "—"}
                        {(() => {
                          const em = embarques[a.noOrden]?.articulos?.[a._key];
                          return em?.cantidad_embarcada != null ? (
                            <div className="text-[10px] text-sky-700 font-semibold" title="Cantidad embarcada según documentos">
                              🚢 {(+em.cantidad_embarcada).toLocaleString("es-MX", { maximumFractionDigits: 1 })} {em.unidad}
                            </div>
                          ) : null;
                        })()}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{a.unidad || "—"}</td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap">
                        {a.fobUSD ? (+a.fobUSD).toFixed(2) : "—"}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        {(() => {
                          const f = fechaEmbarqueDocs(a.noOrden);
                          return f ? <span title="Fecha de embarque según documentos">🚢 {f}</span> : a.shippingDate || "—";
                        })()}
                      </td>
                      <td className="px-2 py-1.5 text-right font-semibold whitespace-nowrap">
                        {a._neto.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{a.ref || "—"}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        {!sub && accionesOrden(g)}                    </td>
                    </tr>
                );
                if (gr.items.length === 1) return filaArticulo(gr.items[0], gIdx, false);
                // Varios artículos: fila resumen de la orden + artículos al desplegar.
                // Con búsqueda activa se despliegan solas para mostrar qué coincidió.
                const abierta = toggleOrdenes.has(gr.noOrden) !== !!q;
                return (
                  <Fragment key={`orden-${gr.noOrden}`}>
                    <tr
                      onClick={() => alternarOrdenAbierta(gr.noOrden)}
                      data-testid={`resumen-orden-${gr.noOrden}`}
                      className={`border-b border-slate-100 cursor-pointer ${
                        gr._cancelado ? "bg-rose-50/50 text-rose-700" : abierta ? "bg-teal-50/60" : "hover:bg-slate-50"
                      }`}
                      title={abierta ? "Ocultar artículos" : "Ver artículos de esta orden"}
                    >
                      <td className="px-2 py-1.5 font-semibold whitespace-nowrap">
                        <span className="inline-block w-3 text-slate-400">{abierta ? "▾" : "▸"}</span>
                        {gr.noOrden}
                        {ligadas.length > 0 && <span className="ml-1 text-teal-600" title="Ligada a cotización">🔗</span>}
                      </td>
                      <td className="px-2 py-1 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        {selectorStatus(gr.noOrden, gr._cancelado)}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{gr.fecha || "—"}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{gr.manejadoPor || "—"}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{gr.cliente || "—"}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{gr.proveedor || "—"}</td>
                      <td className="px-2 py-1.5">
                        <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 whitespace-nowrap">
                          {gr.tela}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap">
                        {gr.cantidad != null ? gr.cantidad.toLocaleString("es-MX") : "—"}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{gr.unidad || "—"}</td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap text-slate-400">—</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        {fechaEmbarqueDocs(gr.noOrden) ? (
                          <span title="Fecha de embarque según documentos">🚢 {fechaEmbarqueDocs(gr.noOrden)}</span>
                        ) : (
                          gr.shippingDate || "—"
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-right font-semibold whitespace-nowrap">
                        {gr._neto.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{gr.ref || "—"}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        {accionesOrden(g)}
                      </td>
                    </tr>
                    {abierta && gr.items.map((a, i) => filaArticulo(a, `${gIdx}-${i}`, true))}
                  </Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-emerald-50 font-semibold text-emerald-700">
                <td className="px-2 py-2" colSpan={11}>
                  TOTAL {ocultarCancelados || filas.some((f) => f._cancelado) ? "" : "(con cancelados en 0)"}
                </td>
                <td className="px-2 py-2 text-right whitespace-nowrap">
                  {totalFiltrado.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {verOrden && (
        <VistaOrdenModal orden={verOrden} items={verOrden.items} onClose={() => setVerOrden(null)} />
      )}
      {docsDe && (
        <DocumentosOrden
          noOrden={docsDe.noOrden}
          proveedor={docsDe.proveedor || docsDe.items?.[0]?.proveedor}
          articulos={docsDe.items || []}
          calcularFacturaCxP={calcularFacturaCxP}
          onEmbarque={cargarEmbarques}
          onClose={() => setDocsDe(null)}
          onCambio={cargarConteoDocs}
        />
      )}
    </div>
  );
}

// ─── CUENTAS POR PAGAR (Fase 1: facturas activas por proveedor) ──────────────
const COLUMNAS_CXP = [
  { key: "proveedor", label: "Proveedor", tipo: "texto" },
  { key: "orderNo", label: "No. Orden", tipo: "texto" },
  { key: "tela", label: "Tela", tipo: "texto" },
  { key: "cliente", label: "Cliente", tipo: "texto" },
  { key: "totalReal", label: "Total Real USD", tipo: "numero" },
  { key: "anticipo", label: "Anticipo USD", tipo: "numero" },
  { key: "pagado", label: "Pagado USD", tipo: "numero" },
  { key: "pendiente", label: "Pendiente USD", tipo: "numero" },
  { key: "vence", label: "Vence", tipo: "fecha" },
  { key: "estado", label: "Estado", tipo: "texto" },
];

const inputCxP =
  "border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-teal-500/30 w-full";

function CuentasPorPagar() {
  const [facturas, setFacturas] = useState([]);
  const [verPagos, setVerPagos] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fileName, setFileName] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const [importOpen, setImportOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importMsg, setImportMsg] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(facturaCxPVacia());

  const [pagoDe, setPagoDe] = useState(null);
  const [pagoMonto, setPagoMonto] = useState("");
  const [pagoFecha, setPagoFecha] = useState(hoy());

  const [busqueda, setBusqueda] = useState("");
  const [filtroProveedor, setFiltroProveedor] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [ordenCol, setOrdenCol] = useState("vence");
  const [ordenDir, setOrdenDir] = useState("asc");

  useEffect(() => {
    (async () => {
      try {
        const data = await api.get("/cxp-facturas");
        if (data && Array.isArray(data.facturas)) {
          setFacturas(data.facturas);
          setFileName(data.file_name || "");
          setUpdatedAt(data.updated_at || null);
        }
      } catch (e) {
        // silencioso
      }
      setLoading(false);
    })();
  }, []);

  const persistir = async (lista, name) => {
    setGuardando(true);
    try {
      const saved = await api.post("/cxp-facturas", {
        facturas: lista,
        file_name: name !== undefined ? name : fileName,
      });
      if (saved?.facturas) setFacturas(saved.facturas);
      if (saved?.updated_at) setUpdatedAt(saved.updated_at);
      if (name !== undefined) setFileName(name);
    } catch (e) {
      console.error("No se pudo guardar cuentas por pagar:", e);
      alert("No se pudo guardar el cambio. Intenta de nuevo.");
    }
    setGuardando(false);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportLoading(true);
    setImportMsg("");
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array", cellDates: true });
      const nuevas = parseLibroCxP(wb);
      if (nuevas.length === 0) {
        setImportMsg("No se encontraron facturas activas (Parcial, Vencida o Al día) en la hoja PROVEEDORES.");
      } else {
        // Combina por _key: si una factura ya existía y tenía pagos capturados en la
        // app, se conservan (no se pisan con la versión "cruda" recién leída del Excel).
        const map = new Map(facturas.map((f) => [f._key, f]));
        for (const n of nuevas) {
          const previa = map.get(n._key);
          // Los campos que se actualizaron desde documentos de embarque (origenDocs)
          // también se conservan para que el Excel no los pise.
          const deDocs = previa?.origenDocs ? Object.fromEntries(Object.keys(previa.origenDocs).map((k) => [k, previa[k]])) : {};
          map.set(n._key, previa ? { ...n, fPago1: previa.fPago1, monto1: previa.monto1, fPago2: previa.fPago2, monto2: previa.monto2, fPago3: previa.fPago3, monto3: previa.monto3, ...deDocs, ...(previa.origenDocs ? { origenDocs: previa.origenDocs } : {}) } : n);
        }
        const merged = Array.from(map.values());
        await persistir(merged, file.name);
        setImportMsg(`✅ ${nuevas.length} facturas activas importadas de "${file.name}".`);
      }
    } catch (err) {
      setImportMsg("Error al leer el archivo: " + err.message);
    }
    setImportLoading(false);
  };

  const abrirNueva = () => {
    setForm(facturaCxPVacia());
    setFormOpen(true);
  };

  const abrirEditar = (f) => {
    setForm({ ...f });
    setFormOpen(true);
  };

  const guardarForm = async () => {
    if (!form.proveedor.trim() || !form.orderNo.trim() || !(+form.cantPedida > 0) || !(+form.precio > 0)) {
      alert("Captura al menos Proveedor, No. de Orden, Cantidad Pedida y Precio.");
      return;
    }
    const factura = {
      ...form,
      _key: form._key || `${form.orderNo}::${form.tela}::${form.proveedor}::${Date.now()}`,
    };
    const map = new Map(facturas.map((f) => [f._key, f]));
    map.set(factura._key, factura);
    await persistir(Array.from(map.values()));
    setFormOpen(false);
    setForm(facturaCxPVacia());
  };

  const eliminar = async (f) => {
    if (!window.confirm(`¿Eliminar la factura de "${f.tela}" (${f.orderNo})?`)) return;
    await persistir(facturas.filter((x) => x._key !== f._key));
  };

  const abrirPago = (f) => {
    setPagoDe(f);
    setPagoMonto("");
    setPagoFecha(hoy());
  };

  const confirmarPago = async () => {
    if (!pagoDe || !(+pagoMonto > 0)) return;
    const slot = !pagoDe.monto1 ? 1 : !pagoDe.monto2 ? 2 : !pagoDe.monto3 ? 3 : null;
    if (!slot) {
      alert("Esta factura ya tiene sus 3 pagos registrados. Edítala manualmente para ajustar un monto.");
      return;
    }
    const actualizada = { ...pagoDe, [`fPago${slot}`]: pagoFecha, [`monto${slot}`]: +pagoMonto };
    const map = new Map(facturas.map((f) => [f._key, f]));
    map.set(actualizada._key, actualizada);
    await persistir(Array.from(map.values()));
    setPagoDe(null);
  };

  if (loading) {
    return <p className="text-center text-sm text-slate-400 py-10">Cargando cuentas por pagar…</p>;
  }

  const facturasCalc = facturas.map(calcularFacturaCxP);

  // "Ver pagos": cada pago capturado (Monto 1/2/3) y cada anticipo, en una sola lista.
  const pagosCxP = [];
  for (const f of facturasCalc) {
    const base = { grupo: f.proveedor || "", proveedor: f.proveedor || "", orden: f.orderNo || "", tela: f.tela || "", cliente: f.cliente || "" };
    if (+f.anticipo > 0) pagosCxP.push({ ...base, fecha: "", tipo: "Anticipo", monto: +f.anticipo });
    for (const n of [1, 2, 3]) {
      if (+f[`monto${n}`] > 0) pagosCxP.push({ ...base, fecha: f[`fPago${n}`] || "", tipo: `Pago ${n}`, monto: +f[`monto${n}`] });
    }
  }
  const proveedoresUnicos = Array.from(new Set(facturasCalc.map((f) => f.proveedor).filter(Boolean))).sort();
  const estadosUnicos = Array.from(new Set(facturasCalc.map((f) => f.estado).filter(Boolean)));
  const ordenEstados = ["VENCIDA", "PARCIAL", "AL DÍA", "PAGADA"];
  estadosUnicos.sort((a, b) => ordenEstados.indexOf(a) - ordenEstados.indexOf(b));

  const q = busqueda.trim().toLowerCase();
  const filas = facturasCalc
    .filter((f) => !filtroProveedor || f.proveedor === filtroProveedor)
    .filter((f) => !filtroEstado || f.estado === filtroEstado)
    .filter(
      (f) =>
        !q || [f.proveedor, f.orderNo, f.tela, f.cliente, f.subProveedor, f.refProveedor].some((v) => (v || "").toLowerCase().includes(q))
    );

  filas.sort((x, y) => {
    const info = COLUMNAS_CXP.find((c) => c.key === ordenCol);
    let vx = x[ordenCol];
    let vy = y[ordenCol];
    if (info?.tipo === "fecha") {
      vx = vx ? vx.getTime() : Infinity;
      vy = vy ? vy.getTime() : Infinity;
      return ordenDir === "asc" ? vx - vy : vy - vx;
    }
    if (info?.tipo === "numero") {
      vx = +vx || 0;
      vy = +vy || 0;
      return ordenDir === "asc" ? vx - vy : vy - vx;
    }
    vx = String(vx || "").toLowerCase();
    vy = String(vy || "").toLowerCase();
    return ordenDir === "asc" ? vx.localeCompare(vy) : vy.localeCompare(vx);
  });

  const alternarOrden = (key) => {
    if (ordenCol === key) {
      setOrdenDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setOrdenCol(key);
      setOrdenDir(key === "vence" ? "asc" : "desc");
    }
  };

  const limpiarFiltros = () => {
    setBusqueda("");
    setFiltroProveedor("");
    setFiltroEstado("");
  };

  const totalPendienteFiltrado = filas.reduce((s, f) => s + (f.estado === "PAGADA" ? 0 : f.pendiente), 0);
  const totalPendienteGlobal = facturasCalc.reduce((s, f) => s + (f.estado === "PAGADA" ? 0 : f.pendiente), 0);
  const numVencidasGlobal = facturasCalc.filter((f) => f.estado === "VENCIDA").length;

  // Reporte por proveedor — siempre sobre el libro completo, no sobre el filtro activo.
  const porProveedor = Array.from(
    facturasCalc
      .reduce((m, f) => {
        const cur = m.get(f.proveedor) || { proveedor: f.proveedor, n: 0, totalReal: 0, pendiente: 0, vencidas: 0 };
        cur.n += 1;
        cur.totalReal += f.totalReal;
        cur.pendiente += f.estado === "PAGADA" ? 0 : f.pendiente;
        if (f.estado === "VENCIDA") cur.vencidas += 1;
        m.set(f.proveedor, cur);
        return m;
      }, new Map())
      .values()
  ).sort((a, b) => b.pendiente - a.pendiente);

  // Reporte por vencimiento (aging)
  const hoyMid = new Date();
  hoyMid.setHours(0, 0, 0, 0);
  const en15 = new Date(hoyMid);
  en15.setDate(en15.getDate() + 15);
  // Se basa en el mismo Estado que ya se muestra en la tabla y en el resumen (no
  // solo en la fecha de vencimiento), para que una factura VENCIDA nunca se vea
  // distinto aquí que en el resto de la pantalla — una factura con abono parcial
  // ya no cuenta como "Vencida" aunque su fecha de Vence haya pasado, igual que
  // en el Excel original.
  const bucketDe = (f) => {
    if (f.estado === "PAGADA") return null;
    if (f.estado === "VENCIDA") return "Vencidas";
    if (f.vence && f.vence <= en15) return "Por vencer (≤15 días)";
    return "Al día / sin fecha";
  };
  const porVencimiento = ["Vencidas", "Por vencer (≤15 días)", "Al día / sin fecha"].map((nombre) => {
    const del = facturasCalc.filter((f) => bucketDe(f) === nombre);
    return { nombre, n: del.length, pendiente: del.reduce((s, f) => s + f.pendiente, 0) };
  });

  const set = (key) => (val) => setForm((p) => ({ ...p, [key]: val }));

  return (
    <div className="space-y-3 pb-8">
      <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs font-semibold text-teal-700 uppercase tracking-wider">Cuentas por Pagar</p>
          <p className="text-sm text-slate-600 mt-0.5">
            {facturasCalc.length} facturas activas · {numVencidasGlobal} vencidas · Pendiente USD{" "}
            {totalPendienteGlobal.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setImportOpen(true)}
            data-testid="cxp-importar-btn"
            className="text-xs font-semibold px-3 py-2 rounded-lg whitespace-nowrap bg-white border-2 border-dashed border-teal-400 text-teal-700 hover:bg-teal-50"
          >
            📁 Importar de Excel
          </button>
          <button
            type="button"
            onClick={abrirNueva}
            data-testid="cxp-nueva-btn"
            className="text-xs font-semibold px-3 py-2 rounded-lg whitespace-nowrap bg-slate-900 text-white hover:bg-slate-800"
          >
            + Nueva factura
          </button>
          <button
            type="button"
            onClick={() => exportarCxPExcel(facturasCalc)}
            data-testid="cxp-exportar-excel"
            className="text-xs font-semibold px-3 py-2 rounded-lg whitespace-nowrap border border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          >
            📗 Exportar a Excel
          </button>
          <button
            type="button"
            onClick={() => setVerPagos(true)}
            data-testid="cxp-ver-pagos"
            className="text-xs font-semibold px-3 py-2 rounded-lg whitespace-nowrap border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            🧾 Ver pagos
          </button>
        </div>
      </div>

      {verPagos && (
        <VerPagos
          titulo="Pagos a proveedores (Cuentas por Pagar)"
          pagos={pagosCxP}
          etiquetaGrupo="Proveedor"
          columnas={[
            { key: "proveedor", label: "Proveedor" },
            { key: "orden", label: "No. Orden" },
            { key: "tela", label: "Tela" },
            { key: "cliente", label: "Cliente" },
            { key: "tipo", label: "Pago" },
          ]}
          nota="Incluye los pagos 1, 2 y 3 de cada factura y sus anticipos. Los anticipos no tienen fecha, así que no entran cuando filtras por fechas."
          placeholder="Proveedor, orden, tela, cliente…"
          archivo={`${EMPRESA.nombre} - Pagos CxP`}
          onCerrar={() => setVerPagos(false)}
        />
      )}

      {/* Reportes: por proveedor y por vencimiento */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide px-4 pt-3 pb-2">Por proveedor</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 text-slate-500">
                  <th className="text-left px-3 py-1.5 font-semibold">Proveedor</th>
                  <th className="text-right px-3 py-1.5 font-semibold">#</th>
                  <th className="text-right px-3 py-1.5 font-semibold">Pendiente USD</th>
                  <th className="text-right px-3 py-1.5 font-semibold">Vencidas</th>
                </tr>
              </thead>
              <tbody>
                {porProveedor.map((p) => (
                  <tr key={p.proveedor} className="border-t border-slate-100">
                    <td className="px-3 py-1.5">{p.proveedor}</td>
                    <td className="px-3 py-1.5 text-right">{p.n}</td>
                    <td className="px-3 py-1.5 text-right font-medium">
                      {p.pendiente.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className={`px-3 py-1.5 text-right ${p.vencidas > 0 ? "text-rose-600 font-semibold" : ""}`}>{p.vencidas}</td>
                  </tr>
                ))}
                {porProveedor.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center text-slate-400 py-4">Sin datos</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide px-4 pt-3 pb-2">Por vencimiento</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 text-slate-500">
                  <th className="text-left px-3 py-1.5 font-semibold">Rango</th>
                  <th className="text-right px-3 py-1.5 font-semibold">#</th>
                  <th className="text-right px-3 py-1.5 font-semibold">Pendiente USD</th>
                </tr>
              </thead>
              <tbody>
                {porVencimiento.map((b) => (
                  <tr key={b.nombre} className="border-t border-slate-100">
                    <td className={`px-3 py-1.5 ${b.nombre === "Vencidas" ? "text-rose-600 font-semibold" : ""}`}>{b.nombre}</td>
                    <td className="px-3 py-1.5 text-right">{b.n}</td>
                    <td className="px-3 py-1.5 text-right font-medium">
                      {b.pendiente.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl border border-slate-200 p-3 flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="🔎 Buscar: proveedor, orden, tela, cliente…"
          data-testid="cxp-buscar"
          className="flex-1 min-w-[220px] border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30"
        />
        <select
          value={filtroProveedor}
          onChange={(e) => setFiltroProveedor(e.target.value)}
          data-testid="cxp-filtro-proveedor"
          className="border border-slate-200 rounded-lg px-2 py-2 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30"
        >
          <option value="">Proveedor: todos</option>
          {proveedoresUnicos.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <select
          value={filtroEstado}
          onChange={(e) => setFiltroEstado(e.target.value)}
          data-testid="cxp-filtro-estado"
          className="border border-slate-200 rounded-lg px-2 py-2 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30"
        >
          <option value="">Estado: todos</option>
          {estadosUnicos.map((e) => (
            <option key={e} value={e}>{e}</option>
          ))}
        </select>
        {(busqueda || filtroProveedor || filtroEstado) && (
          <button
            type="button"
            onClick={limpiarFiltros}
            data-testid="cxp-limpiar-filtros"
            className="text-xs font-medium text-slate-500 hover:text-rose-600 px-2"
          >
            ✕ Limpiar filtros
          </button>
        )}
        <span className="text-xs text-slate-400 ml-auto whitespace-nowrap">
          {filas.length} de {facturasCalc.length} facturas · Pendiente USD{" "}
          {totalPendienteFiltrado.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      </div>

      {facturasCalc.length === 0 && (
        <p className="text-center text-sm text-slate-400 py-10">
          Todavía no hay facturas de Cuentas por Pagar. Importa tu hoja PROVEEDORES o captura una manualmente.
        </p>
      )}

      {facturasCalc.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-[1100px] w-full border-collapse text-xs">
              <thead>
                <tr className="bg-slate-50">
                  {COLUMNAS_CXP.map((c) => (
                    <th
                      key={c.key}
                      onClick={() => alternarOrden(c.key)}
                      data-testid={`cxp-col-${c.key}`}
                      className={`text-left font-semibold text-slate-500 uppercase tracking-wide text-[10px] px-2 py-2 border-b border-slate-200 whitespace-nowrap cursor-pointer select-none hover:text-teal-700 ${
                        c.tipo === "numero" ? "text-right" : ""
                      }`}
                    >
                      {c.label}
                      {ordenCol === c.key ? (ordenDir === "asc" ? " ▲" : " ▼") : ""}
                    </th>
                  ))}
                  <th className="px-2 py-2 border-b border-slate-200"></th>
                </tr>
              </thead>
              <tbody>
                {filas.map((f, idx) => (
                  <tr
                    key={f._key || idx}
                    data-testid={`cxp-fila-${f._key}`}
                    className={`border-b border-slate-100 ${f.estado === "VENCIDA" ? "bg-rose-50/40" : "hover:bg-slate-50"}`}
                  >
                    <td className="px-2 py-1.5 font-semibold whitespace-nowrap">{f.proveedor}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap">{f.orderNo}</td>
                    <td className="px-2 py-1.5">{f.tela || "—"}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap">{f.cliente || "—"}</td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">
                      {f.totalReal.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">
                      {f.anticipo ? (+f.anticipo).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"}
                    </td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">
                      {f.pagado ? f.pagado.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"}
                    </td>
                    <td className="px-2 py-1.5 text-right font-semibold whitespace-nowrap">
                      {f.pendiente.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap">{f.venceStr || "—"}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${ESTADO_ESTILO_CXP[f.estado] || ""}`}>
                        {f.estado}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      {f.estado !== "PAGADA" && (
                        <button
                          type="button"
                          onClick={() => abrirPago(f)}
                          title="Registrar pago"
                          data-testid={`cxp-pago-${f._key}`}
                          className="text-slate-400 hover:text-teal-600 px-1"
                        >
                          💵
                        </button>
                      )}
                      <button type="button" onClick={() => abrirEditar(f)} title="Editar" className="text-slate-400 hover:text-teal-600 px-1">
                        ✏️
                      </button>
                      <button type="button" onClick={() => eliminar(f)} title="Eliminar" className="text-slate-400 hover:text-rose-600 px-1">
                        🗑
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal: importar de Excel */}
      {importOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-3"
          onClick={() => setImportOpen(false)}
          data-testid="cxp-importar-modal"
        >
          <div className="bg-white rounded-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-teal-700 uppercase tracking-wider">Importar Cuentas por Pagar</p>
                <p className="text-sm text-slate-600">Sube tu libro con la hoja "PROVEEDORES" (.xlsx o .xlsm)</p>
              </div>
              <button onClick={() => setImportOpen(false)} className="text-slate-400 hover:text-slate-600 text-2xl leading-none px-2">×</button>
            </div>
            <div className="p-4 space-y-3">
              <label className="cursor-pointer flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                <span className="text-2xl">📁</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800">
                    {importLoading ? "Procesando…" : fileName || "Elegir archivo Excel"}
                  </p>
                  <p className="text-xs text-slate-500">Solo se importan facturas Parcial, Vencida o Al día</p>
                </div>
                <input type="file" accept=".xlsx,.xlsm,.xls" onChange={handleFile} className="hidden" data-testid="cxp-importar-file" />
              </label>
              {importMsg && (
                <p className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">{importMsg}</p>
              )}
              {updatedAt && (
                <p className="text-[11px] text-slate-400">
                  Última actualización: {new Date(updatedAt).toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" })}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal: nueva factura / editar */}
      {formOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-3"
          onClick={() => setFormOpen(false)}
          data-testid="cxp-form-modal"
        >
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white">
              <p className="text-xs font-semibold text-teal-700 uppercase tracking-wider">
                {form._key ? "Editar factura" : "Nueva factura"}
              </p>
              <button onClick={() => setFormOpen(false)} className="text-slate-400 hover:text-slate-600 text-2xl leading-none px-2">×</button>
            </div>
            <div className="p-4 grid grid-cols-2 gap-3">
              <label className="text-xs text-slate-500">Proveedor
                <input className={inputCxP} value={form.proveedor} onChange={(e) => set("proveedor")(e.target.value)} data-testid="cxp-form-proveedor" />
              </label>
              <label className="text-xs text-slate-500">No. de Orden
                <input className={inputCxP} value={form.orderNo} onChange={(e) => set("orderNo")(e.target.value)} data-testid="cxp-form-orderno" />
              </label>
              <label className="text-xs text-slate-500">Sub-Proveedor
                <input className={inputCxP} value={form.subProveedor} onChange={(e) => set("subProveedor")(e.target.value)} />
              </label>
              <label className="text-xs text-slate-500">Tela
                <input className={inputCxP} value={form.tela} onChange={(e) => set("tela")(e.target.value)} />
              </label>
              <label className="text-xs text-slate-500">Cliente
                <input className={inputCxP} value={form.cliente} onChange={(e) => set("cliente")(e.target.value)} />
              </label>
              <label className="text-xs text-slate-500">Ref. Proveedor
                <input className={inputCxP} value={form.refProveedor} onChange={(e) => set("refProveedor")(e.target.value)} />
              </label>
              <label className="text-xs text-slate-500">F. Pedido (dd/mm/aaaa)
                <input className={inputCxP} value={form.fPedido} onChange={(e) => set("fPedido")(e.target.value)} placeholder="dd/mm/aaaa" />
              </label>
              <label className="text-xs text-slate-500">F. Embarque (dd/mm/aaaa)
                <input className={inputCxP} value={form.fEmbarque} onChange={(e) => set("fEmbarque")(e.target.value)} placeholder="dd/mm/aaaa" />
              </label>
              <label className="text-xs text-slate-500">Cantidad Pedida
                <input type="number" className={inputCxP} value={form.cantPedida} onChange={(e) => set("cantPedida")(e.target.value)} />
              </label>
              <label className="text-xs text-slate-500">Unidad
                <select className={inputCxP} value={form.unidad} onChange={(e) => set("unidad")(e.target.value)}>
                  <option value="MTS">MTS</option>
                  <option value="KGS">KGS</option>
                </select>
              </label>
              <label className="text-xs text-slate-500">Cantidad Embarcada
                <input type="number" className={inputCxP} value={form.cantEmbarcada} onChange={(e) => set("cantEmbarcada")(e.target.value)} />
              </label>
              <label className="text-xs text-slate-500">Precio (USD)
                <input type="number" className={inputCxP} value={form.precio} onChange={(e) => set("precio")(e.target.value)} />
              </label>
              <label className="text-xs text-slate-500">Anticipo (USD)
                <input type="number" className={inputCxP} value={form.anticipo} onChange={(e) => set("anticipo")(e.target.value)} />
              </label>
              <label className="text-xs text-slate-500">F. Anticipo (dd/mm/aaaa)
                <input className={inputCxP} value={form.fAnticipo} onChange={(e) => set("fAnticipo")(e.target.value)} placeholder="dd/mm/aaaa" />
              </label>
              <label className="text-xs text-slate-500">Plazo (días)
                <input type="number" className={inputCxP} value={form.plazo} onChange={(e) => set("plazo")(e.target.value)} />
              </label>
              <label className="text-xs text-slate-500">OC Original
                <input className={inputCxP} value={form.ocOriginal} onChange={(e) => set("ocOriginal")(e.target.value)} />
              </label>
              <div className="col-span-2 border-t border-slate-100 pt-3 grid grid-cols-3 gap-3">
                <label className="text-xs text-slate-500">F. Pago 1
                  <input className={inputCxP} value={form.fPago1} onChange={(e) => set("fPago1")(e.target.value)} placeholder="dd/mm/aaaa" />
                </label>
                <label className="text-xs text-slate-500">Monto 1
                  <input type="number" className={inputCxP} value={form.monto1} onChange={(e) => set("monto1")(e.target.value)} />
                </label>
                <div />
                <label className="text-xs text-slate-500">F. Pago 2
                  <input className={inputCxP} value={form.fPago2} onChange={(e) => set("fPago2")(e.target.value)} placeholder="dd/mm/aaaa" />
                </label>
                <label className="text-xs text-slate-500">Monto 2
                  <input type="number" className={inputCxP} value={form.monto2} onChange={(e) => set("monto2")(e.target.value)} />
                </label>
                <div />
                <label className="text-xs text-slate-500">F. Pago 3
                  <input className={inputCxP} value={form.fPago3} onChange={(e) => set("fPago3")(e.target.value)} placeholder="dd/mm/aaaa" />
                </label>
                <label className="text-xs text-slate-500">Monto 3
                  <input type="number" className={inputCxP} value={form.monto3} onChange={(e) => set("monto3")(e.target.value)} />
                </label>
              </div>
            </div>
            <div className="p-4 border-t border-slate-100 flex justify-end gap-2 sticky bottom-0 bg-white">
              <button type="button" onClick={() => setFormOpen(false)} className="text-xs font-medium text-slate-500 px-3 py-2">Cancelar</button>
              <button
                type="button"
                onClick={guardarForm}
                disabled={guardando}
                data-testid="cxp-form-guardar"
                className="text-xs font-semibold px-4 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-60"
              >
                {guardando ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: registrar pago */}
      {pagoDe && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-3" onClick={() => setPagoDe(null)} data-testid="cxp-pago-modal">
          <div className="bg-white rounded-2xl w-full max-w-sm p-4" onClick={(e) => e.stopPropagation()}>
            <p className="text-xs font-semibold text-teal-700 uppercase tracking-wider mb-1">Registrar pago</p>
            <p className="text-sm text-slate-600 mb-3">{pagoDe.tela} · {pagoDe.orderNo} · {pagoDe.proveedor}</p>
            <label className="text-xs text-slate-500 block mb-2">Fecha
              <input className={inputCxP} value={pagoFecha} onChange={(e) => setPagoFecha(e.target.value)} placeholder="dd/mm/aaaa" data-testid="cxp-pago-fecha" />
            </label>
            <label className="text-xs text-slate-500 block mb-3">Monto (USD)
              <input type="number" className={inputCxP} value={pagoMonto} onChange={(e) => setPagoMonto(e.target.value)} data-testid="cxp-pago-monto" />
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setPagoDe(null)} className="text-xs font-medium text-slate-500 px-3 py-2">Cancelar</button>
              <button
                type="button"
                onClick={confirmarPago}
                disabled={guardando}
                data-testid="cxp-pago-confirmar"
                className="text-xs font-semibold px-4 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-60"
              >
                {guardando ? "Guardando…" : "Registrar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── CHAMPTEX (cuenta corriente con la línea de crédito de Champtex Global) ──
// Reimplementación fiel de las macros del libro "CUENTAS POR PAGAR MACROS.xlsm":
//   - Hoja "TOTALES CHAMPTEX": bloques mensuales (ORIGINAL BALANCE / PAYMENT /
//     traspasos MOVED TO-FROM / PENDING BALANCE). Verificado celda por celda:
//     la suma de "PENDING BALANCE" de los 88 bloques reales cuadra exacto con
//     el "Gran Total" del Excel (597,359.29).
//   - Hoja "DESGLOCE": una línea por renglón de factura, con 4 parejas de
//     casillas de pago (fecha/monto) que se llenan de abajo hacia arriba.
//   - Macro "RegistrarPago" (Hoja28.cls): reparte un depósito entre los meses
//     con saldo pendiente (el más antiguo primero) y, si algo cae en un mes
//     futuro al mes real del depósito, recorre ("traspasa") esa misma cantidad
//     de meses hacia adelante desde el primer mes abierto que encuentre.
//   - Macro "AplicarNuevosPagos" (Module1.bas): baja los pagos ya registrados
//     en TOTALES CHAMPTEX (los que no traen marca "aplicado") a las facturas
//     de DESGLOCE cuyo vencimiento sea ese mes o después, en el orden en que
//     aparecen, llenando la primera casilla de pago libre.
const MESES_EN_CHAMPTEX = [
  "JANUARY", "FEBRUARY", "MARCH", "APRIL", "MAY", "JUNE",
  "JULY", "AUGUST", "SEPTEMBER", "OCTOBER", "NOVEMBER", "DECEMBER",
];

const round2 = (n) => Math.round((+n || 0) * 100) / 100;

const primerDiaMes = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const mesISO = (d) => {
  const p = primerDiaMes(d);
  return `${p.getFullYear()}-${String(p.getMonth() + 1).padStart(2, "0")}-01`;
};
const mesDesdeISO = (iso) => {
  const [y, m] = iso.split("-").map(Number);
  return new Date(y, m - 1, 1);
};
const sumarMesesISO = (iso, n) => {
  const d = mesDesdeISO(iso);
  return mesISO(new Date(d.getFullYear(), d.getMonth() + n, 1));
};
const diffMesesISO = (isoA, isoB) => {
  const a = mesDesdeISO(isoA);
  const b = mesDesdeISO(isoB);
  return (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
};
const diffMesesDesde = (mesIso, fecha) => {
  const d0 = mesDesdeISO(mesIso);
  if (fecha < d0) return 0;
  return (fecha.getFullYear() - d0.getFullYear()) * 12 + (fecha.getMonth() - d0.getMonth());
};
const formatMesLabelChamptex = (iso) => {
  const d = mesDesdeISO(iso);
  return `${MESES_EN_CHAMPTEX[d.getMonth()]} ${d.getFullYear()}`;
};
const formatMesCortoChamptex = (iso) => {
  const d = mesDesdeISO(iso);
  return d.toLocaleDateString("es-MX", { month: "short", year: "numeric" });
};

const esPagoChamptex = (tipo) => /^PAYMENT/i.test(String(tipo || "").trim());
// Pagos que sí corresponden a una factura y deben (o pueden) bajarse a DESGLOCE.
// Excluye los "PAYMENT FROM C1…": son créditos tomados del saldo de la comisión
// C1 para cubrir intereses moratorios — un ajuste interno del ledger, nunca se
// reparten a ninguna factura, así que no cuentan como "pagos sin aplicar".
const esPagoAplicableChamptex = (tipo) =>
  esPagoChamptex(tipo) && !/^PAYMENT\s+FROM\s+C1/i.test(String(tipo || "").trim());

// ── Cálculo derivado de un bloque mensual (saldo corrido, meses de atraso e
// interés informativo del 1% mensual sobre cada PAYMENT) ─────────────────────
const calcularBloqueChamptex = (bloque) => {
  let saldo = +bloque.saldoInicial || 0;
  let interesTotal = 0;
  const movimientos = (bloque.movimientos || []).map((m) => {
    saldo = round2(saldo + (+m.cargo || 0) - (+m.abono || 0));
    let mesesAtraso = null;
    let interes = 0;
    if (esPagoChamptex(m.tipo)) {
      const fecha = parseFechaDDMMYYYY(m.fecha);
      mesesAtraso = fecha ? diffMesesDesde(bloque.mes, fecha) : 0;
      interes = round2((+m.abono || 0) * 0.01 * mesesAtraso);
      interesTotal = round2(interesTotal + interes);
    }
    return { ...m, saldo, mesesAtraso, interes };
  });
  return { ...bloque, movimientos, saldoFinal: saldo, interesTotal };
};

const calcularChamptex = (bloques) =>
  (bloques || [])
    .map(calcularBloqueChamptex)
    .sort((a, b) => a.mes.localeCompare(b.mes));

const calcularLineaChamptex = (l) => {
  const pagado = round2((l.pagos || []).reduce((s, p) => s + (+p.monto || 0), 0));
  const pendiente = round2((+l.totalReal || 0) - pagado);
  return { ...l, pagado, pendiente };
};

// ── Parseo de las hojas TOTALES CHAMPTEX y DESGLOCE ──────────────────────────
const parseLibroChamptexTC = (workbook) => {
  const hoja = workbook.SheetNames.find((n) => n.trim().toUpperCase() === "TOTALES CHAMPTEX");
  if (!hoja) return [];
  const ws = workbook.Sheets[hoja];
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  const bloques = [];
  let cur = null;
  let contador = 0;
  for (let r = 1; r < rows.length; r++) {
    const row = rows[r] || [];
    const colA = row[0];
    let nota = String(row[1] || "").trim();
    // Algunos renglones de ajuste manual traen la etiqueta en la columna A.
    if (!nota && typeof colA === "string" && colA.trim()) nota = colA.trim();
    if (!nota) continue;
    if (/GRAN TOTAL/i.test(nota)) continue;
    if (/NAL BALANCE/i.test(nota)) {
      if (!(colA instanceof Date)) {
        cur = null;
        continue;
      }
      cur = { mes: mesISO(colA), saldoInicial: round2(+row[5] || 0), movimientos: [] };
      bloques.push(cur);
      continue;
    }
    if (/^PENDING BALANCE$/i.test(nota)) {
      cur = null;
      continue;
    }
    if (!cur) continue;
    const fecha = row[2];
    contador += 1;
    cur.movimientos.push({
      _id: `mvi${contador}`,
      tipo: nota,
      fecha: fecha instanceof Date ? formatearFechaCelda(fecha) : "",
      cargo: round2(+row[3] || 0),
      abono: round2(+row[4] || 0),
      tc: row[8] !== "" && !isNaN(+row[8]) ? +row[8] : null,
      aplicado: String(row[9] || "").trim().toUpperCase() === "SI",
    });
  }
  return bloques;
};

const parseLibroChamptexDesgloce = (workbook) => {
  const hoja = workbook.SheetNames.find((n) => n.trim().toUpperCase() === "DESGLOCE");
  if (!hoja) return [];
  const ws = workbook.Sheets[hoja];
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  const lineas = [];
  let lastOrder = null, lastEtd = "", lastPay = "", lastImp = "", lastVence = "";
  let contador = 0;
  for (let r = 2; r < rows.length; r++) {
    const row = rows[r] || [];
    const orderNo = String(row[0] || "").trim();
    const itemNo = String(row[5] || "").trim();
    const totalReal = row[8];
    if (orderNo) {
      lastOrder = orderNo;
      lastEtd = row[1] instanceof Date ? formatearFechaCelda(row[1]) : "";
      lastPay = row[2] instanceof Date ? formatearFechaCelda(row[2]) : "";
      lastImp = String(row[3] || "").trim();
      lastVence = row[4] instanceof Date ? formatearFechaCelda(row[4]) : "";
    }
    if (!itemNo && !totalReal) continue;
    if (!lastOrder) continue;
    contador += 1;
    const pagos = [[11, 12], [13, 14], [15, 16], [17, 18]].map(([fc, mc]) => ({
      fecha: row[fc] instanceof Date ? formatearFechaCelda(row[fc]) : "",
      monto: round2(+row[mc] || 0),
    }));
    lineas.push({
      _key: `dgi${contador}`,
      orderNo: lastOrder,
      etd: lastEtd,
      payToFactory: lastPay,
      importador: lastImp,
      vence: lastVence,
      itemNo,
      qty: +row[6] || 0,
      unitPrice: +row[7] || 0,
      totalReal: round2(+totalReal || 0),
      pagos,
    });
  }
  return lineas;
};

// ── Simulación "Registrar pago" (RegistrarPago / Hoja28.cls) ─────────────────
// Reparte `monto` recibido en `fecha` entre los bloques con saldo pendiente
// (el más antiguo primero); lo que caiga en un mes futuro al mes real del pago
// se recorre esa misma cantidad de meses hacia adelante desde el primer mes
// abierto que encuentre (igual que el "traspaso" de la macro). No escribe nada
// todavía — regresa el plan para mostrarlo y pedir confirmación.
function simularRegistrarPagoChamptex(bloquesCalc, { fecha, monto, tc, mesObjetivo }) {
  const TOL = 0.01;
  const copia = bloquesCalc.map((b) => ({
    mes: b.mes,
    saldoInicial: b.saldoInicial,
    movimientos: (b.movimientos || []).map((m) => ({ tipo: m.tipo, fecha: m.fecha, cargo: m.cargo, abono: m.abono, tc: m.tc, aplicado: m.aplicado })),
  }));
  const porMes = new Map(copia.map((b) => [b.mes, b]));
  const pendienteDe = (b) => {
    let s = +b.saldoInicial || 0;
    for (const m of b.movimientos) s += (+m.cargo || 0) - (+m.abono || 0);
    return round2(s);
  };
  const ordenados = () => copia.slice().sort((a, b) => a.mes.localeCompare(b.mes));

  const mesInicioPago = mesISO(fecha);
  const fechaStr = formatearFechaCelda(fecha);

  let restante = round2(monto);
  const partes = [];
  const avisos = [];
  let guard = 0;
  while (restante > TOL && guard < 500) {
    guard += 1;
    const candidato = ordenados().find(
      (b) => pendienteDe(b) > TOL && (!mesObjetivo || b.mes >= mesObjetivo)
    );
    if (!candidato) break;
    const aplicar = Math.min(pendienteDe(candidato), restante);
    candidato.movimientos.push({
      tipo: "PAYMENT",
      fecha: fechaStr,
      cargo: 0,
      abono: round2(aplicar),
      tc: tc || null,
      aplicado: false,
    });
    partes.push({ mes: candidato.mes, monto: round2(aplicar) });
    restante = round2(restante - aplicar);
  }
  if (restante > TOL) {
    avisos.push(`Sobran ${round2(restante).toLocaleString("es-MX", { minimumFractionDigits: 2 })} USD sin bloque de mes con saldo pendiente donde aplicar.`);
  }

  for (const parte of partes) {
    const n = diffMesesISO(mesInicioPago, parte.mes);
    if (n <= 0) continue;
    let porMover = parte.monto;
    let guard2 = 0;
    while (porMover > TOL && guard2 < 50) {
      guard2 += 1;
      const origen = ordenados().find(
        (b) => pendienteDe(b) > TOL && b.mes > mesInicioPago && b.mes !== parte.mes
      );
      if (!origen) {
        avisos.push(`${formatMesLabelChamptex(parte.mes)}: faltaron ${round2(porMover).toLocaleString("es-MX", { minimumFractionDigits: 2 })} USD por recorrer, no hay más meses abiertos con saldo.`);
        break;
      }
      const mover = Math.min(porMover, pendienteDe(origen));
      const mesDestino = sumarMesesISO(origen.mes, n);
      origen.movimientos.push({
        tipo: `MOVED TO ${formatMesLabelChamptex(mesDestino)}`,
        fecha: fechaStr,
        cargo: 0,
        abono: round2(mover),
        tc: null,
        aplicado: false,
      });
      let destino = porMes.get(mesDestino);
      if (!destino) {
        destino = { mes: mesDestino, saldoInicial: 0, movimientos: [] };
        copia.push(destino);
        porMes.set(mesDestino, destino);
      }
      destino.movimientos.push({
        tipo: `MOVED FROM ${formatMesLabelChamptex(origen.mes)}`,
        fecha: fechaStr,
        cargo: round2(mover),
        abono: 0,
        tc: null,
        aplicado: false,
      });
      porMover = round2(porMover - mover);
    }
  }

  copia.sort((a, b) => a.mes.localeCompare(b.mes));
  const totalAplicado = round2(monto - Math.max(restante, 0));
  return { bloques: copia, partes, avisos, totalAplicado, restante: Math.max(restante, 0) };
}

// ── Simulación "Aplicar pagos a facturas" (AplicarNuevosPagos / Module1.bas) ──
// Toma los movimientos PAYMENT de TOTALES CHAMPTEX que aún no se han bajado a
// DESGLOCE (aplicado=false) y los reparte, en el orden en que aparecen, entre
// las facturas de DESGLOCE con saldo pendiente cuyo vencimiento sea ese mes o
// después — llenando la primera casilla de pago libre de cada factura.
function simularAplicarPagosChamptex(bloquesCalc, lineas) {
  const TOL = 0.01;
  const bloquesOrdenados = bloquesCalc.slice().sort((a, b) => a.mes.localeCompare(b.mes));
  const pagos = [];
  for (const b of bloquesOrdenados) {
    for (const m of b.movimientos || []) {
      if (esPagoAplicableChamptex(m.tipo) && !m.aplicado && (+m.abono || 0) > TOL) {
        pagos.push({ id: m._id, mesBloque: b.mes, monto: +m.abono, fecha: m.fecha });
      }
    }
  }
  if (pagos.length === 0) {
    return { lineas, aplicadoIds: [], asignaciones: [], avisos: ["No hay pagos nuevos por aplicar — solo se procesan movimientos PAYMENT que todavía no se hayan bajado a DESGLOCE."], totalPlan: 0 };
  }

  const lineasCalc = lineas.map((l) => {
    const pagado = round2((l.pagos || []).reduce((s, p) => s + (+p.monto || 0), 0));
    const vence = l.vence ? parseFechaDDMMYYYY(l.vence) : null;
    return { _key: l._key, orderNo: l.orderNo, itemNo: l.itemNo, _pendiente: round2((+l.totalReal || 0) - pagado), _venceMes: vence ? mesISO(vence) : null };
  });

  const asignaciones = [];
  const avisos = [];
  let totalPlan = 0;
  for (const pago of pagos) {
    let resto = round2(pago.monto);
    for (const l of lineasCalc) {
      if (resto <= TOL) break;
      if (l._pendiente > TOL && l._venceMes && l._venceMes >= pago.mesBloque) {
        const ap = Math.min(l._pendiente, resto);
        asignaciones.push({ lineaKey: l._key, orderNo: l.orderNo, itemNo: l.itemNo, monto: round2(ap), fecha: pago.fecha });
        l._pendiente = round2(l._pendiente - ap);
        resto = round2(resto - ap);
        totalPlan = round2(totalPlan + ap);
      }
    }
    if (resto > TOL) {
      avisos.push(`Pago del ${pago.fecha} (bloque ${formatMesLabelChamptex(pago.mesBloque)}): sobran ${resto.toLocaleString("es-MX", { minimumFractionDigits: 2 })} USD sin factura donde aplicar.`);
    }
  }

  const lineasNuevas = lineas.map((l) => ({ ...l, pagos: (l.pagos || []).map((p) => ({ ...p })) }));
  const porKey = new Map(lineasNuevas.map((l) => [l._key, l]));
  for (const asig of asignaciones) {
    const linea = porKey.get(asig.lineaKey);
    if (!linea) continue;
    const slot = linea.pagos.findIndex((p) => !p.fecha && !p.monto);
    if (slot === -1) {
      avisos.push(`${linea.orderNo} (${linea.itemNo}): ya tiene sus 4 pagos llenos, no se aplicaron ${asig.monto.toLocaleString("es-MX", { minimumFractionDigits: 2 })} USD.`);
      continue;
    }
    linea.pagos[slot] = { fecha: asig.fecha, monto: asig.monto };
  }

  const aplicadoIds = pagos.map((p) => p.id);
  return { lineas: lineasNuevas, aplicadoIds, asignaciones, avisos, totalPlan: round2(totalPlan) };
}

const marcarAplicados = (bloques, aplicadoIds) => {
  const set = new Set(aplicadoIds);
  return bloques.map((b) => ({
    ...b,
    movimientos: (b.movimientos || []).map((m) => (set.has(m._id) ? { ...m, aplicado: true } : m)),
  }));
};
function exportarChamptexExcel(bloquesCalc, lineasCalc) {
  const filasTC = [];
  for (const b of bloquesCalc.slice().sort((a, c) => a.mes.localeCompare(c.mes))) {
    filasTC.push({ Mes: formatMesLabelChamptex(b.mes), Nota: "ORIGINAL BALANCE", Fecha: "", Cargo: "", Abono: "", Saldo: b.saldoInicial, "Meses atraso": "", Interés: "", "T.C.": "" });
    for (const m of b.movimientos) {
      filasTC.push({ Mes: "", Nota: m.tipo, Fecha: m.fecha, Cargo: m.cargo || "", Abono: m.abono || "", Saldo: m.saldo, "Meses atraso": m.mesesAtraso ?? "", Interés: m.interes || "", "T.C.": m.tc ?? "" });
    }
    filasTC.push({ Mes: formatMesLabelChamptex(b.mes), Nota: "PENDING BALANCE", Fecha: "", Cargo: "", Abono: "", Saldo: b.saldoFinal, "Meses atraso": "", Interés: b.interesTotal || "", "T.C.": "" });
  }
  const filasDg = lineasCalc.map((l) => ({
    "Order No.": l.orderNo,
    ETD: l.etd,
    "Pay to Factory": l.payToFactory,
    Importador: l.importador,
    Vence: l.vence,
    Item: l.itemNo,
    Cantidad: l.qty,
    "Precio Unit.": l.unitPrice,
    "Sales Amt": l.totalReal,
    Pagado: l.pagado,
    Pendiente: l.pendiente,
    "1er Pago Fecha": l.pagos?.[0]?.fecha || "",
    "1er Pago Monto": l.pagos?.[0]?.monto || "",
    "2do Pago Fecha": l.pagos?.[1]?.fecha || "",
    "2do Pago Monto": l.pagos?.[1]?.monto || "",
    "3er Pago Fecha": l.pagos?.[2]?.fecha || "",
    "3er Pago Monto": l.pagos?.[2]?.monto || "",
    "4to Pago Fecha": l.pagos?.[3]?.fecha || "",
    "4to Pago Monto": l.pagos?.[3]?.monto || "",
  }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(filasTC), "TOTALES CHAMPTEX");
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(filasDg), "DESGLOCE");
  XLSX.writeFile(wb, `${EMPRESA.nombre} - Champtex ${hoy().replace(/\//g, "-")}.xlsx`);
}

const COLUMNAS_CHAMPTEX_DG = [
  { key: "orderNo", label: "Order No.", tipo: "texto" },
  { key: "itemNo", label: "Item", tipo: "texto" },
  { key: "importador", label: "Importador", tipo: "texto" },
  { key: "vence", label: "Vence", tipo: "fecha" },
  { key: "totalReal", label: "Sales Amt USD", tipo: "numero" },
  { key: "pagado", label: "Pagado USD", tipo: "numero" },
  { key: "pendiente", label: "Pendiente USD", tipo: "numero" },
];

function Champtex() {
  const [bloques, setBloques] = useState([]);
  const [verPagos, setVerPagos] = useState(false);
  const [lineas, setLineas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fileName, setFileName] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const [importOpen, setImportOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importMsg, setImportMsg] = useState("");

  const [pagoOpen, setPagoOpen] = useState(false);
  const [pagoFecha, setPagoFecha] = useState(hoy());
  const [pagoMonto, setPagoMonto] = useState("");
  const [pagoTC, setPagoTC] = useState("");
  const [pagoMesObjetivo, setPagoMesObjetivo] = useState("");
  const [pagoSim, setPagoSim] = useState(null);

  const [aplicarSim, setAplicarSim] = useState(null);

  const [soloAbiertos, setSoloAbiertos] = useState(true);
  const [bloqueExpandido, setBloqueExpandido] = useState(null);

  const [busquedaLineas, setBusquedaLineas] = useState("");
  const [soloPendientesLineas, setSoloPendientesLineas] = useState(true);
  const [ordenColLinea, setOrdenColLinea] = useState("vence");
  const [ordenDirLinea, setOrdenDirLinea] = useState("asc");

  useEffect(() => {
    (async () => {
      try {
        const data = await api.get("/champtex");
        if (data) {
          setBloques(Array.isArray(data.bloques) ? data.bloques : []);
          setLineas(Array.isArray(data.lineas) ? data.lineas : []);
          setFileName(data.file_name || "");
          setUpdatedAt(data.updated_at || null);
        }
      } catch (e) {
        // silencioso
      }
      setLoading(false);
    })();
  }, []);

  const persistir = async (bloquesNuevos, lineasNuevas, name) => {
    setGuardando(true);
    try {
      const saved = await api.post("/champtex", {
        bloques: bloquesNuevos,
        lineas: lineasNuevas,
        file_name: name !== undefined ? name : fileName,
      });
      if (saved) {
        setBloques(Array.isArray(saved.bloques) ? saved.bloques : bloquesNuevos);
        setLineas(Array.isArray(saved.lineas) ? saved.lineas : lineasNuevas);
        if (saved.updated_at) setUpdatedAt(saved.updated_at);
        if (name !== undefined) setFileName(name);
      }
    } catch (e) {
      console.error("No se pudo guardar Champtex:", e);
      alert("No se pudo guardar el cambio. Intenta de nuevo.");
    }
    setGuardando(false);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (
      !window.confirm(
        'Esto va a REEMPLAZAR todo el historial de Champtex guardado en la app (TOTALES CHAMPTEX y DESGLOCE) con lo que traiga este archivo.\n\nÚsalo solo para una reconciliación completa contra el Excel. Si solo quieres registrar un pago nuevo, cierra esto y usa "Registrar pago".\n\n¿Continuar?'
      )
    ) {
      e.target.value = "";
      return;
    }
    setImportLoading(true);
    setImportMsg("");
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array", cellDates: true });
      const nuevosBloques = parseLibroChamptexTC(wb);
      const nuevasLineas = parseLibroChamptexDesgloce(wb);
      if (nuevosBloques.length === 0 && nuevasLineas.length === 0) {
        setImportMsg('No se encontraron las hojas "TOTALES CHAMPTEX" y/o "DESGLOCE" en el archivo.');
      } else {
        await persistir(nuevosBloques, nuevasLineas, file.name);
        setImportMsg(`✅ Importado: ${nuevosBloques.length} bloques mensuales y ${nuevasLineas.length} líneas de factura.`);
      }
    } catch (err) {
      setImportMsg("Error al leer el archivo: " + err.message);
    }
    setImportLoading(false);
  };

  if (loading) {
    return <p className="text-center text-sm text-slate-400 py-10">Cargando cuenta Champtex…</p>;
  }

  const bloquesCalc = calcularChamptex(bloques);

  // "Ver pagos": todos los movimientos PAYMENT de TOTALES CHAMPTEX. Un depósito
  // que se repartió entre varios meses aparece como un renglón por mes.
  const pagosChamptex = [];
  for (const b of bloquesCalc) {
    for (const m of b.movimientos || []) {
      if (!esPagoChamptex(m.tipo) || !(+m.abono > 0)) continue;
      pagosChamptex.push({
        fecha: m.fecha || "",
        grupo: String(m.tipo || "").trim().toUpperCase(),
        tipo: m.tipo,
        mes: formatMesLabelChamptex(b.mes),
        tc: m.tc ? Number(m.tc).toFixed(4) : "",
        aplicado: esPagoAplicableChamptex(m.tipo) ? (m.aplicado ? "Sí" : "No") : "N/A",
        monto: +m.abono,
      });
    }
  }
  const lineasCalc = lineas.map(calcularLineaChamptex);
  const granTotal = bloquesCalc.reduce((s, b) => s + b.saldoFinal, 0);
  const mesesAbiertos = bloquesCalc.filter((b) => b.saldoFinal > 0.01);
  const pendienteDesgloce = lineasCalc.reduce((s, l) => s + l.pendiente, 0);
  const pagosSinAplicar = bloquesCalc.reduce(
    (s, b) => s + b.movimientos.filter((m) => esPagoAplicableChamptex(m.tipo) && !m.aplicado && m.abono > 0.01).length,
    0
  );

  const abrirPago = () => {
    setPagoFecha(hoy());
    setPagoMonto("");
    setPagoTC("");
    setPagoMesObjetivo("");
    setPagoSim(null);
    setPagoOpen(true);
  };

  const simularPago = () => {
    const fecha = parseFechaDDMMYYYY(pagoFecha);
    if (!fecha) {
      alert("Captura la fecha del depósito (dd/mm/aaaa).");
      return;
    }
    if (!(+pagoMonto > 0)) {
      alert("Captura el monto del depósito.");
      return;
    }
    const sim = simularRegistrarPagoChamptex(bloquesCalc, {
      fecha,
      monto: +pagoMonto,
      tc: pagoTC ? +pagoTC : null,
      mesObjetivo: pagoMesObjetivo || null,
    });
    setPagoSim(sim);
  };

  const confirmarPago = async () => {
    if (!pagoSim) return;
    await persistir(pagoSim.bloques, lineas);
    setPagoOpen(false);
    setPagoSim(null);
  };

  const abrirAplicar = () => {
    const sim = simularAplicarPagosChamptex(bloquesCalc, lineas);
    setAplicarSim(sim);
  };

  const confirmarAplicar = async () => {
    if (!aplicarSim) return;
    const bloquesMarcados = marcarAplicados(bloques, aplicarSim.aplicadoIds);
    await persistir(bloquesMarcados, aplicarSim.lineas);
    setAplicarSim(null);
  };

  const bloquesVista = bloquesCalc
    .filter((b) => !soloAbiertos || b.saldoFinal > 0.01)
    .slice()
    .sort((a, b) => b.mes.localeCompare(a.mes));

  const q = busquedaLineas.trim().toLowerCase();
  let filasLineas = lineasCalc
    .filter((l) => !soloPendientesLineas || l.pendiente > 0.01)
    .filter((l) => !q || [l.orderNo, l.itemNo, l.importador].some((v) => (v || "").toLowerCase().includes(q)));

  filasLineas = filasLineas.slice().sort((a, b) => {
    const info = COLUMNAS_CHAMPTEX_DG.find((c) => c.key === ordenColLinea);
    let vx = a[ordenColLinea];
    let vy = b[ordenColLinea];
    if (info?.tipo === "fecha") {
      const dx = parseFechaDDMMYYYY(vx);
      const dy = parseFechaDDMMYYYY(vy);
      vx = dx ? dx.getTime() : Infinity;
      vy = dy ? dy.getTime() : Infinity;
      return ordenDirLinea === "asc" ? vx - vy : vy - vx;
    }
    if (info?.tipo === "numero") {
      vx = +vx || 0;
      vy = +vy || 0;
      return ordenDirLinea === "asc" ? vx - vy : vy - vx;
    }
    vx = String(vx || "").toLowerCase();
    vy = String(vy || "").toLowerCase();
    return ordenDirLinea === "asc" ? vx.localeCompare(vy) : vy.localeCompare(vx);
  });

  const alternarOrdenLinea = (key) => {
    if (ordenColLinea === key) setOrdenDirLinea((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setOrdenColLinea(key);
      setOrdenDirLinea(key === "vence" ? "asc" : "desc");
    }
  };

  const fmt = (n) => (+n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="space-y-3 pb-8">
      <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs font-semibold text-teal-700 uppercase tracking-wider">Champtex Global — cuenta corriente</p>
          <p className="text-sm text-slate-600 mt-0.5">
            Saldo total USD {fmt(granTotal)} · {mesesAbiertos.length} meses con saldo abierto
            {pagosSinAplicar > 0 && (
              <span className="text-amber-600 font-medium"> · {pagosSinAplicar} pago(s) sin aplicar a facturas</span>
            )}
          </p>
          <p className="text-[11px] text-slate-400 mt-0.5">Pendiente en DESGLOCE (referencia): USD {fmt(pendienteDesgloce)}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setImportOpen(true)}
            data-testid="champtex-importar-btn"
            className="text-xs font-semibold px-3 py-2 rounded-lg whitespace-nowrap bg-white border-2 border-dashed border-teal-400 text-teal-700 hover:bg-teal-50"
          >
            📁 Importar / reconciliar
          </button>
          <button
            type="button"
            onClick={abrirPago}
            data-testid="champtex-registrar-pago-btn"
            className="text-xs font-semibold px-3 py-2 rounded-lg whitespace-nowrap bg-teal-600 text-white hover:bg-teal-700"
          >
            💵 Registrar pago
          </button>
          <button
            type="button"
            onClick={abrirAplicar}
            data-testid="champtex-aplicar-pagos-btn"
            className="text-xs font-semibold px-3 py-2 rounded-lg whitespace-nowrap bg-slate-900 text-white hover:bg-slate-800"
          >
            ⬇ Aplicar pagos a facturas
          </button>
          <button
            type="button"
            onClick={() => exportarChamptexExcel(bloquesCalc, lineasCalc)}
            data-testid="champtex-exportar-excel"
            className="text-xs font-semibold px-3 py-2 rounded-lg whitespace-nowrap border border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          >
            📗 Exportar a Excel
          </button>
          <button
            type="button"
            onClick={() => setVerPagos(true)}
            data-testid="champtex-ver-pagos"
            className="text-xs font-semibold px-3 py-2 rounded-lg whitespace-nowrap border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            🧾 Ver pagos
          </button>
        </div>
      </div>

      {verPagos && (
        <VerPagos
          titulo="Pagos a Champtex"
          pagos={pagosChamptex}
          etiquetaGrupo="Tipo"
          columnas={[
            { key: "tipo", label: "Tipo" },
            { key: "mes", label: "Mes aplicado" },
            { key: "tc", label: "T.C.", align: "right" },
            { key: "aplicado", label: "Bajado a facturas", align: "center" },
          ]}
          nota="Movimientos PAYMENT de TOTALES CHAMPTEX. Un depósito que se repartió entre varios meses aparece una vez por cada mes. Los 'PAYMENT FROM C1' son créditos de la comisión C1, no depósitos."
          placeholder="Tipo, mes, fecha…"
          archivo={`${EMPRESA.nombre} - Pagos Champtex`}
          onCerrar={() => setVerPagos(false)}
        />
      )}

      {/* Ledger mensual (TOTALES CHAMPTEX) */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="p-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Bloques mensuales (TOTALES CHAMPTEX)</p>
          <label className="flex items-center gap-1.5 text-xs text-slate-600">
            <input type="checkbox" checked={soloAbiertos} onChange={(e) => setSoloAbiertos(e.target.checked)} data-testid="champtex-solo-abiertos" />
            Solo meses con saldo abierto
          </label>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-[900px] w-full border-collapse text-xs">
            <thead>
              <tr className="bg-slate-50 text-slate-500 uppercase tracking-wide text-[10px]">
                <th className="text-left px-2 py-2 border-b border-slate-200"></th>
                <th className="text-left px-2 py-2 border-b border-slate-200">Mes</th>
                <th className="text-right px-2 py-2 border-b border-slate-200">Saldo Inicial</th>
                <th className="text-right px-2 py-2 border-b border-slate-200">Abonos</th>
                <th className="text-right px-2 py-2 border-b border-slate-200">Saldo Final</th>
                <th className="text-right px-2 py-2 border-b border-slate-200">Interés</th>
              </tr>
            </thead>
            <tbody>
              {bloquesVista.map((b) => {
                const abonos = b.movimientos.reduce((s, m) => s + (m.abono || 0), 0);
                const expandido = bloqueExpandido === b.mes;
                return (
                  <Fragment key={b.mes}>
                    <tr
                      className={`border-b border-slate-100 cursor-pointer ${b.saldoFinal > 0.01 ? "hover:bg-slate-50" : "text-slate-400 hover:bg-slate-50"}`}
                      onClick={() => setBloqueExpandido(expandido ? null : b.mes)}
                      data-testid={`champtex-bloque-${b.mes}`}
                    >
                      <td className="px-2 py-1.5">{expandido ? "▾" : "▸"}</td>
                      <td className="px-2 py-1.5 font-semibold whitespace-nowrap">{formatMesCortoChamptex(b.mes)}</td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap">{fmt(b.saldoInicial)}</td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap">{fmt(abonos)}</td>
                      <td className="px-2 py-1.5 text-right font-semibold whitespace-nowrap">{fmt(b.saldoFinal)}</td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap">{b.interesTotal ? fmt(b.interesTotal) : "—"}</td>
                    </tr>
                    {expandido && (
                      <tr>
                        <td colSpan={6} className="px-2 py-2 bg-slate-50/60">
                          <table className="w-full text-[11px]">
                            <thead>
                              <tr className="text-slate-400 uppercase text-[9px]">
                                <th className="text-left px-2 py-1">Nota</th>
                                <th className="text-left px-2 py-1">Fecha</th>
                                <th className="text-right px-2 py-1">Cargo</th>
                                <th className="text-right px-2 py-1">Abono</th>
                                <th className="text-right px-2 py-1">Saldo</th>
                                <th className="text-right px-2 py-1">Meses atraso</th>
                                <th className="text-right px-2 py-1">Interés</th>
                                <th className="text-center px-2 py-1">Aplicado</th>
                              </tr>
                            </thead>
                            <tbody>
                              {b.movimientos.length === 0 && (
                                <tr><td colSpan={8} className="text-center text-slate-400 py-2">Sin movimientos este mes</td></tr>
                              )}
                              {b.movimientos.map((m, i) => (
                                <tr key={i} className="border-t border-slate-100">
                                  <td className="px-2 py-1">{m.tipo}</td>
                                  <td className="px-2 py-1 whitespace-nowrap">{m.fecha || "—"}</td>
                                  <td className="px-2 py-1 text-right">{m.cargo ? fmt(m.cargo) : "—"}</td>
                                  <td className="px-2 py-1 text-right">{m.abono ? fmt(m.abono) : "—"}</td>
                                  <td className="px-2 py-1 text-right font-medium">{fmt(m.saldo)}</td>
                                  <td className="px-2 py-1 text-right">{m.mesesAtraso ?? "—"}</td>
                                  <td className="px-2 py-1 text-right">{m.interes ? fmt(m.interes) : "—"}</td>
                                  <td className="px-2 py-1 text-center">
                                    {esPagoAplicableChamptex(m.tipo)
                                      ? m.aplicado
                                        ? "✅"
                                        : "⏳"
                                      : esPagoChamptex(m.tipo)
                                      ? "🔒"
                                      : "—"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {bloquesVista.length === 0 && (
                <tr><td colSpan={6} className="text-center text-slate-400 py-6">Sin bloques que mostrar</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Facturas (DESGLOCE) */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="p-3 border-b border-slate-100 flex flex-wrap items-center gap-2">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mr-auto">Facturas (DESGLOCE)</p>
          <input
            type="text"
            value={busquedaLineas}
            onChange={(e) => setBusquedaLineas(e.target.value)}
            placeholder="🔎 Buscar orden, item, importador…"
            data-testid="champtex-buscar"
            className="border border-slate-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-teal-500/30 min-w-[220px]"
          />
          <label className="flex items-center gap-1.5 text-xs text-slate-600">
            <input type="checkbox" checked={soloPendientesLineas} onChange={(e) => setSoloPendientesLineas(e.target.checked)} data-testid="champtex-solo-pendientes" />
            Solo con saldo pendiente
          </label>
          <span className="text-xs text-slate-400 whitespace-nowrap">{filasLineas.length} de {lineasCalc.length}</span>
        </div>
        <div className="overflow-x-auto max-h-[500px]">
          <table className="min-w-[900px] w-full border-collapse text-xs">
            <thead className="sticky top-0">
              <tr className="bg-slate-50">
                {COLUMNAS_CHAMPTEX_DG.map((c) => (
                  <th
                    key={c.key}
                    onClick={() => alternarOrdenLinea(c.key)}
                    data-testid={`champtex-col-${c.key}`}
                    className={`text-left font-semibold text-slate-500 uppercase tracking-wide text-[10px] px-2 py-2 border-b border-slate-200 whitespace-nowrap cursor-pointer select-none hover:text-teal-700 ${c.tipo === "numero" ? "text-right" : ""}`}
                  >
                    {c.label}
                    {ordenColLinea === c.key ? (ordenDirLinea === "asc" ? " ▲" : " ▼") : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filasLineas.map((l) => (
                <tr key={l._key} data-testid={`champtex-linea-${l._key}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-2 py-1.5 font-semibold whitespace-nowrap">{l.orderNo}</td>
                  <td className="px-2 py-1.5">{l.itemNo}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{l.importador || "—"}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{l.vence || "—"}</td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap">{fmt(l.totalReal)}</td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap">{l.pagado ? fmt(l.pagado) : "—"}</td>
                  <td className="px-2 py-1.5 text-right font-semibold whitespace-nowrap">{fmt(l.pendiente)}</td>
                </tr>
              ))}
              {filasLineas.length === 0 && (
                <tr><td colSpan={7} className="text-center text-slate-400 py-6">Sin facturas que mostrar</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal: importar / reconciliar */}
      {importOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-3" onClick={() => setImportOpen(false)} data-testid="champtex-importar-modal">
          <div className="bg-white rounded-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-teal-700 uppercase tracking-wider">Importar / reconciliar Champtex</p>
                <p className="text-sm text-slate-600">Sube el libro con las hojas "TOTALES CHAMPTEX" y "DESGLOCE"</p>
              </div>
              <button onClick={() => setImportOpen(false)} className="text-slate-400 hover:text-slate-600 text-2xl leading-none px-2">×</button>
            </div>
            <div className="p-4 space-y-3">
              <label className="cursor-pointer flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                <span className="text-2xl">📁</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800">{importLoading ? "Procesando…" : fileName || "Elegir archivo Excel"}</p>
                  <p className="text-xs text-slate-500">Reemplaza todo el historial guardado con el del archivo</p>
                </div>
                <input type="file" accept=".xlsx,.xlsm,.xls" onChange={handleFile} className="hidden" data-testid="champtex-importar-file" />
              </label>
              {importMsg && <p className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">{importMsg}</p>}
              {updatedAt && (
                <p className="text-[11px] text-slate-400">
                  Última actualización: {new Date(updatedAt).toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" })}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal: registrar pago */}
      {pagoOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-3" onClick={() => { setPagoOpen(false); setPagoSim(null); }} data-testid="champtex-pago-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white">
              <p className="text-xs font-semibold text-teal-700 uppercase tracking-wider">Registrar pago a Champtex</p>
              <button onClick={() => { setPagoOpen(false); setPagoSim(null); }} className="text-slate-400 hover:text-slate-600 text-2xl leading-none px-2">×</button>
            </div>
            {!pagoSim ? (
              <div className="p-4 space-y-3">
                <label className="text-xs text-slate-500 block">Fecha del depósito
                  <input className={inputCxP} value={pagoFecha} onChange={(e) => setPagoFecha(e.target.value)} placeholder="dd/mm/aaaa" data-testid="champtex-pago-fecha" />
                </label>
                <label className="text-xs text-slate-500 block">Monto (USD)
                  <input type="number" className={inputCxP} value={pagoMonto} onChange={(e) => setPagoMonto(e.target.value)} data-testid="champtex-pago-monto" />
                </label>
                <label className="text-xs text-slate-500 block">Tipo de cambio (opcional)
                  <input type="number" className={inputCxP} value={pagoTC} onChange={(e) => setPagoTC(e.target.value)} />
                </label>
                <label className="text-xs text-slate-500 block">Aplicar solo a partir del mes… (opcional)
                  <select className={inputCxP} value={pagoMesObjetivo} onChange={(e) => setPagoMesObjetivo(e.target.value)}>
                    <option value="">El más antiguo con saldo (normal)</option>
                    {mesesAbiertos.map((b) => (
                      <option key={b.mes} value={b.mes}>{formatMesCortoChamptex(b.mes)}</option>
                    ))}
                  </select>
                </label>
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={() => { setPagoOpen(false); setPagoSim(null); }} className="text-xs font-medium text-slate-500 px-3 py-2">Cancelar</button>
                  <button type="button" onClick={simularPago} data-testid="champtex-pago-simular" className="text-xs font-semibold px-4 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700">
                    Ver plan de aplicación
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-4 space-y-3">
                <p className="text-sm text-slate-700">Simulación — todavía no se ha guardado nada.</p>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs space-y-1" data-testid="champtex-pago-plan">
                  {pagoSim.partes.map((p, i) => (
                    <p key={i}>• {formatMesCortoChamptex(p.mes)}: USD {fmt(p.monto)}</p>
                  ))}
                  {pagoSim.partes.length === 0 && <p className="text-slate-400">No se pudo aplicar a ningún mes.</p>}
                </div>
                <p className="text-sm font-semibold">Total a aplicar: USD {fmt(pagoSim.totalAplicado)}</p>
                {pagoSim.restante > 0.01 && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    Sobran USD {fmt(pagoSim.restante)} sin dónde aplicar.
                  </p>
                )}
                {pagoSim.avisos.map((a, i) => (
                  <p key={i} className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">{a}</p>
                ))}
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={() => setPagoSim(null)} className="text-xs font-medium text-slate-500 px-3 py-2">← Editar</button>
                  <button
                    type="button"
                    onClick={confirmarPago}
                    disabled={guardando}
                    data-testid="champtex-pago-confirmar"
                    className="text-xs font-semibold px-4 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-60"
                  >
                    {guardando ? "Guardando…" : "Confirmar y guardar"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal: aplicar pagos a facturas */}
      {aplicarSim && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-3" onClick={() => setAplicarSim(null)} data-testid="champtex-aplicar-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white">
              <p className="text-xs font-semibold text-teal-700 uppercase tracking-wider">Aplicar pagos a facturas</p>
              <button onClick={() => setAplicarSim(null)} className="text-slate-400 hover:text-slate-600 text-2xl leading-none px-2">×</button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-sm text-slate-700">Simulación — todavía no se ha guardado nada.</p>
              <p className="text-sm">{aplicarSim.asignaciones.length} facturas recibirán un pago · Total: USD {fmt(aplicarSim.totalPlan)}</p>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs space-y-1 max-h-56 overflow-y-auto" data-testid="champtex-aplicar-plan">
                {aplicarSim.asignaciones.slice(0, 50).map((a, i) => (
                  <p key={i}>• {a.orderNo} ({a.itemNo}): USD {fmt(a.monto)}</p>
                ))}
                {aplicarSim.asignaciones.length > 50 && <p className="text-slate-400">…y {aplicarSim.asignaciones.length - 50} más.</p>}
                {aplicarSim.asignaciones.length === 0 && <p className="text-slate-400">Nada por aplicar.</p>}
              </div>
              {aplicarSim.avisos.map((a, i) => (
                <p key={i} className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">{a}</p>
              ))}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setAplicarSim(null)} className="text-xs font-medium text-slate-500 px-3 py-2">Cancelar</button>
                <button
                  type="button"
                  onClick={confirmarAplicar}
                  disabled={guardando || aplicarSim.asignaciones.length === 0}
                  data-testid="champtex-aplicar-confirmar"
                  className="text-xs font-semibold px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60"
                >
                  {guardando ? "Guardando…" : "Confirmar y guardar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── CAPTURA DE ORDEN DE COMPRA (formulario tipo Excel, con desglose por color) ──
// Un "artículo" es un bloque de tela (como cada ART_n del Excel): datos generales
// de la tela/proveedor + una lista de "colores" (cada uno con su propia cantidad,
// precio e incoterm — igual que las filas #1, #2, #3... bajo cada artículo).
const colorVacio = () => ({
  diseno: "",
  color: "",
  cantidad: "",
  unidad: "metros",
  precio: "",
  incoterm: "FOB",
});

const articuloVacio = () => ({
  cliente: "",
  proveedor: "",
  tela: "",
  ref: "",
  composicion: "",
  peso: "",
  ancho: "",
  unidad: "metros",
  precioUnitario: "",
  capContenedor: "",
  colores: [colorVacio()],
});

const CAMPOS_COLOR = [
  { key: "diseno", label: "Diseño / DSN", type: "text", width: "w-24" },
  { key: "color", label: "Color", type: "text", width: "w-28" },
  { key: "cantidad", label: "Cantidad", type: "number", width: "w-24" },
  { key: "unidad", label: "Unidad", type: "select", width: "w-24" },
  { key: "precio", label: "Precio USD", type: "number", width: "w-24" },
  { key: "incoterm", label: "Incoterm", type: "text", width: "w-20" },
];

// NuevaOC recibe opcionalmente `ordenExistente` (un grupo del Resumen) para editar
// una orden ya guardada — se usa junto con key={} en App para forzar recarga del formulario.
function NuevaOC({ ordenExistente, quotes, onCrearCotizaciones, onVolverPedido }) {
  // Si viene de un pedido de programación: { id, folio, proveedor, cliente }.
  const pedidoOrigen = ordenExistente?._pedido || null;
  const [ligadaAPedido, setLigadaAPedido] = useState(false);
  const [noOrden, setNoOrden] = useState(ordenExistente?.noOrden || "");
  const [fecha, setFecha] = useState(ordenExistente?.fecha || hoy());
  const [shippingDate, setShippingDate] = useState(ordenExistente?.shippingDate || "");
  const [contenedorCbm, setContenedorCbm] = useState(ordenExistente?.contenedorCbm || "");
  const [manejadoPor, setManejadoPor] = useState(ordenExistente?.manejadoPor || "");
  const [incoterm, setIncoterm] = useState(ordenExistente?.incoterm || "FOB");
  const [anticipo, setAnticipo] = useState(ordenExistente?.anticipo || "");
  const [puerto, setPuerto] = useState(ordenExistente?.puerto || "MEXICO CITY");
  const [consignee, setConsignee] = useState(ordenExistente?.consignee || "");
  const [notify, setNotify] = useState(ordenExistente?.notify || "");
  const [pago, setPago] = useState(ordenExistente?.pago || "");
  const [articulosForm, setArticulosForm] = useState(
    ordenExistente?.items?.length
      ? ordenExistente.items.map((it) => ({
          ...articuloVacio(),
          ...it,
          precioUnitario: it.precioUnitario ?? it.fobUSD ?? "",
          colores: coloresDe(it).map((c) => ({ ...colorVacio(), ...c })),
        }))
      : [articuloVacio()]
  );
  const [saving, setSaving] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [generandoExcel, setGenerandoExcel] = useState(false);
  const [msg, setMsg] = useState("");

  // ── Generador automático de No. de Orden (replica la macro "GenerarNuevaOC") ──
  const [proveedores, setProveedores] = useState([]);
  const [proveedorGen, setProveedorGen] = useState(pedidoOrigen?.proveedor || "");
  const [necesitaCodigo, setNecesitaCodigo] = useState(false);
  const [codigoNuevo, setCodigoNuevo] = useState("");
  const [generandoNum, setGenerandoNum] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const lista = await api.get("/proveedores");
        if (Array.isArray(lista)) setProveedores(lista);
      } catch (e) {
        // silencioso
      }
    })();
  }, []);

  const pedirNumero = async (codigo_nuevo) => {
    setGenerandoNum(true);
    setMsg("");
    try {
      const r = await api.post("/oc-workbook/siguiente-numero", {
        proveedor: proveedorGen,
        codigo_nuevo: codigo_nuevo || undefined,
      });
      if (r.necesita_codigo) {
        setNecesitaCodigo(true);
      } else {
        setNoOrden(r.numeroOrden);
        setNecesitaCodigo(false);
        setCodigoNuevo("");
        setArticulosForm((prev) =>
          prev.map((a, i) => (i === 0 && !a.proveedor ? { ...a, proveedor: proveedorGen } : a))
        );
        setMsg(`🔢 Número generado: ${r.numeroOrden}`);
        try {
          const lista = await api.get("/proveedores");
          if (Array.isArray(lista)) setProveedores(lista);
        } catch (e) {
          // silencioso
        }
      }
    } catch (e) {
      setMsg("❌ No se pudo generar el número: " + e.message);
    }
    setGenerandoNum(false);
  };

  // ── Artículos (bloques de tela) ──
  const setArticuloField = (aIdx, field, value) =>
    setArticulosForm((prev) => prev.map((a, i) => (i === aIdx ? { ...a, [field]: value } : a)));
  const addArticulo = () => setArticulosForm((prev) => [...prev, articuloVacio()]);
  const duplicateArticulo = (aIdx) =>
    setArticulosForm((prev) => {
      const copy = [...prev];
      copy.splice(aIdx + 1, 0, { ...prev[aIdx], colores: prev[aIdx].colores.map((c) => ({ ...c })) });
      return copy;
    });
  const removeArticulo = (aIdx) =>
    setArticulosForm((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== aIdx) : prev));

  // ── Colores dentro de un artículo ──
  const setColorField = (aIdx, cIdx, field, value) =>
    setArticulosForm((prev) =>
      prev.map((a, i) =>
        i !== aIdx ? a : { ...a, colores: a.colores.map((c, j) => (j === cIdx ? { ...c, [field]: value } : c)) }
      )
    );
  const addColor = (aIdx) =>
    setArticulosForm((prev) =>
      prev.map((a, i) => (i === aIdx ? { ...a, colores: [...a.colores, colorVacio()] } : a))
    );
  const duplicateColor = (aIdx, cIdx) =>
    setArticulosForm((prev) =>
      prev.map((a, i) => {
        if (i !== aIdx) return a;
        const copy = [...a.colores];
        copy.splice(cIdx + 1, 0, { ...copy[cIdx] });
        return { ...a, colores: copy };
      })
    );
  const removeColor = (aIdx, cIdx) =>
    setArticulosForm((prev) =>
      prev.map((a, i) =>
        i !== aIdx ? a : { ...a, colores: a.colores.length > 1 ? a.colores.filter((_, j) => j !== cIdx) : a.colores }
      )
    );

  const limpiar = () => {
    setNoOrden("");
    setFecha(hoy());
    setShippingDate("");
    setContenedorCbm("");
    setManejadoPor("");
    setIncoterm("FOB");
    setAnticipo("");
    setPuerto("MEXICO CITY");
    setConsignee("");
    setNotify("");
    setPago("");
    setArticulosForm([articuloVacio()]);
    setProveedorGen("");
    setNecesitaCodigo(false);
    setCodigoNuevo("");
    setMsg("");
  };

  const colorValido = (c) => +c.cantidad > 0 && +c.precio > 0;
  const articulosValidos = () =>
    articulosForm
      .map((a) => ({ ...a, colores: (a.colores || []).filter(colorValido) }))
      .filter((a) => a.tela && a.proveedor && a.colores.length > 0);

  const datosOrden = () => ({
    noOrden: noOrden || "",
    fecha,
    shippingDate,
    contenedorCbm: +contenedorCbm || 0,
    manejadoPor: manejadoPor || "",
    incoterm: incoterm || "",
    anticipo: +anticipo || 0,
    puerto: puerto || "",
    consignee: consignee || "",
    notify: notify || "",
    pago: pago || "",
  });

  const construirArticulos = () => {
    const cab = datosOrden();
    return articulosValidos().map((a) => {
      const colores = a.colores.map((c, idx) => ({
        numero: idx + 1,
        diseno: c.diseno || "",
        color: c.color || "",
        cantidad: +c.cantidad || 0,
        unidad: c.unidad || a.unidad || "metros",
        precio: +c.precio || 0,
        incoterm: c.incoterm || incoterm || "FOB",
      }));
      const cantidadTotal = colores.reduce((s, c) => s + c.cantidad, 0);
      const totalArt = colores.reduce((s, c) => s + c.cantidad * c.precio, 0);
      const fobPromedio = cantidadTotal > 0 ? totalArt / cantidadTotal : +a.precioUnitario || 0;
      return {
        _key: `${noOrden || "SIN-ORDEN"}::${a.tela}::${a.proveedor}`,
        hoja: noOrden || "",
        ...cab,
        cliente: a.cliente || "",
        proveedor: a.proveedor,
        tela: a.tela,
        ref: a.ref || "",
        composicion: a.composicion || "",
        peso: +a.peso || 0,
        ancho: +a.ancho || 0,
        unidad: a.unidad || "metros",
        precioUnitario: +a.precioUnitario || fobPromedio,
        capContenedor: +a.capContenedor || 0,
        colores,
        cantidad: cantidadTotal,
        fobUSD: fobPromedio,
      };
    });
  };

  const guardar = async () => {
    if (!noOrden.trim()) {
      setMsg("⚠️ Ponle un No. de Orden antes de guardar (o genera uno automático).");
      return;
    }
    const nuevos = construirArticulos();
    if (nuevos.length === 0) {
      setMsg("⚠️ Cada artículo necesita Proveedor, Tela y al menos un color con cantidad y precio.");
      return;
    }
    setSaving(true);
    setMsg("");
    try {
      const actual = await api.get("/oc-workbook").catch(() => null);
      const existentes = Array.isArray(actual?.articulos) ? actual.articulos : [];
      // Reemplaza TODOS los artículos guardados de esta orden (por hoja/noOrden),
      // no solo los que coincidan por _key — así, si quitaste un artículo o le
      // cambiaste tela/proveedor al editar, no se queda un "fantasma" del
      // artículo viejo. Si además renombraste el No. de Orden, también se
      // limpian los artículos que quedaron con el número anterior.
      const hojaActual = (noOrden || "").trim();
      const hojaAnterior = (ordenExistente?.noOrden || "").trim();
      const conservados = existentes.filter((a) => {
        const h = (a.hoja || a.noOrden || "").trim();
        return h !== hojaActual && !(hojaAnterior && h === hojaAnterior);
      });
      // Si la orden estaba cancelada desde la app, sigue cancelada al editarla.
      const estabaCancelada = existentes.some((a) => {
        const h = (a.hoja || a.noOrden || "").trim();
        return a.cancelado === true && (h === hojaActual || (hojaAnterior && h === hojaAnterior));
      });
      const merged = [...conservados, ...(estabaCancelada ? nuevos.map((a) => ({ ...a, cancelado: true })) : nuevos)];
      await api.post("/oc-workbook", {
        articulos: merged,
        file_name: actual?.file_name || "Órdenes capturadas en la app",
      });
      let extra = "";
      if (onCrearCotizaciones) {
        const { nuevos: paraCotizar, omitidos } = filtrarArticulosParaCotizar(nuevos, quotes);
        if (paraCotizar.length > 0) {
          const quiere = window.confirm(
            `Orden ${noOrden} guardada.\n\n¿Generar también un borrador de cotización en el cotizador (Simple/Detallado) para ${paraCotizar.length} artículo(s) de esta orden?\n\nSe crean con supuestos por default (TC 17.50, impuestos $280,000, flete $100,000, utilidad 25%) — los puedes ajustar después.`
          );
          if (quiere) {
            const result = await onCrearCotizaciones(paraCotizar);
            extra = ` · 🧾 ${result?.added ?? paraCotizar.length} cotización(es) creada(s)`;
          }
        } else if (omitidos > 0) {
          extra = " · (ya tenían cotización)";
        }
      }
      if (pedidoOrigen) {
        try {
          await api.post(
            `/pedidos/${pedidoOrigen.id}/oc`,
            { proveedor: pedidoOrigen.proveedor, noOrden: noOrden.trim() },
            pedidoOrigen.slToken ? { "X-SL-Token": pedidoOrigen.slToken } : {}
          );
          extra += ` · 🔗 ligada al pedido ${pedidoOrigen.folio}`;
          setLigadaAPedido(true);
        } catch (e) {
          extra += ` · ⚠️ no se pudo ligar al pedido ${pedidoOrigen.folio}: ${e.message}`;
        }
      }
      setMsg(`✅ Orden ${noOrden} guardada · ${nuevos.length} artículo(s)${extra}`);
    } catch (e) {
      setMsg("❌ No se pudo guardar: " + e.message);
    }
    setSaving(false);
  };

  const generarPDF = async () => {
    const nuevos = construirArticulos();
    if (nuevos.length === 0) {
      setMsg("⚠️ Cada artículo necesita Proveedor, Tela y al menos un color con cantidad y precio.");
      return;
    }
    setGenerando(true);
    try {
      await descargarPDFOrdenCompra(datosOrden(), nuevos);
    } catch (e) {
      setMsg("❌ No se pudo generar el PDF: " + e.message);
    }
    setGenerando(false);
  };

  const generarExcel = async () => {
    const nuevos = construirArticulos();
    if (nuevos.length === 0) {
      setMsg("⚠️ Cada artículo necesita Proveedor, Tela y al menos un color con cantidad y precio.");
      return;
    }
    setGenerandoExcel(true);
    try {
      await descargarExcelOrdenCompra(datosOrden(), nuevos);
    } catch (e) {
      setMsg("❌ No se pudo generar el Excel: " + e.message);
    }
    setGenerandoExcel(false);
  };

  const enviarWhatsApp = async () => {
    const nuevos = construirArticulos();
    if (nuevos.length === 0) {
      setMsg("⚠️ Cada artículo necesita Proveedor, Tela y al menos un color con cantidad y precio.");
      return;
    }
    await compartirPDFOrdenCompraWhatsApp(datosOrden(), nuevos);
  };

  const totalUSD = articulosValidos().reduce(
    (s, a) =>
      esArticuloCancelado(a)
        ? s
        : s + a.colores.reduce((ss, c) => ss + (+c.cantidad || 0) * (+c.precio || 0), 0),
    0
  );

  return (
    <div className="space-y-4 pb-8">
      {pedidoOrigen && (
        <div className="text-xs text-violet-800 bg-violet-50 border border-violet-200 rounded-lg px-3 py-2 flex items-center justify-between gap-2 flex-wrap" data-testid="nueva-oc-pedido">
          <span>
            🗓️ OC de programación del pedido <strong>{pedidoOrigen.folio}</strong> ({pedidoOrigen.cliente}) · proveedor{" "}
            <strong>{pedidoOrigen.proveedor}</strong>. Revisa, genera el número y guarda: la OC queda ligada al pedido.
          </span>
          {ligadaAPedido && onVolverPedido && (
            <button type="button" onClick={() => onVolverPedido(pedidoOrigen.id)} className="font-semibold px-3 py-1.5 rounded-lg bg-violet-600 text-white" data-testid="nueva-oc-volver-pedido">
              ← Volver al pedido
            </button>
          )}
        </div>
      )}
      {ordenExistente && !pedidoOrigen && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          ✏️ Editando la orden <strong>{ordenExistente.noOrden}</strong>. Al guardar se actualizan sus artículos
          (los que borres aquí no se eliminan del historial — solo deja de agregarles cambios).
        </p>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 p-4">
        <p className="text-xs font-semibold text-teal-700 uppercase tracking-wider mb-3">
          No. de Orden automático
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            list="nueva-oc-proveedores-conocidos"
            value={proveedorGen}
            onChange={(e) => setProveedorGen(e.target.value)}
            placeholder="Proveedor (ej. GISELLE) o vacío si aún no lo sabes"
            data-testid="nueva-oc-proveedor-gen"
            className="flex-1 min-w-0 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30"
          />
          <datalist id="nueva-oc-proveedores-conocidos">
            {proveedores.map((p) => (
              <option key={p.nombre} value={p.nombre} />
            ))}
          </datalist>
          <button
            type="button"
            onClick={() => pedirNumero()}
            disabled={generandoNum}
            data-testid="nueva-oc-generar-numero"
            className={`text-xs font-semibold px-3 py-2 rounded-lg whitespace-nowrap ${
              generandoNum ? "bg-slate-200 text-slate-500" : "bg-teal-600 text-white hover:bg-teal-700"
            }`}
          >
            {generandoNum ? "…" : "🔢 Generar"}
          </button>
        </div>
        {necesitaCodigo && (
          <div className="flex gap-2 mt-2">
            <input
              type="text"
              maxLength={3}
              value={codigoNuevo}
              onChange={(e) => setCodigoNuevo(e.target.value.toUpperCase())}
              placeholder="Código de 3 letras (ej. GIS)"
              data-testid="nueva-oc-codigo-nuevo"
              className="flex-1 min-w-0 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30"
            />
            <button
              type="button"
              onClick={() => pedirNumero(codigoNuevo)}
              disabled={generandoNum || codigoNuevo.trim().length !== 3}
              data-testid="nueva-oc-confirmar-codigo"
              className="text-xs font-semibold px-3 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 whitespace-nowrap disabled:opacity-40"
            >
              Confirmar
            </button>
          </div>
        )}
        <p className="text-[11px] text-slate-400 mt-2">
          "{proveedorGen || "—"}" nuevo o conocido: si es nuevo te pide un código de 3 letras (como GIS de
          GISELLE). El número sigue el mismo esquema que tu Excel: consecutivo + {EMPRESA.prefijoOC} + código + consecutivo del
          proveedor.
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-4">
        <p className="text-xs font-semibold text-teal-700 uppercase tracking-wider mb-3">Datos de la orden</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <div>
            <label className="text-xs text-slate-500">No. de Orden</label>
            <input
              type="text"
              value={noOrden}
              onChange={(e) => setNoOrden(e.target.value)}
              placeholder={`Ej. 001${EMPRESA.prefijoOC}GIS-01`}
              data-testid="nueva-oc-numero"
              className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500">Fecha</label>
            <input
              type="text"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              data-testid="nueva-oc-fecha"
              className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500">Manejado por</label>
            <input
              type="text"
              value={manejadoPor}
              onChange={(e) => setManejadoPor(e.target.value)}
              placeholder="Ej. CHAMPTEX, DIRECT"
              data-testid="nueva-oc-manejado"
              className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500">Incoterm</label>
            <input
              type="text"
              value={incoterm}
              onChange={(e) => setIncoterm(e.target.value)}
              placeholder="FOB"
              data-testid="nueva-oc-incoterm"
              className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500">Anticipo (%)</label>
            <input
              type="number"
              value={anticipo}
              onChange={(e) => setAnticipo(e.target.value)}
              placeholder="10"
              data-testid="nueva-oc-anticipo"
              className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500">Puerto</label>
            <input
              type="text"
              value={puerto}
              onChange={(e) => setPuerto(e.target.value)}
              data-testid="nueva-oc-puerto"
              className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500">Consignee</label>
            <input
              type="text"
              value={consignee}
              onChange={(e) => setConsignee(e.target.value)}
              placeholder="PENDING"
              data-testid="nueva-oc-consignee"
              className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500">Notify</label>
            <input
              type="text"
              value={notify}
              onChange={(e) => setNotify(e.target.value)}
              placeholder="PENDING"
              data-testid="nueva-oc-notify"
              className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500">Forma de pago</label>
            <input
              type="text"
              value={pago}
              onChange={(e) => setPago(e.target.value)}
              placeholder="Ej. 90 days after BL"
              data-testid="nueva-oc-pago"
              className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500">Fecha de embarque</label>
            <input
              type="text"
              value={shippingDate}
              onChange={(e) => setShippingDate(e.target.value)}
              placeholder="dd/mm/aaaa"
              data-testid="nueva-oc-shipping"
              className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500">Contenedor (CBM)</label>
            <input
              type="number"
              value={contenedorCbm}
              onChange={(e) => setContenedorCbm(e.target.value)}
              data-testid="nueva-oc-cbm"
              className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold text-teal-700 uppercase tracking-wider">
            Artículos ({articulosForm.length})
          </p>
          <button
            type="button"
            onClick={addArticulo}
            data-testid="nueva-oc-add-articulo"
            className="text-xs font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 px-3 py-1.5 rounded-lg"
          >
            + Agregar artículo
          </button>
        </div>

        <div className="space-y-4">
          {articulosForm.map((a, aIdx) => {
            const cancelado = esArticuloCancelado(a);
            const totalArt = a.colores.reduce((s, c) => s + (+c.cantidad || 0) * (+c.precio || 0), 0);
            const CAMPOS_ARTICULO_TEXTO = [
              { key: "cliente", label: "Cliente", placeholder: "Escribe CANCELADO para excluirlo" },
              { key: "proveedor", label: "Proveedor" },
              { key: "tela", label: "Tela" },
              { key: "ref", label: "Ref./No. ítem" },
              { key: "composicion", label: "Composición" },
            ];
            return (
              <div
                key={aIdx}
                data-testid={`nueva-oc-articulo-${aIdx}`}
                className={`rounded-xl border p-3 ${
                  cancelado ? "border-rose-200 bg-rose-50/50" : "border-slate-200 bg-slate-50/60"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <p className={`text-xs font-semibold ${cancelado ? "text-rose-700" : "text-slate-500"}`}>
                    Artículo {aIdx + 1}
                    {cancelado ? " · ⛔ CANCELADO" : ""}
                  </p>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => duplicateArticulo(aIdx)}
                      title="Duplicar artículo"
                      className="text-slate-400 hover:text-teal-600 px-1"
                    >
                      ⧉
                    </button>
                    <button
                      type="button"
                      onClick={() => removeArticulo(aIdx)}
                      title="Eliminar artículo"
                      className="text-slate-400 hover:text-rose-600 px-1"
                    >
                      🗑
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-3">
                  {CAMPOS_ARTICULO_TEXTO.map((c) => (
                    <div key={c.key}>
                      <label className="text-[10px] text-slate-500">{c.label}</label>
                      <input
                        type="text"
                        value={a[c.key]}
                        onChange={(e) => setArticuloField(aIdx, c.key, e.target.value)}
                        placeholder={c.placeholder || ""}
                        data-testid={`nueva-oc-${c.key}-${aIdx}`}
                        className="w-full mt-0.5 border border-slate-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-teal-500/40"
                      />
                    </div>
                  ))}
                  <div>
                    <label className="text-[10px] text-slate-500">Peso g/m²</label>
                    <input
                      type="number"
                      value={a.peso}
                      onChange={(e) => setArticuloField(aIdx, "peso", e.target.value)}
                      data-testid={`nueva-oc-peso-${aIdx}`}
                      className="w-full mt-0.5 border border-slate-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-teal-500/40"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-500">Ancho cm</label>
                    <input
                      type="number"
                      value={a.ancho}
                      onChange={(e) => setArticuloField(aIdx, "ancho", e.target.value)}
                      data-testid={`nueva-oc-ancho-${aIdx}`}
                      className="w-full mt-0.5 border border-slate-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-teal-500/40"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-500">Unidad</label>
                    <select
                      value={a.unidad}
                      onChange={(e) => setArticuloField(aIdx, "unidad", e.target.value)}
                      data-testid={`nueva-oc-unidad-${aIdx}`}
                      className="w-full mt-0.5 border border-slate-200 rounded px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-teal-500/40"
                    >
                      <option value="metros">metros</option>
                      <option value="kg">kg</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-500">Precio/Unid. USD</label>
                    <input
                      type="number"
                      value={a.precioUnitario}
                      onChange={(e) => setArticuloField(aIdx, "precioUnitario", e.target.value)}
                      data-testid={`nueva-oc-precio-unitario-${aIdx}`}
                      className="w-full mt-0.5 border border-slate-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-teal-500/40"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-500">Cap. Contenedor</label>
                    <input
                      type="number"
                      value={a.capContenedor}
                      onChange={(e) => setArticuloField(aIdx, "capContenedor", e.target.value)}
                      data-testid={`nueva-oc-cap-contenedor-${aIdx}`}
                      className="w-full mt-0.5 border border-slate-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-teal-500/40"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">
                    Colores ({a.colores.length})
                  </p>
                  <button
                    type="button"
                    onClick={() => addColor(aIdx)}
                    data-testid={`nueva-oc-add-color-${aIdx}`}
                    className="text-[11px] font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 px-2 py-1 rounded"
                  >
                    + Agregar color
                  </button>
                </div>

                <div className="overflow-x-auto -mx-3 px-3">
                  <table className="min-w-[640px] w-full border-collapse text-xs">
                    <thead>
                      <tr>
                        {CAMPOS_COLOR.map((c) => (
                          <th
                            key={c.key}
                            className="text-left font-semibold text-slate-400 uppercase tracking-wide text-[9px] px-1 py-1 border-b border-slate-200 whitespace-nowrap"
                          >
                            {c.label}
                          </th>
                        ))}
                        <th className="border-b border-slate-200"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {a.colores.map((c, cIdx) => (
                        <tr key={cIdx} className="border-b border-slate-100">
                          {CAMPOS_COLOR.map((campo) => (
                            <td key={campo.key} className="px-1 py-1 align-top">
                              {campo.type === "select" ? (
                                <select
                                  value={c[campo.key]}
                                  onChange={(e) => setColorField(aIdx, cIdx, campo.key, e.target.value)}
                                  data-testid={`nueva-oc-color-${campo.key}-${aIdx}-${cIdx}`}
                                  className={`${campo.width} border border-slate-200 rounded px-1 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-teal-500/40`}
                                >
                                  <option value="metros">metros</option>
                                  <option value="kg">kg</option>
                                </select>
                              ) : (
                                <input
                                  type={campo.type}
                                  value={c[campo.key]}
                                  onChange={(e) => setColorField(aIdx, cIdx, campo.key, e.target.value)}
                                  data-testid={`nueva-oc-color-${campo.key}-${aIdx}-${cIdx}`}
                                  className={`${campo.width} border border-slate-200 rounded px-1.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-teal-500/40`}
                                />
                              )}
                            </td>
                          ))}
                          <td className="px-1 py-1 whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() => duplicateColor(aIdx, cIdx)}
                              title="Duplicar color"
                              className="text-slate-400 hover:text-teal-600 px-1"
                            >
                              ⧉
                            </button>
                            <button
                              type="button"
                              onClick={() => removeColor(aIdx, cIdx)}
                              title="Eliminar color"
                              className="text-slate-400 hover:text-rose-600 px-1"
                            >
                              🗑
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {totalArt > 0 && (
                  <p className={`text-right text-xs font-semibold mt-2 ${cancelado ? "text-rose-600" : "text-slate-600"}`}>
                    Subtotal artículo: USD{" "}
                    {totalArt.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-[11px] text-slate-400 mt-3">
          Tip: escribe <strong>CANCELADO</strong> en el campo Cliente de un artículo para excluir todos sus colores
          del total y del PDF sin borrarlo.
        </p>

        {totalUSD > 0 && (
          <p className="text-right text-sm font-semibold text-emerald-700 mt-3">
            Total de la orden: USD{" "}
            {totalUSD.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        )}
      </div>

      {msg && (
        <p
          data-testid="nueva-oc-msg"
          className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2"
        >
          {msg}
        </p>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 max-w-3xl mx-auto w-full">
        <button
          type="button"
          onClick={guardar}
          disabled={saving}
          data-testid="nueva-oc-guardar"
          className={`py-2.5 rounded-xl text-sm font-semibold transition-colors ${
            saving ? "bg-slate-200 text-slate-500" : "bg-teal-600 text-white hover:bg-teal-700"
          }`}
        >
          {saving ? "Guardando…" : "💾 Guardar orden"}
        </button>
        <button
          type="button"
          onClick={generarPDF}
          disabled={generando}
          data-testid="nueva-oc-pdf"
          className={`py-2.5 rounded-xl text-sm font-semibold transition-colors ${
            generando ? "bg-slate-200 text-slate-500" : "bg-slate-900 text-white hover:bg-slate-800"
          }`}
        >
          {generando ? "Generando…" : "⬇ Descargar PDF"}
        </button>
        <button
          type="button"
          onClick={generarExcel}
          disabled={generandoExcel}
          data-testid="nueva-oc-excel"
          className={`py-2.5 rounded-xl text-sm font-semibold transition-colors ${
            generandoExcel ? "bg-slate-200 text-slate-500" : "bg-emerald-700 text-white hover:bg-emerald-800"
          }`}
        >
          {generandoExcel ? "Generando…" : "📊 Descargar Excel"}
        </button>
        <button
          type="button"
          onClick={enviarWhatsApp}
          data-testid="nueva-oc-whatsapp"
          className="py-2.5 rounded-xl text-sm font-semibold border border-emerald-300 text-emerald-700 hover:bg-emerald-50 transition-colors"
        >
          📲 Enviar por WhatsApp
        </button>
        <button
          type="button"
          onClick={limpiar}
          data-testid="nueva-oc-limpiar"
          className="py-2.5 rounded-xl text-sm font-semibold border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
        >
          Limpiar formulario
        </button>
      </div>
    </div>
  );
}

// ─── FRACCIONES ARANCELARIAS (referencia textil MX) ──────────────────────────
// Aranceles general MFN (NMF) — pueden reducirse por TLC. CuotaComp por defecto 0.
const FRACCIONES = [
  // Algodón (Cap. 52)
  { code: "5208.11.01", desc: "Tejido de algodón crudo, tafetán, ≥85% algodón, ≤100 g/m²", arancel: 20, cuotaComp: 0 },
  { code: "5208.21.01", desc: "Tejido de algodón blanqueado, tafetán, ≥85% algodón, ≤100 g/m²", arancel: 20, cuotaComp: 0 },
  { code: "5208.31.01", desc: "Tejido de algodón teñido, tafetán, ≥85% algodón, ≤100 g/m²", arancel: 20, cuotaComp: 0 },
  { code: "5208.32.01", desc: "Tejido de algodón teñido, tafetán, ≥85% algodón, >100 g/m² ≤200 g/m²", arancel: 20, cuotaComp: 0 },
  { code: "5208.41.01", desc: "Tejido de algodón hilos distintos colores, tafetán, ≥85% algodón, ≤100 g/m²", arancel: 20, cuotaComp: 0 },
  { code: "5208.51.01", desc: "Tejido de algodón estampado, tafetán, ≥85% algodón, ≤100 g/m²", arancel: 20, cuotaComp: 0 },
  { code: "5209.11.01", desc: "Tejido de algodón crudo, tafetán, ≥85% algodón, >200 g/m²", arancel: 20, cuotaComp: 0 },
  { code: "5209.31.01", desc: "Tejido de algodón teñido, tafetán, ≥85% algodón, >200 g/m²", arancel: 20, cuotaComp: 0 },
  { code: "5209.41.01", desc: "Tejido de algodón hilos distintos colores, tafetán, ≥85% algodón, >200 g/m²", arancel: 20, cuotaComp: 0 },
  { code: "5209.51.01", desc: "Tejido de algodón estampado, tafetán, ≥85% algodón, >200 g/m²", arancel: 20, cuotaComp: 0 },
  { code: "5210.11.01", desc: "Tejido de algodón <85% mezclado con sintéticas, crudo, ≤200 g/m²", arancel: 20, cuotaComp: 0 },
  { code: "5211.11.01", desc: "Tejido de algodón <85% mezclado con sintéticas, crudo, >200 g/m²", arancel: 20, cuotaComp: 0 },
  { code: "5212.11.01", desc: "Otros tejidos de algodón, crudos, ≤200 g/m²", arancel: 20, cuotaComp: 0 },
  // Seda (Cap. 50)
  { code: "5007.10.01", desc: "Tejidos de borrilla de seda", arancel: 5, cuotaComp: 0 },
  { code: "5007.20.01", desc: "Tejidos con ≥85% de seda o desperdicios de seda", arancel: 5, cuotaComp: 0 },
  { code: "5007.90.99", desc: "Los demás tejidos de seda", arancel: 5, cuotaComp: 0 },
  // Lana (Cap. 51)
  { code: "5111.11.01", desc: "Tejido de lana cardada, ≥85% lana, ≤300 g/m²", arancel: 10, cuotaComp: 0 },
  { code: "5112.11.01", desc: "Tejido de lana peinada, ≥85% lana, ≤200 g/m²", arancel: 10, cuotaComp: 0 },
  // Filamento sintético/artificial (Cap. 54)
  { code: "5407.10.01", desc: "Tejidos de hilados de alta tenacidad (nylon/poliéster)", arancel: 20, cuotaComp: 0 },
  { code: "5407.42.01", desc: "Tejido de filamento de nylon teñido", arancel: 20, cuotaComp: 0 },
  { code: "5407.52.01", desc: "Tejido de filamento de poliéster texturado teñido", arancel: 20, cuotaComp: 0 },
  { code: "5407.61.01", desc: "Tejido con ≥85% poliéster sin texturar", arancel: 20, cuotaComp: 0 },
  { code: "5407.72.01", desc: "Otros tejidos de filamento sintético teñidos", arancel: 20, cuotaComp: 0 },
  { code: "5408.22.01", desc: "Tejido de filamento artificial teñido (rayón viscosa)", arancel: 20, cuotaComp: 0 },
  // Fibras sintéticas discontinuas (Cap. 55)
  { code: "5512.19.01", desc: "Tejido de fibras de poliéster ≥85%, teñido/blanqueado", arancel: 20, cuotaComp: 0 },
  { code: "5513.21.01", desc: "Tejido poliéster <85% mezclado con algodón, teñido, ≤170 g/m²", arancel: 20, cuotaComp: 0 },
  { code: "5514.21.01", desc: "Tejido poliéster <85% mezclado con algodón, teñido, >170 g/m²", arancel: 20, cuotaComp: 0 },
  { code: "5515.11.01", desc: "Tejido de poliéster mezclado con rayón viscosa", arancel: 20, cuotaComp: 0 },
  { code: "5516.22.01", desc: "Tejido de fibras artificiales teñido (rayón)", arancel: 20, cuotaComp: 0 },
  // Tejidos especiales (Cap. 58)
  { code: "5801.27.01", desc: "Terciopelo y pana de algodón", arancel: 20, cuotaComp: 0 },
  { code: "5802.11.01", desc: "Tejido toalla, esponja (terry), de algodón, crudo", arancel: 20, cuotaComp: 0 },
  { code: "5806.32.01", desc: "Cintas estrechas de fibras sintéticas", arancel: 15, cuotaComp: 0 },
  // Tejidos recubiertos (Cap. 59)
  { code: "5903.10.01", desc: "Tejido recubierto con PVC", arancel: 15, cuotaComp: 0 },
  { code: "5903.20.01", desc: "Tejido recubierto con poliuretano", arancel: 15, cuotaComp: 0 },
  // Tejidos de punto (Cap. 60)
  { code: "6001.10.01", desc: "Tejido de punto de pelo largo", arancel: 25, cuotaComp: 0 },
  { code: "6004.10.01", desc: "Tejido de punto con elastómero ≥5%", arancel: 25, cuotaComp: 0 },
  { code: "6006.22.01", desc: "Tejido de punto de algodón teñido", arancel: 25, cuotaComp: 0 },
  { code: "6006.32.01", desc: "Tejido de punto de filamento sintético teñido", arancel: 25, cuotaComp: 0 },
];

// ─── COMPONENTES BASE ─────────────────────────────────────────────────────────
const Card = ({ children, className = "" }) => (
  <div
    className={`bg-white rounded-2xl shadow-sm border border-slate-100 p-4 ${className}`}
  >
    {children}
  </div>
);

const SecTitle = ({ children }) => (
  <p className="text-xs font-semibold text-teal-700 uppercase tracking-wider mb-3">
    {children}
  </p>
);

const Field = ({ label, children, span2 = false }) => (
  <div className={`flex flex-col gap-1 ${span2 ? "col-span-2" : ""}`}>
    <label className="text-xs text-slate-500 font-medium">{label}</label>
    {children}
  </div>
);

const TxtInput = ({ value, onChange, placeholder = "", testId }) => (
  <input
    type="text"
    data-testid={testId}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 bg-white transition-colors"
  />
);

const NumInput = ({ value, onChange, prefix, suffix, testId }) => (
  <div className="flex items-center border border-slate-200 rounded-lg focus-within:ring-2 focus-within:ring-teal-500/30 focus-within:border-teal-500 bg-white overflow-hidden transition-colors">
    {prefix && (
      <span className="px-2 text-xs text-slate-400 bg-slate-50 border-r border-slate-200 py-2 whitespace-nowrap">
        {prefix}
      </span>
    )}
    <input
      type="number"
      step="any"
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="flex-1 px-3 py-2 text-sm text-slate-800 focus:outline-none bg-transparent min-w-0"
    />
    {suffix && (
      <span className="px-2 text-xs text-slate-400 bg-slate-50 border-l border-slate-200 py-2 whitespace-nowrap">
        {suffix}
      </span>
    )}
  </div>
);

const SelInput = ({ value, onChange, options, testId }) => (
  <select
    data-testid={testId}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 bg-white transition-colors"
  >
    {options.map((o) => (
      <option key={o.value} value={o.value}>
        {o.label}
      </option>
    ))}
  </select>
);

const Toggle = ({ value, onChange, label, testId }) => (
  <div className="flex items-center gap-3">
    <button
      type="button"
      data-testid={testId}
      onClick={() => onChange(!value)}
      className={`w-11 h-6 rounded-full transition-colors relative flex-shrink-0 ${
        value ? "bg-teal-500" : "bg-slate-300"
      }`}
    >
      <span
        className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${
          value ? "left-5" : "left-0.5"
        }`}
      />
    </button>
    <span className="text-sm text-slate-600 font-medium">{label}</span>
  </div>
);

const Badge = ({ label, value, accent, green }) => (
  <div
    className={`rounded-xl p-3 border ${
      green
        ? "bg-emerald-50 border-emerald-200"
        : accent
          ? "bg-teal-50 border-teal-200"
          : "bg-slate-50 border-slate-200"
    }`}
  >
    <p
      className={`text-xs font-medium mb-0.5 ${
        green
          ? "text-emerald-600"
          : accent
            ? "text-teal-600"
            : "text-slate-500"
      }`}
    >
      {label}
    </p>
    <p
      className={`text-base font-bold ${
        green
          ? "text-emerald-700"
          : accent
            ? "text-teal-700"
            : "text-slate-700"
      }`}
    >
      {value}
    </p>
  </div>
);

const SaveBtn = ({ onSave, disabled, saved, isEditing }) => (
  <button
    onClick={onSave}
    disabled={disabled}
    data-testid="save-quote-btn"
    className={`w-full py-3 rounded-xl font-semibold text-sm transition-all ${
      saved
        ? "bg-emerald-500 text-white"
        : disabled
          ? "bg-slate-200 text-slate-400 cursor-not-allowed"
          : "bg-teal-600 hover:bg-teal-500 text-white shadow-sm"
    }`}
  >
    {saved
      ? isEditing
        ? "✓ Cotización actualizada"
        : "✓ Cotización guardada"
      : isEditing
        ? "Actualizar cotización"
        : "Guardar cotización"}
  </button>
);

const CancelEditBtn = ({ onCancel }) => (
  <button
    onClick={onCancel}
    data-testid="cancel-edit-btn"
    className="w-full py-2.5 rounded-xl font-medium text-sm text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 transition-colors"
  >
    Cancelar edición
  </button>
);

const OpBanner = ({ name, mode }) => {
  const isEdit = mode === "edit";
  const cls = isEdit
    ? { bg: "bg-amber-50", bd: "border-amber-200", ic: "text-amber-600", lb: "text-amber-700", tx: "text-amber-900" }
    : { bg: "bg-sky-50", bd: "border-sky-200", ic: "text-sky-600", lb: "text-sky-700", tx: "text-sky-900" };
  return (
    <div className={`${cls.bg} ${cls.bd} border rounded-xl p-3 flex items-center gap-2`}>
      <span className={`${cls.ic} text-lg`}>{isEdit ? "✏️" : "📋"}</span>
      <div className="flex-1 min-w-0">
        <p className={`text-xs font-semibold ${cls.lb} uppercase tracking-wider`}>
          {isEdit ? "Editando cotización" : "Duplicando cotización"}
        </p>
        <p className={`text-sm ${cls.tx} font-medium truncate`}>{name}</p>
      </div>
    </div>
  );
};

const FraccionPicker = ({ value, onChange, onSelect }) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const selected = FRACCIONES.find((f) => f.code === value);
  const norm = (s) => (s || "").toString().toLowerCase().replace(/[\s.]/g, "");
  const q = norm(query);
  const filtered =
    q.length >= 1
      ? FRACCIONES.filter(
          (f) =>
            norm(f.code).includes(q) ||
            f.desc.toLowerCase().includes(query.toLowerCase())
        ).slice(0, 15)
      : FRACCIONES.slice(0, 15);

  if (selected) {
    return (
      <div
        data-testid="fraccion-selected"
        className="flex items-start border border-teal-200 bg-teal-50 rounded-lg px-3 py-2 gap-2"
      >
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-teal-800">{selected.code}</p>
          <p className="text-xs text-teal-700">{selected.desc}</p>
          <p className="text-xs text-teal-600 font-medium mt-1">
            Arancel {selected.arancel}% · Cuota comp. {selected.cuotaComp}%
          </p>
        </div>
        <button
          type="button"
          onClick={() => onChange("")}
          data-testid="fraccion-clear"
          aria-label="Quitar fracción"
          className="text-teal-600 hover:text-red-500 text-xl leading-none flex-shrink-0"
        >
          ×
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 180)}
        placeholder="Buscar por código (52, 5208…) o tela (algodón, seda…)"
        data-testid="fraccion-input"
        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 bg-white transition-colors"
      />
      {open && filtered.length > 0 && (
        <div
          data-testid="fraccion-results"
          className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto"
        >
          {filtered.map((f) => (
            <button
              key={f.code}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(f.code);
                if (onSelect) onSelect(f);
                setQuery("");
                setOpen(false);
              }}
              data-testid={`fraccion-opt-${f.code}`}
              className="w-full text-left px-3 py-2 hover:bg-teal-50 border-b border-slate-100 last:border-0 transition-colors"
            >
              <div className="flex justify-between items-start gap-2">
                <p className="text-sm font-medium text-slate-800">{f.code}</p>
                <span className="text-[10px] font-medium text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded whitespace-nowrap">
                  {f.arancel}% · {f.cuotaComp}%
                </span>
              </div>
              <p className="text-xs text-slate-500">{f.desc}</p>
            </button>
          ))}
        </div>
      )}
      {open && query.length > 0 && filtered.length === 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg p-3">
          <p className="text-xs text-slate-500">
            Sin resultados. Confirma el código con tu agente aduanal.
          </p>
        </div>
      )}
    </div>
  );
};


const FinBlock = ({ f, set }) => (
  <Card>
    <Toggle
      value={f.financiamiento}
      onChange={set("financiamiento")}
      label="Financiamiento"
      testId="financiamiento-toggle"
    />
    {f.financiamiento && (
      <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-slate-100">
        <Field label="Tasa anual">
          <NumInput value={f.tasa} onChange={set("tasa")} suffix="%" testId="fin-tasa" />
        </Field>
        <Field label="Días financiados">
          <NumInput value={f.dias} onChange={set("dias")} suffix="días" testId="fin-dias" />
        </Field>
        <div className="col-span-2 text-xs text-slate-400">
          Costo = FOB MXN × (tasa% ÷ 365) × días
        </div>
      </div>
    )}
  </Card>
);

const PrecioCliente = ({ cxu, f, set, unL, cxMetro = 0, rendimiento = 0 }) => {
  const util = +f.utilidad || 0;
  const pv = util > 0 && util < 100 ? cxu / (1 - util / 100) : 0;
  const pvMetro = cxMetro > 0 && util > 0 && util < 100 ? cxMetro / (1 - util / 100) : 0;
  const puedeMostrarMetro = cxMetro > 0;
  const mostrar = f.costoMostrar || "kg";
  // Utilidad real del precio capturado a mano, como % sobre la venta.
  const costoRef = mostrar === "metro" && cxMetro > 0 ? cxMetro : cxu;
  const pvManual = +f.precioVentaManual || 0;
  const utilReal = pvManual > 0 && costoRef > 0 ? pvManual - costoRef : null;
  const margenReal = utilReal !== null ? (utilReal / pvManual) * 100 : null;

  return (
    <Card>
      <SecTitle>Precio al cliente (opcional)</SecTitle>

      {/* Selector de costo a mostrar */}
      {puedeMostrarMetro && (
        <div className="mb-3">
          <p className="text-xs text-slate-500 font-medium mb-1">Costo a mostrar en PDF/Excel</p>
          <div className="flex gap-2" data-testid="costo-mostrar-selector">
            {[
              ["kg", "Por kilogramo"],
              ["metro", "Por metro"],
            ].map(([v, l]) => (
              <button
                key={v}
                type="button"
                onClick={() => set("costoMostrar")(v)}
                data-testid={`costo-mostrar-${v}`}
                className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-colors ${
                  mostrar === v
                    ? "bg-teal-600 text-white"
                    : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      )}

      <Field label="Utilidad esperada (calculadora)">
        <NumInput
          value={f.utilidad}
          onChange={set("utilidad")}
          suffix="%"
          testId="utilidad-input"
        />
      </Field>
      {pv > 0 && (
        <div className="mt-2 mb-3 p-2 bg-slate-50 rounded-lg">
          <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Sugerido</p>
          <p className="text-sm text-slate-700 font-medium">
            {mostrar === "metro" && pvMetro > 0
              ? `${mxn(pvMetro)} / metro`
              : `${mxn(pv)} / ${unL}`}
          </p>
        </div>
      )}

      <Field label={`Precio de venta al cliente / ${mostrar === "metro" ? "metro" : unL}`}>
        <NumInput
          value={f.precioVentaManual}
          onChange={set("precioVentaManual")}
          prefix="$"
          suffix="MXN"
          testId="precio-venta-manual"
        />
      </Field>
      {utilReal !== null && (
        <div
          className={`mt-2 p-2 rounded-lg ${utilReal < 0 ? "bg-rose-50" : "bg-emerald-50"}`}
          data-testid="utilidad-real"
        >
          <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Utilidad real (sobre la venta)</p>
          <p className={`text-sm font-semibold ${utilReal < 0 ? "text-rose-600" : "text-emerald-700"}`}>
            {margenReal.toFixed(1)}% · {mxn(utilReal)} / {mostrar === "metro" && cxMetro > 0 ? "metro" : unL}
          </p>
          <p className="text-[10px] text-slate-400 mt-0.5">
            (Precio de venta − costo {mxn(costoRef)}) ÷ precio de venta
          </p>
        </div>
      )}
      <p className="text-xs text-slate-400 mt-1">
        Déjalo en blanco hasta negociar con el cliente. Solo aparecerá en PDF/Excel cuando lo captures.
      </p>
    </Card>
  );
};

// ─── MODO SIMPLE ──────────────────────────────────────────────────────────────
const initSimple = {
  nombre: "",
  numeroOrden: "",
  tela: "",
  itemProveedor: "",
  peso: "",
  ancho: "",
  composicion: "",
  proveedor: "",
  fobUSD: "",
  tipoCambio: "17.50",
  contenedor: "40'HC",
  unidad: "kg",
  cantidad: "",
  costoImportacion: String(COSTO_IMPORTACION_DEFAULT),
  fleteTerrestre: String(FLETE_DEFAULT),
  financiamiento: false,
  tasa: TASA_FIN_DEFAULT,
  dias: DIAS_FIN_DEFAULT,
  utilidad: "25",
  tcArribo: "",
  capContenedor: "",
  precioVentaManual: "",
  costoMostrar: "kg",
  status: "Borrador",
};

function ModoSimple({ onSave, editingQuote, mode, onCancelEdit, quotes, onBulkImport }) {
  const [f, setF] = useState(initSimple);
  const [saved, setSaved] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  // Cargar datos cuando se está editando
  useEffect(() => {
    if (editingQuote && editingQuote.modo === "Simple") {
      setF({
        nombre: editingQuote.nombre || "",
        numeroOrden: editingQuote.numeroOrden || "",
        tela: editingQuote.tela || "",
        itemProveedor: editingQuote.itemProveedor || "",
        peso: String(editingQuote.peso ?? ""),
        ancho: String(editingQuote.ancho ?? ""),
        composicion: editingQuote.composicion || "",
        proveedor: editingQuote.proveedor || "",
        fobUSD: String(editingQuote.fobUSD ?? ""),
        tipoCambio: String(editingQuote.tipoCambio || "17.50"),
        contenedor: editingQuote.contenedor || "40'",
        unidad: editingQuote.unidad || "metros",
        cantidad: String(editingQuote.cantidad ?? ""),
        costoImportacion: String(editingQuote.costoImportacion ?? ""),
        fleteTerrestre: String(editingQuote.fleteTerrestre ?? ""),
        financiamiento: editingQuote.financiamiento || false,
        tasa: +editingQuote.tasaAnual > 0 ? String(editingQuote.tasaAnual) : TASA_FIN_DEFAULT,
        dias: +editingQuote.dias > 0 ? String(editingQuote.dias) : DIAS_FIN_DEFAULT,
        utilidad: String(editingQuote.utilidad ?? ""),
        tcArribo: String(editingQuote.tcArribo ?? ""),
        capContenedor: String(editingQuote.capContenedor ?? ""),
        precioVentaManual: String(editingQuote.precioVentaManual ?? ""),
        costoMostrar: editingQuote.costoMostrar || "kg",
        status: editingQuote.status || "Borrador",
      });
    }
  }, [editingQuote]);

  const fob = +f.fobUSD || 0,
    cantidad = +f.cantidad || 0,
    tc = +f.tipoCambio || 17.5;
  // El costo se reparte sobre la capacidad del contenedor si se capturó; si no, sobre la cantidad.
  const capCtr = +f.capContenedor || 0;
  const qty = cantidadParaCosto(capCtr, cantidad);
  const ci = +f.costoImportacion || 0,
    ft = +f.fleteTerrestre || 0;
  const tasaA = +f.tasa || 0,
    dias = +f.dias || 0,
    util = +f.utilidad || 0;
  const peso = +f.peso || 0;
  const anchoCm = +f.ancho || 0;
  const isKg = f.unidad === "kg";
  const rendimiento =
    isKg && peso > 0 && anchoCm > 0 ? 100000 / (peso * anchoCm) : 0;
  const fobMXN = fob * qty * tc;
  const finMXN =
    f.financiamiento && fobMXN > 0 && dias > 0
      ? fobMXN * (tasaA / 100 / 365) * dias
      : 0;
  const total = fobMXN + finMXN + ci + ft;
  const cxu = qty > 0 ? total / qty : 0;
  const cxMetro = isKg && rendimiento > 0 && cxu > 0 ? cxu / rendimiento : 0;
  const pv = util > 0 && util < 100 ? cxu / (1 - util / 100) : 0;
  const pvMetro = cxMetro > 0 && util > 0 && util < 100 ? cxMetro / (1 - util / 100) : 0;
  const unL = f.unidad === "metros" ? "metro" : "kg";
  const canSave =
    f.nombre && f.proveedor && f.tela && qty > 0 && fob > 0 && ci > 0;
  const isEditing = !!editingQuote && mode === "edit";
  const isDuplicating = !!editingQuote && mode === "duplicate";
  const hasOp = isEditing || isDuplicating;

  const handleSave = () => {
    if (!canSave) return;
    const quote = {
      id: isEditing ? editingQuote.id : Date.now(),
      fecha: isEditing ? editingQuote.fecha : hoy(),
      modo: "Simple",
      nombre: f.nombre,
      numeroOrden: f.numeroOrden,
      tela: f.tela,
      itemProveedor: f.itemProveedor,
      peso: peso > 0 ? peso : "",
      ancho: anchoCm > 0 ? anchoCm : "",
      composicion: f.composicion,
      proveedor: f.proveedor,
      contenedor: f.contenedor,
      unidad: f.unidad,
      cantidad,
      fobUSD: fob,
      tipoCambio: tc,
      fobTotalMXN: fobMXN,
      costoImportacion: ci,
      fleteTerrestre: ft,
      financiamiento: f.financiamiento,
      tasaAnual: tasaA,
      dias,
      financiamientoMXN: finMXN,
      totalCosto: total,
      costoPorUnidad: cxu,
      rendimiento,
      costoPorMetro: cxMetro,
      utilidad: util,
      precioVenta: pv,
      precioVentaMetro: pvMetro,
      tcArribo: +f.tcArribo || null,
      capContenedor: +f.capContenedor || null,
      precioVentaManual: +f.precioVentaManual || null,
      costoMostrar: f.costoMostrar || "kg",
      status: f.status || "Borrador",
    };
    onSave(quote, isEditing);
    setSaved(true);
    setTimeout(() => {
      setSaved(false);
      setF(initSimple);
    }, 2000);
  };

  const handleCancel = () => {
    setF(initSimple);
    onCancelEdit();
  };

  return (
    <div className="flex flex-col gap-4 pb-6" data-testid="modo-simple">
      {hasOp && <OpBanner name={editingQuote.nombre} mode={mode} />}
      {!hasOp && (
        <ImportarLibroOC
          quotes={quotes}
          onBulkImport={onBulkImport}
          onImport={(a) => {
            setF((p) => ({
              ...p,
              nombre: a.cliente || p.nombre,
              numeroOrden: a.noOrden || p.numeroOrden,
              tela: a.tela ? `${a.tela}${a.ref ? " · " + a.ref : ""}` : p.tela,
              itemProveedor: a.ref || p.itemProveedor,
              peso: a.peso ? String(a.peso) : p.peso,
              ancho: a.ancho ? String(a.ancho) : p.ancho,
              composicion: a.composicion || p.composicion,
              proveedor: a.proveedor || p.proveedor,
              fobUSD: a.fobUSD ? String(a.fobUSD) : p.fobUSD,
              unidad: a.unidad || p.unidad,
              cantidad: a.cantidad ? String(a.cantidad) : p.cantidad,
              capContenedor: a.capContenedor ? String(a.capContenedor) : p.capContenedor,
            }));
          }}
        />
      )}
      <Card>
        <SecTitle>Identificación</SecTitle>
        <div className="grid grid-cols-1 gap-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nombre del cliente">
              <TxtInput
                value={f.nombre}
                onChange={set("nombre")}
                placeholder="Ej. Juan Pérez"
                testId="simple-nombre"
              />
            </Field>
            <Field label="No. de orden">
              <TxtInput
                value={f.numeroOrden}
                onChange={set("numeroOrden")}
                placeholder="Opcional"
                testId="simple-orden"
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tipo de tela">
              <TxtInput value={f.tela} onChange={set("tela")} testId="simple-tela" />
            </Field>
            <Field label="No. de ítem (proveedor)">
              <TxtInput
                value={f.itemProveedor}
                onChange={set("itemProveedor")}
                placeholder="Opcional"
                testId="simple-item"
              />
            </Field>
            <Field label="Peso">
              <NumInput
                value={f.peso}
                onChange={set("peso")}
                suffix="g/m²"
                testId="simple-peso"
              />
            </Field>
            <Field label="Ancho">
              <NumInput
                value={f.ancho}
                onChange={set("ancho")}
                suffix="cm"
                testId="simple-ancho"
              />
            </Field>
            <Field label="Composición" span2>
              <TxtInput
                value={f.composicion}
                onChange={set("composicion")}
                placeholder="Ej. 100% algodón · 50% poliéster / 50% algodón"
                testId="simple-composicion"
              />
            </Field>
            <Field label="Proveedor" span2>
              <TxtInput value={f.proveedor} onChange={set("proveedor")} testId="simple-proveedor" />
            </Field>
            <Field label="Estado de la cotización" span2>
              <select
                value={f.status || "Borrador"}
                onChange={(e) => set("status")(e.target.value)}
                data-testid="simple-status"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500"
              >
                <option value="Borrador">📝 Borrador</option>
                <option value="Enviada">📤 Enviada</option>
                <option value="Negociando">🤝 Negociando</option>
                <option value="Cerrada">✅ Cerrada</option>
                <option value="Cancelada">❌ Cancelada</option>
              </select>
            </Field>
          </div>
        </div>
      </Card>

      <Card>
        <SecTitle>Mercancía</SecTitle>
        <div className="grid grid-cols-2 gap-3">
          <Field label="FOB por unidad">
            <NumInput value={f.fobUSD} onChange={set("fobUSD")} prefix="USD" testId="simple-fob" />
          </Field>
          <Field label="Tipo de cambio">
            <NumInput
              value={f.tipoCambio}
              onChange={set("tipoCambio")}
              suffix="MXN"
              testId="simple-tc"
            />
          </Field>
          <Field label="Contenedor">
            <SelInput
              value={f.contenedor}
              onChange={set("contenedor")}
              options={[
                { value: "20'", label: "20'" },
                { value: "40'", label: "40'" },
                { value: "40'HC", label: "40' HC" },
              ]}
              testId="simple-contenedor"
            />
          </Field>
          <Field label="Unidad">
            <SelInput
              value={f.unidad}
              onChange={set("unidad")}
              options={[
                { value: "metros", label: "Metros" },
                { value: "kg", label: "Kilogramos" },
              ]}
              testId="simple-unidad"
            />
          </Field>
          <Field label={`Cantidad (${f.unidad})`} span2>
            <NumInput
              value={f.cantidad}
              onChange={set("cantidad")}
              suffix={f.unidad === "metros" ? "m" : "kg"}
              testId="simple-cantidad"
            />
          </Field>
          <Field label="Tipo de cambio a la llegada (opcional)" span2>
            <NumInput
              value={f.tcArribo}
              onChange={set("tcArribo")}
              suffix="MXN"
              testId="simple-tc-arribo"
            />
          </Field>
          <Field label="Capacidad contenedor (unidades que caben)" span2>
            <NumInput
              value={f.capContenedor}
              onChange={set("capContenedor")}
              suffix={f.unidad === "metros" ? "m" : "kg"}
              testId="simple-cap-ctr"
            />
          </Field>
        </div>
      </Card>

      <FinBlock f={f} set={set} />

      <Card>
        <SecTitle>Costos de importación</SecTitle>
        <div className="flex flex-col gap-3">
          <Field label="Costo global (impuestos, aranceles, seguros, agente aduanal incluidos)">
            <NumInput
              value={f.costoImportacion}
              onChange={set("costoImportacion")}
              prefix="$"
              suffix="MXN"
              testId="simple-costo-importacion"
            />
          </Field>
          <Field label="Flete marítimo">
            <NumInput
              value={f.fleteTerrestre}
              onChange={set("fleteTerrestre")}
              prefix="$"
              suffix="MXN"
              testId="simple-flete-terrestre"
            />
          </Field>
        </div>
      </Card>

      <Card>
        <SecTitle>Desglose</SecTitle>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <Badge label={`FOB total (${qty.toLocaleString("es-MX")} ${f.unidad}${capCtr > 0 ? " · cap. contenedor" : ""})`} value={mxn(fobMXN)} />
          {f.financiamiento && finMXN > 0 && (
            <Badge label={`Financiamiento (${tasaA}% / ${dias}d)`} value={mxn(finMXN)} />
          )}
          <Badge label="Importación global" value={mxn(ci)} />
          <Badge label="Flete marítimo" value={mxn(ft)} />
          <Badge label="Total acumulado" value={mxn(total)} />
        </div>
        <Badge label={`Costo por ${unL} puesto México`} value={mxn(cxu)} accent />
        {capCtr > 0 && (
          <p className="text-[11px] text-slate-500 mt-2" data-testid="cap-ctr-nota">
            Costo calculado sobre la capacidad del contenedor ({capCtr.toLocaleString("es-MX")} {f.unidad}), no sobre la
            cantidad del pedido.
          </p>
        )}
        {isKg && rendimiento > 0 && (
          <div className="mt-3 pt-3 border-t border-slate-100" data-testid="rendimiento-block">
            <p className="text-xs font-semibold text-teal-700 uppercase tracking-wider mb-2">
              Rendimiento
            </p>
            <div className="grid grid-cols-3 gap-2">
              <Badge
                label="Metros por kg"
                value={`${rendimiento.toFixed(2)} m`}
                accent
              />
              <Badge label="Costo / kg" value={mxn(cxu)} />
              <Badge label="Costo / metro" value={mxn(cxMetro)} />
            </div>
            <p className="text-[10px] text-slate-400 mt-2">
              Rendimiento = 100 000 ÷ (peso g/m² × ancho cm)
            </p>
          </div>
        )}
      </Card>

      <PrecioCliente cxu={cxu} f={f} set={set} unL={unL} cxMetro={cxMetro} rendimiento={rendimiento} />
      <SaveBtn onSave={handleSave} disabled={!canSave} saved={saved} isEditing={isEditing} />
      {hasOp && <CancelEditBtn onCancel={handleCancel} />}
    </div>
  );
}

// ─── MODO DETALLADO ───────────────────────────────────────────────────────────
const initDet = {
  nombre: "",
  numeroOrden: "",
  tela: "",
  itemProveedor: "",
  peso: "",
  ancho: "",
  composicion: "",
  proveedor: "",
  fobUSD: "",
  tipoCambio: "17.50",
  unidad: "kg",
  cantidad: "",
  fraccion: "",
  fleteMaritimo: "",
  arancel: "",
  cuotaComp: "",
  agenteAduanal: "",
  fleteTerrestre: "",
  financiamiento: false,
  tasa: TASA_FIN_DEFAULT,
  dias: DIAS_FIN_DEFAULT,
  utilidad: "25",
  tcArribo: "",
  capContenedor: "",
  precioVentaManual: "",
  costoMostrar: "kg",
  status: "Borrador",
};

function ModoDetallado({ onSave, editingQuote, mode, onCancelEdit, quotes, onBulkImport }) {
  const [f, setF] = useState(initDet);
  const [saved, setSaved] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (editingQuote && editingQuote.modo === "Detallado") {
      setF({
        nombre: editingQuote.nombre || "",
        numeroOrden: editingQuote.numeroOrden || "",
        tela: editingQuote.tela || "",
        itemProveedor: editingQuote.itemProveedor || "",
        composicion: editingQuote.composicion || "",
        proveedor: editingQuote.proveedor || "",
        fobUSD: String(editingQuote.fobUSD ?? ""),
        tipoCambio: String(editingQuote.tipoCambio || "17.50"),
        unidad: editingQuote.unidad || "metros",
        cantidad: String(editingQuote.cantidad ?? ""),
        fraccion: editingQuote.fraccion || "",
        fleteMaritimo: String(editingQuote.fleteMaritimo ?? ""),
        arancel: String(editingQuote.arancel ?? ""),
        cuotaComp: String(editingQuote.cuotaComp ?? ""),
        agenteAduanal: String(editingQuote.agenteAduanal ?? ""),
        fleteTerrestre: String(editingQuote.fleteTerrestre ?? ""),
        financiamiento: editingQuote.financiamiento || false,
        tasa: +editingQuote.tasaAnual > 0 ? String(editingQuote.tasaAnual) : TASA_FIN_DEFAULT,
        dias: +editingQuote.dias > 0 ? String(editingQuote.dias) : DIAS_FIN_DEFAULT,
        utilidad: String(editingQuote.utilidad ?? ""),
        tcArribo: String(editingQuote.tcArribo ?? ""),
        capContenedor: String(editingQuote.capContenedor ?? ""),
        precioVentaManual: String(editingQuote.precioVentaManual ?? ""),
        costoMostrar: editingQuote.costoMostrar || "kg",
        status: editingQuote.status || "Borrador",
      });
    }
  }, [editingQuote]);

  const fob = +f.fobUSD || 0,
    cantidad = +f.cantidad || 0,
    tc = +f.tipoCambio || 17.5;
  // El costo se reparte sobre la capacidad del contenedor si se capturó; si no, sobre la cantidad.
  const capCtr = +f.capContenedor || 0;
  const qty = cantidadParaCosto(capCtr, cantidad);
  const fm = +f.fleteMaritimo || 0,
    ar = +f.arancel / 100 || 0,
    cq = +f.cuotaComp / 100 || 0;
  const ag = +f.agenteAduanal || 0,
    ft = +f.fleteTerrestre || 0;
  const tasaA = +f.tasa || 0,
    dias = +f.dias || 0,
    util = +f.utilidad || 0;

  const fobTot = fob * qty;
  const seg = fobTot * 0.004;
  const cifUSD = fobTot + seg + fm;
  const cifMXN = cifUSD * tc;
  const arMXN = cifMXN * ar,
    cqMXN = cifMXN * cq,
    dta = cifMXN * 0.008;
  const iva = (cifMXN + arMXN + cqMXN + dta) * 0.16;
  const finMXN =
    f.financiamiento && dias > 0
      ? fobTot * tc * (tasaA / 100 / 365) * dias
      : 0;
  const total = cifMXN + arMXN + cqMXN + dta + iva + ag + ft + finMXN;
  const cxu = qty > 0 ? total / qty : 0;
  const pv = util > 0 && util < 100 ? cxu / (1 - util / 100) : 0;
  const unL = f.unidad === "metros" ? "metro" : "kg";
  const canSave = f.nombre && f.proveedor && f.tela && qty > 0 && fob > 0;
  const isEditing = !!editingQuote && mode === "edit";
  const isDuplicating = !!editingQuote && mode === "duplicate";
  const hasOp = isEditing || isDuplicating;

  const rows = [
    { l: "Valor FOB", v: fobTot * tc },
    { l: "Seguro marítimo (0.4%)", v: seg * tc },
    { l: "Flete marítimo", v: fm * tc },
    { l: "= Valor CIF", v: cifMXN, bold: true },
    { l: `Arancel (${f.arancel || 0}%)`, v: arMXN },
    { l: `Cuota compensatoria (${f.cuotaComp || 0}%)`, v: cqMXN },
    { l: "DTA (0.8%)", v: dta },
    { l: "IVA 16%", v: iva },
    { l: "Agente aduanal", v: ag },
    { l: "Flete terrestre", v: ft },
    ...(f.financiamiento && finMXN > 0
      ? [{ l: `Financiamiento (${tasaA}% anual / ${dias}d)`, v: finMXN }]
      : []),
  ];

  const handleSave = () => {
    if (!canSave) return;
    const quote = {
      id: isEditing ? editingQuote.id : Date.now(),
      fecha: isEditing ? editingQuote.fecha : hoy(),
      modo: "Detallado",
      nombre: f.nombre,
      numeroOrden: f.numeroOrden,
      tela: f.tela,
      itemProveedor: f.itemProveedor,
      composicion: f.composicion,
      proveedor: f.proveedor,
      unidad: f.unidad,
      cantidad,
      fobUSD: fob,
      tipoCambio: tc,
      fobTotalMXN: fobTot * tc,
      fraccion: f.fraccion,
      fleteMaritimo: fm,
      arancel: +f.arancel || 0,
      cuotaComp: +f.cuotaComp || 0,
      agenteAduanal: ag,
      fleteTerrestre: ft,
      financiamiento: f.financiamiento,
      tasaAnual: tasaA,
      dias,
      financiamientoMXN: finMXN,
      totalCosto: total,
      costoPorUnidad: cxu,
      utilidad: util,
      precioVenta: pv,
      tcArribo: +f.tcArribo || null,
      capContenedor: +f.capContenedor || null,
      precioVentaManual: +f.precioVentaManual || null,
      costoMostrar: f.costoMostrar || "kg",
      status: f.status || "Borrador",
    };
    onSave(quote, isEditing);
    setSaved(true);
    setTimeout(() => {
      setSaved(false);
      setF(initDet);
    }, 2000);
  };

  const handleCancel = () => {
    setF(initDet);
    onCancelEdit();
  };

  return (
    <div className="flex flex-col gap-4 pb-6" data-testid="modo-detallado">
      {hasOp && <OpBanner name={editingQuote.nombre} mode={mode} />}
      {!hasOp && (
        <ImportarLibroOC
          quotes={quotes}
          onBulkImport={onBulkImport}
          onImport={(a) => {
            setF((p) => ({
              ...p,
              nombre: a.cliente || p.nombre,
              numeroOrden: a.noOrden || p.numeroOrden,
              tela: a.tela ? `${a.tela}${a.ref ? " · " + a.ref : ""}` : p.tela,
              itemProveedor: a.ref || p.itemProveedor,
              composicion: a.composicion || p.composicion,
              proveedor: a.proveedor || p.proveedor,
              fobUSD: a.fobUSD ? String(a.fobUSD) : p.fobUSD,
              unidad: a.unidad || p.unidad,
              cantidad: a.cantidad ? String(a.cantidad) : p.cantidad,
              capContenedor: a.capContenedor ? String(a.capContenedor) : p.capContenedor,
            }));
          }}
        />
      )}
      <Card>
        <SecTitle>Identificación</SecTitle>
        <div className="grid grid-cols-1 gap-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nombre del cliente">
              <TxtInput value={f.nombre} onChange={set("nombre")} testId="det-nombre" />
            </Field>
            <Field label="No. de orden">
              <TxtInput
                value={f.numeroOrden}
                onChange={set("numeroOrden")}
                placeholder="Opcional"
                testId="det-orden"
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tipo de tela">
              <TxtInput value={f.tela} onChange={set("tela")} testId="det-tela" />
            </Field>
            <Field label="No. de ítem (proveedor)">
              <TxtInput
                value={f.itemProveedor}
                onChange={set("itemProveedor")}
                placeholder="Opcional"
                testId="det-item"
              />
            </Field>
            <Field label="Composición" span2>
              <TxtInput
                value={f.composicion}
                onChange={set("composicion")}
                placeholder="Ej. 100% algodón · 50% poliéster / 50% algodón"
                testId="det-composicion"
              />
            </Field>
            <Field label="Proveedor" span2>
              <TxtInput value={f.proveedor} onChange={set("proveedor")} testId="det-proveedor" />
            </Field>
            <Field label="Estado de la cotización" span2>
              <select
                value={f.status || "Borrador"}
                onChange={(e) => set("status")(e.target.value)}
                data-testid="det-status"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500"
              >
                <option value="Borrador">📝 Borrador</option>
                <option value="Enviada">📤 Enviada</option>
                <option value="Negociando">🤝 Negociando</option>
                <option value="Cerrada">✅ Cerrada</option>
                <option value="Cancelada">❌ Cancelada</option>
              </select>
            </Field>
          </div>
        </div>
      </Card>

      <Card>
        <SecTitle>Mercancía</SecTitle>
        <div className="grid grid-cols-2 gap-3">
          <Field label="FOB por unidad">
            <NumInput value={f.fobUSD} onChange={set("fobUSD")} prefix="USD" testId="det-fob" />
          </Field>
          <Field label="Tipo de cambio">
            <NumInput
              value={f.tipoCambio}
              onChange={set("tipoCambio")}
              suffix="MXN"
              testId="det-tc"
            />
          </Field>
          <Field label="Unidad">
            <SelInput
              value={f.unidad}
              onChange={set("unidad")}
              options={[
                { value: "metros", label: "Metros" },
                { value: "kg", label: "Kilogramos" },
              ]}
            />
          </Field>
          <Field label="Cantidad">
            <NumInput
              value={f.cantidad}
              onChange={set("cantidad")}
              suffix={f.unidad === "metros" ? "m" : "kg"}
              testId="det-cantidad"
            />
          </Field>
          <Field label="Tipo de cambio a la llegada (opcional)" span2>
            <NumInput
              value={f.tcArribo}
              onChange={set("tcArribo")}
              suffix="MXN"
              testId="det-tc-arribo"
            />
          </Field>
          <Field label="Capacidad contenedor (unidades que caben)" span2>
            <NumInput
              value={f.capContenedor}
              onChange={set("capContenedor")}
              suffix={f.unidad === "metros" ? "m" : "kg"}
              testId="det-cap-ctr"
            />
          </Field>
        </div>
      </Card>

      <Card>
        <SecTitle>Internacional</SecTitle>
        <Field label="Fracción arancelaria (referencia)">
          <FraccionPicker
            value={f.fraccion}
            onChange={set("fraccion")}
            onSelect={(fr) => {
              set("arancel")(String(fr.arancel ?? ""));
              set("cuotaComp")(String(fr.cuotaComp ?? ""));
            }}
          />
        </Field>
        <p className="text-xs text-slate-400 mt-1 mb-3">
          Al seleccionar se autollenan Arancel y Cuota comp. · Puedes editarlos manualmente
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Flete marítimo">
            <NumInput
              value={f.fleteMaritimo}
              onChange={set("fleteMaritimo")}
              prefix="USD"
              testId="det-flete-maritimo"
            />
          </Field>
          <Field label="Arancel %">
            <NumInput value={f.arancel} onChange={set("arancel")} suffix="%" testId="det-arancel" />
          </Field>
          <Field label="Cuota compensatoria %" span2>
            <NumInput
              value={f.cuotaComp}
              onChange={set("cuotaComp")}
              suffix="%"
              testId="det-cuota-comp"
            />
          </Field>
        </div>
      </Card>

      <Card>
        <SecTitle>Locales</SecTitle>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Agente aduanal">
            <NumInput
              value={f.agenteAduanal}
              onChange={set("agenteAduanal")}
              prefix="$"
              suffix="MXN"
            />
          </Field>
          <Field label="Flete terrestre">
            <NumInput
              value={f.fleteTerrestre}
              onChange={set("fleteTerrestre")}
              prefix="$"
              suffix="MXN"
            />
          </Field>
        </div>
      </Card>

      <FinBlock f={f} set={set} />

      {fob > 0 && qty > 0 && (
        <Card>
          <SecTitle>Desglose de costos</SecTitle>
          <div className="divide-y divide-slate-100">
            {rows.map((r, i) => (
              <div key={i} className={`flex justify-between py-2`}>
                <span
                  className={`text-sm ${
                    r.bold ? "text-teal-700 font-semibold" : "text-slate-500"
                  }`}
                >
                  {r.l}
                </span>
                <span
                  className={`text-sm font-medium ${
                    r.bold ? "text-teal-700" : "text-slate-700"
                  }`}
                >
                  {mxn(r.v)}
                </span>
              </div>
            ))}
            <div className="flex justify-between py-2 font-bold">
              <span className="text-slate-800">TOTAL</span>
              <span className="text-teal-700">{mxn(total)}</span>
            </div>
          </div>
        </Card>
      )}

      <Badge
        label={`Costo por ${unL} puesto México`}
        value={mxn(cxu)}
        accent
      />
      <PrecioCliente cxu={cxu} f={f} set={set} unL={unL} />
      <SaveBtn onSave={handleSave} disabled={!canSave} saved={saved} isEditing={isEditing} />
      {hasOp && <CancelEditBtn onCancel={handleCancel} />}
    </div>
  );
}

// ─── HISTORIAL ────────────────────────────────────────────────────────────────
// ─── STATUS BADGE ─────────────────────────────────────────────────────────────
const STATUS_STYLES = {
  Borrador: { bg: "bg-slate-100", text: "text-slate-600", label: "📝 Borrador" },
  Enviada: { bg: "bg-sky-100", text: "text-sky-700", label: "📤 Enviada" },
  Negociando: { bg: "bg-amber-100", text: "text-amber-700", label: "🤝 Negociando" },
  Cerrada: { bg: "bg-emerald-100", text: "text-emerald-700", label: "✅ Cerrada" },
  Cancelada: { bg: "bg-rose-100", text: "text-rose-700", label: "❌ Cancelada" },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.Borrador;
  return (
    <span
      data-testid={`status-badge-${status || "Borrador"}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${s.bg} ${s.text}`}
    >
      {s.label}
    </span>
  );
}

function Historial({ quotes, onDelete, onEdit, onDuplicate }) {
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("fecha");

  const filtered = quotes
    .filter(
      (q) =>
        !search ||
        [q.proveedor, q.tela, q.nombre].some((v) =>
          v?.toLowerCase().includes(search.toLowerCase())
        )
    )
    .sort((a, b) => {
      if (sortBy === "precio") return a.costoPorUnidad - b.costoPorUnidad;
      if (sortBy === "proveedor")
        return (a.proveedor || "").localeCompare(b.proveedor || "");
      if (sortBy === "tela") return (a.tela || "").localeCompare(b.tela || "");
      return (b.id || 0) - (a.id || 0);
    });

  if (quotes.length === 0)
    return (
      <div className="text-center py-16" data-testid="empty-history">
        <p className="text-5xl mb-3">📋</p>
        <p className="text-slate-500 text-sm">
          Sin cotizaciones guardadas aún.
        </p>
        <p className="text-slate-400 text-xs mt-1">
          Usa Modo Simple o Detallado para crear la primera.
        </p>
      </div>
    );

  return (
    <div className="flex flex-col gap-3 pb-6" data-testid="historial">
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Buscar por proveedor, tela o nombre…"
        data-testid="search-input"
        className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500"
      />

      <div className="flex gap-2 overflow-x-auto -mx-1 px-1">
        {[
          ["fecha", "📅 Fecha"],
          ["proveedor", "🏭 Proveedor"],
          ["tela", "🧵 Tela"],
          ["precio", "💰 Precio"],
        ].map(([v, l]) => (
          <button
            key={v}
            onClick={() => setSortBy(v)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
              sortBy === v
                ? "bg-teal-600 text-white"
                : "bg-white border border-slate-200 text-slate-600"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      <p className="text-xs text-slate-400">
        {filtered.length} {filtered.length !== 1 ? "cotizaciones" : "cotización"}
      </p>

      {filtered.map((q) => (
        <Card key={q.id}>
          <div className="flex justify-between items-start mb-2 gap-2">
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-slate-800 text-sm truncate">
                {q.nombre}
              </p>
              <p className="text-xs text-slate-400">
                {q.fecha} · {q.modo}
                {q.numeroOrden ? ` · OC ${q.numeroOrden}` : ""}
              </p>
              <div className="mt-1">
                <StatusBadge status={q.status} />
              </div>
            </div>
            <div className="flex gap-1 flex-shrink-0">
              <button
                onClick={() => onEdit(q)}
                data-testid={`edit-quote-${q.id}`}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-teal-600 hover:bg-teal-50 transition-colors"
                title="Editar cotización"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                </svg>
              </button>
              <button
                onClick={() => descargarPDFCotizacion(q)}
                data-testid={`pdf-quote-${q.id}`}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-rose-600 hover:bg-rose-50 transition-colors"
                title="Descargar PDF"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <path d="M9 15h6" />
                  <path d="M9 11h6" />
                </svg>
              </button>
              <button
                onClick={() => compartirPDFWhatsApp(q)}
                data-testid={`whatsapp-quote-${q.id}`}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-emerald-600 hover:bg-emerald-50 transition-colors"
                title="Enviar por WhatsApp"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </button>
              <button
                onClick={() => onDuplicate(q)}
                data-testid={`duplicate-quote-${q.id}`}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-sky-600 hover:bg-sky-50 transition-colors"
                title="Duplicar cotización"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                  <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                </svg>
              </button>
              <button
                onClick={() => onDelete(q.id)}
                data-testid={`delete-quote-${q.id}`}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                title="Eliminar cotización"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 6h18" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                  <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-1 text-xs mb-2">
            <span className="text-slate-500">
              Proveedor:{" "}
              <span className="text-slate-700 font-medium">{q.proveedor}</span>
            </span>
            <span className="text-slate-500">
              Tela:{" "}
              <span className="text-slate-700 font-medium">{q.tela}</span>
            </span>
            <span className="text-slate-500">
              FOB:{" "}
              <span className="text-slate-700 font-medium">
                USD {q.fobUSD} · TC {q.tipoCambio}
              </span>
            </span>
            <span className="text-slate-500">
              Cantidad:{" "}
              <span className="text-slate-700 font-medium">
                {q.cantidad?.toLocaleString("es-MX")} {q.unidad}
              </span>
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Badge label="Costo / unidad" value={mxn(q.costoPorUnidad)} accent />
            {(q.precioVentaManual && +q.precioVentaManual > 0) ? (
              <Badge
                label="Precio venta (negociado)"
                value={mxn(+q.precioVentaManual)}
                green
              />
            ) : q.precioVenta > 0 ? (
              <Badge
                label={`Precio venta (${q.utilidad}%)`}
                value={mxn(q.precioVenta)}
                green
              />
            ) : null}
          </div>
        </Card>
      ))}
    </div>
  );
}

// ─── EXPORTAR EXCEL ───────────────────────────────────────────────────────────
function Exportar({ quotes }) {
  const [loading, setLoading] = useState(false);

  const handleExport = () => {
    if (quotes.length === 0) return;
    setLoading(true);
    try {
      const toRow = (q) => ({
        Fecha: q.fecha,
        Cliente: q.nombre,
        "No. Orden": q.numeroOrden || "",
        Estado: q.status || "Borrador",
        Modo: q.modo,
        Proveedor: q.proveedor,
        Tela: q.tela,
        "No. Ítem": q.itemProveedor || "",
        "Peso g/m²": q.peso ?? "",
        "Ancho cm": q.ancho ?? "",
        Composición: q.composicion || "",
        "Fracción Arancelaria": q.fraccion || "",
        "Arancel %": q.arancel ?? "",
        "Cuota Comp. %": q.cuotaComp ?? "",
        Contenedor: q.contenedor || "",
        Unidad: q.unidad,
        Cantidad: q.cantidad,
        "FOB USD/u": q.fobUSD,
        "Tipo de cambio": q.tipoCambio,
        "FOB Total MXN": Math.round(q.fobTotalMXN || 0),
        "Financiamiento MXN": Math.round(q.financiamientoMXN || 0),
        "Importación Global MXN": Math.round(q.costoImportacion || 0),
        "Flete Marítimo MXN": Math.round(q.fleteTerrestre || 0),
        "Total Costo MXN": Math.round(q.totalCosto || 0),
        "Rendimiento m/kg": q.rendimiento > 0 ? +q.rendimiento.toFixed(2) : "",
        "Costo/kg MXN": q.unidad === "kg" && q.costoPorUnidad > 0 ? +q.costoPorUnidad.toFixed(2) : "",
        "Costo/metro MXN": q.costoPorMetro > 0 ? +q.costoPorMetro.toFixed(2) : "",
        "Costo/Unidad MXN": +q.costoPorUnidad?.toFixed(2) || 0,
        "Utilidad %": q.utilidad || 0,
        "Precio Venta/Unidad MXN":
          q.precioVentaManual && +q.precioVentaManual > 0
            ? +(+q.precioVentaManual).toFixed(2)
            : +q.precioVenta?.toFixed(2) || 0,
        "Precio Venta/metro MXN": q.precioVentaMetro > 0 ? +q.precioVentaMetro.toFixed(2) : "",
      });

      const toRowShare = (q) => {
        const preferMetro = q.costoMostrar === "metro" && q.costoPorMetro > 0;
        const costoMostrado = preferMetro
          ? +q.costoPorMetro.toFixed(2)
          : q.unidad === "kg" && q.costoPorUnidad > 0
            ? +q.costoPorUnidad.toFixed(2)
            : q.costoPorUnidad > 0
              ? +q.costoPorUnidad.toFixed(2)
              : "";
        const unidadCosto = preferMetro
          ? "metro"
          : q.unidad === "metros"
            ? "metro"
            : "kg";
        return {
          Fecha: q.fecha,
          Cliente: q.nombre,
          "No. Orden": q.numeroOrden || "",
          Proveedor: q.proveedor,
          Tela: q.tela,
          "Ancho cm": q.ancho ?? "",
          "Peso g/m²": q.peso ?? "",
          Composición: q.composicion || "",
          [`Costo / ${unidadCosto} MXN`]: costoMostrado,
          [`Precio venta / ${unidadCosto} MXN`]:
            q.precioVentaManual && +q.precioVentaManual > 0
              ? +(+q.precioVentaManual).toFixed(2)
              : "",
        };
      };

      const wb = XLSX.utils.book_new();
      const sheets = [
        ["Todas", [...quotes].sort((a, b) => (b.id || 0) - (a.id || 0))],
        [
          "Por Cliente",
          [...quotes].sort((a, b) =>
            (a.nombre || "").localeCompare(b.nombre || "")
          ),
        ],
        [
          "Por Proveedor",
          [...quotes].sort((a, b) =>
            (a.proveedor || "").localeCompare(b.proveedor || "")
          ),
        ],
        [
          "Por Tela",
          [...quotes].sort((a, b) =>
            (a.tela || "").localeCompare(b.tela || "")
          ),
        ],
        [
          "Por Precio",
          [...quotes].sort((a, b) => a.costoPorUnidad - b.costoPorUnidad),
        ],
        ["Por Fecha", [...quotes].sort((a, b) => (b.id || 0) - (a.id || 0))],
      ];

      for (const [name, data] of sheets) {
        const ws = XLSX.utils.json_to_sheet(data.map(toRow));
        ws["!cols"] = Array(30).fill({ wch: 20 });
        XLSX.utils.book_append_sheet(wb, ws, name);
      }

      // Hoja "Compartir" con columnas reducidas para enviar al equipo
      const shareData = [...quotes].sort((a, b) => (b.id || 0) - (a.id || 0));
      const wsShare = XLSX.utils.json_to_sheet(shareData.map(toRowShare));
      wsShare["!cols"] = Array(10).fill({ wch: 20 });
      XLSX.utils.book_append_sheet(wb, wsShare, "Compartir");

      XLSX.writeFile(
        wb,
        `${EMPRESA.nombre}_${new Date().toISOString().slice(0, 10)}.xlsx`
      );
    } catch (e) {
      alert("Error al exportar. Verifica que xlsx esté instalado.");
    }
    setLoading(false);
  };

  return (
    <div className="flex flex-col gap-4 pb-6" data-testid="exportar-tab">
      <Card>
        <SecTitle>Exportar base de datos</SecTitle>
        <p className="text-sm text-slate-500 mb-4">
          {quotes.length}{" "}
          {quotes.length !== 1 ? "cotizaciones guardadas" : "cotización guardada"}{" "}
          · Se generará un archivo Excel con 7 hojas:
        </p>
        <div className="flex flex-col gap-2 mb-5">
          {[
            ["📋", "Todas", "Cotizador completo"],
            ["👤", "Por Cliente", "Agrupado A → Z"],
            ["🏭", "Por Proveedor", "Agrupado A → Z"],
            ["🧵", "Por Tela", "Agrupado A → Z"],
            ["💰", "Por Precio", "Más barato primero"],
            ["📅", "Por Fecha", "Más reciente primero"],
            ["📤", "Compartir", "Versión resumida para el equipo"],
          ].map(([icon, name, desc]) => (
            <div
              key={name}
              className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-xl"
            >
              <span className="text-lg w-7 text-center">{icon}</span>
              <div>
                <p className="text-sm font-medium text-slate-700">{name}</p>
                <p className="text-xs text-slate-400">{desc}</p>
              </div>
            </div>
          ))}
        </div>
        <button
          onClick={handleExport}
          disabled={quotes.length === 0 || loading}
          data-testid="export-excel-btn"
          className={`w-full py-3 rounded-xl font-semibold text-sm transition-all ${
            quotes.length === 0
              ? "bg-slate-200 text-slate-400 cursor-not-allowed"
              : "bg-teal-600 hover:bg-teal-500 text-white shadow-sm"
          }`}
        >
          {loading ? "Generando…" : "⬇ Descargar Excel"}
        </button>
      </Card>
    </div>
  );
}

// ─── COMPARATIVA ──────────────────────────────────────────────────────────────
function Comparar({ quotes }) {
  const [selected, setSelected] = useState([]);

  const toggle = (id) => {
    setSelected((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : prev.length < 3
          ? [...prev, id]
          : prev
    );
  };

  // Preserve selection order so columns reflect click order
  const selectedQuotes = selected
    .map((id) => quotes.find((q) => q.id === id))
    .filter(Boolean);

  if (quotes.length === 0)
    return (
      <div className="text-center py-16" data-testid="empty-comparar">
        <p className="text-5xl mb-3">⚖️</p>
        <p className="text-slate-500 text-sm">
          No hay cotizaciones para comparar.
        </p>
        <p className="text-slate-400 text-xs mt-1">
          Crea al menos 2 cotizaciones para usar el comparativo.
        </p>
      </div>
    );

  const costs = selectedQuotes.map((q) => q.costoPorUnidad);
  const prices = selectedQuotes.filter((q) => q.precioVenta > 0).map((q) => q.precioVenta);
  const minCost = costs.length ? Math.min(...costs) : 0;
  const maxCost = costs.length ? Math.max(...costs) : 0;
  const minPrice = prices.length ? Math.min(...prices) : 0;
  const winner = selectedQuotes.find((q) => q.costoPorUnidad === minCost);
  const savings = maxCost - minCost;

  return (
    <div className="flex flex-col gap-4 pb-6" data-testid="comparar-tab">
      <Card>
        <SecTitle>Selecciona hasta 3 cotizaciones</SecTitle>
        <p className="text-xs text-slate-400 mb-3" data-testid="comparar-count">
          {selected.length}/3 seleccionadas
        </p>
        <div className="flex flex-col gap-1 max-h-96 overflow-y-auto -mx-1 px-1">
          {quotes.map((q) => {
            const isSel = selected.includes(q.id);
            const disabled = !isSel && selected.length >= 3;
            return (
              <label
                key={q.id}
                data-testid={`comparar-option-${q.id}`}
                className={`flex items-start gap-3 p-2.5 rounded-lg transition-colors ${
                  disabled
                    ? "opacity-40 cursor-not-allowed"
                    : "cursor-pointer hover:bg-slate-50"
                } ${isSel ? "bg-teal-50 border border-teal-200" : "border border-transparent"}`}
              >
                <input
                  type="checkbox"
                  checked={isSel}
                  onChange={() => toggle(q.id)}
                  disabled={disabled}
                  className="mt-0.5 w-4 h-4 accent-teal-600 flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">
                    {q.nombre}
                  </p>
                  <p className="text-xs text-slate-500 truncate">
                    {q.proveedor} · {q.tela} · {q.fecha}
                  </p>
                  <p className="text-xs text-teal-700 font-medium mt-0.5">
                    {mxn(q.costoPorUnidad)} / {q.unidad === "metros" ? "metro" : "kg"}
                  </p>
                </div>
              </label>
            );
          })}
        </div>
      </Card>

      {selectedQuotes.length === 1 && (
        <p className="text-center text-sm text-slate-400 py-2">
          Selecciona al menos una más para comparar
        </p>
      )}

      {selectedQuotes.length >= 2 && (
        <>
          <Card>
            <SecTitle>Comparativa lado a lado</SecTitle>
            <div className="overflow-x-auto -mx-4 px-4">
              <table className="w-full text-xs border-collapse" data-testid="comparar-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 pr-2 text-slate-500 font-medium sticky left-0 bg-white">
                      Concepto
                    </th>
                    {selectedQuotes.map((q) => (
                      <th
                        key={q.id}
                        className="text-right py-2 pl-3 text-slate-800 font-semibold min-w-[110px]"
                      >
                        <div className="truncate max-w-[140px]">{q.nombre}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {[
                    ["Proveedor", (q) => q.proveedor],
                    ["Tela", (q) => q.tela],
                    ["Modo", (q) => q.modo],
                    ["Cantidad", (q) => `${q.cantidad?.toLocaleString("es-MX")} ${q.unidad}`],
                    ["FOB / unidad", (q) => `USD ${q.fobUSD}`],
                    ["Tipo de cambio", (q) => q.tipoCambio],
                    ["Total costo", (q) => mxn(q.totalCosto)],
                  ].map(([label, fn]) => (
                    <tr key={label}>
                      <td className="py-2 pr-2 text-slate-500 sticky left-0 bg-white">
                        {label}
                      </td>
                      {selectedQuotes.map((q) => (
                        <td key={q.id} className="py-2 pl-3 text-right text-slate-700">
                          {fn(q)}
                        </td>
                      ))}
                    </tr>
                  ))}

                  {/* Cost per unit - highlighted */}
                  <tr className="bg-teal-50/30">
                    <td className="py-2 pr-2 text-teal-700 font-semibold sticky left-0 bg-teal-50/30">
                      Costo / unidad
                    </td>
                    {selectedQuotes.map((q) => {
                      const isBest = q.costoPorUnidad === minCost && selectedQuotes.length > 1;
                      return (
                        <td
                          key={q.id}
                          className={`py-2 pl-3 text-right font-bold ${
                            isBest
                              ? "text-emerald-700 bg-emerald-50"
                              : "text-teal-700"
                          }`}
                        >
                          {mxn(q.costoPorUnidad)}
                          {isBest && <span className="ml-1">⭐</span>}
                        </td>
                      );
                    })}
                  </tr>

                  {/* Precio venta - highlighted */}
                  {prices.length > 0 && (
                    <tr>
                      <td className="py-2 pr-2 text-slate-500 sticky left-0 bg-white">
                        Precio venta
                      </td>
                      {selectedQuotes.map((q) => {
                        const isBest =
                          q.precioVenta > 0 &&
                          q.precioVenta === minPrice &&
                          prices.length > 1;
                        return (
                          <td
                            key={q.id}
                            className={`py-2 pl-3 text-right font-medium ${
                              isBest ? "text-emerald-700 bg-emerald-50" : "text-slate-700"
                            }`}
                          >
                            {q.precioVenta > 0 ? mxn(q.precioVenta) : "—"}
                          </td>
                        );
                      })}
                    </tr>
                  )}

                  <tr>
                    <td className="py-2 pr-2 text-slate-500 sticky left-0 bg-white">
                      Utilidad
                    </td>
                    {selectedQuotes.map((q) => (
                      <td key={q.id} className="py-2 pl-3 text-right text-slate-700">
                        {q.utilidad > 0 ? `${q.utilidad}%` : "—"}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>

          {/* Winner card */}
          {winner && savings > 0 && (
            <div
              data-testid="comparar-winner"
              className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4"
            >
              <div className="flex items-start gap-3">
                <span className="text-2xl">🏆</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">
                    Más rentable
                  </p>
                  <p className="text-base font-bold text-emerald-900 mt-0.5 truncate">
                    {winner.nombre}
                  </p>
                  <p className="text-xs text-emerald-700 mt-1">
                    {winner.proveedor} · {mxn(winner.costoPorUnidad)} /{" "}
                    {winner.unidad === "metros" ? "metro" : "kg"}
                  </p>
                  <p className="text-sm text-emerald-800 font-medium mt-2">
                    Ahorras {mxn(savings)} por {winner.unidad === "metros" ? "metro" : "kg"}{" "}
                    vs la más cara
                  </p>
                  {winner.cantidad > 0 && (
                    <p className="text-xs text-emerald-700 mt-0.5">
                      Ahorro total estimado:{" "}
                      <span className="font-semibold">
                        {mxn(savings * winner.cantidad)}
                      </span>{" "}
                      en {winner.cantidad?.toLocaleString("es-MX")} {winner.unidad}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}



// ─── APP PRINCIPAL ────────────────────────────────────────────────────────────
export default function App() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("pedidos");
  const [editingQuote, setEditingQuote] = useState(null);
  const [editMode, setEditMode] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [ocParaEditar, setOcParaEditar] = useState(null);

  const editarOrden = (grupo) => {
    setOcParaEditar(grupo);
    setTab("nueva-oc");
  };

  // Pedido de programación → abre "Nueva OC" ya llena con el borrador de un
  // proveedor. Al guardarla, NuevaOC liga el número de OC de vuelta al pedido.
  const [pedidoAbrir, setPedidoAbrir] = useState(null);
  // Para no volver a pedir la contraseña SL al regresar de "Nueva OC" a un pedido SL.
  const [slTokenPedidos, setSlTokenPedidos] = useState(null);
  const confirmarOCPedido = (pedido, grupo, articulosOC = [], slToken = null) => {
    const ultima = (tela) =>
      articulosOC
        .filter((a) => (a.tela || "").toUpperCase() === (tela || "").toUpperCase() && (a.proveedor || "").toUpperCase() === grupo.proveedor.toUpperCase())
        .sort((x, y) => numeroConsecutivo(y.noOrden) - numeroConsecutivo(x.noOrden))[0];
    const items = grupo.articulos.map((a) => {
      const prev = ultima(a.tela) || {};
      const unidad = /kg/i.test(a.unidad || prev.unidad || "") ? "kg" : "metros";
      return {
        cliente: pedido.cliente?.nombre || "",
        proveedor: grupo.proveedor,
        tela: a.tela,
        ref: a.ref || prev.ref || "",
        composicion: prev.composicion || "",
        peso: prev.peso || "",
        ancho: prev.ancho || "",
        unidad,
        precioUnitario: a.colores.find((c) => c.precio != null)?.precio ?? prev.precioUnitario ?? "",
        colores: a.colores.map((c) => ({ color: c.color, cantidad: c.cantidad, unidad, precio: c.precio ?? prev.precioUnitario ?? "" })),
      };
    });
    setOcParaEditar({
      noOrden: "",
      fecha: hoy(),
      incoterm: items.length ? "FOB" : "",
      items,
      _pedido: { id: pedido.id, folio: pedido.folio, proveedor: grupo.proveedor, cliente: pedido.cliente?.nombre, slToken },
    });
    setTab("nueva-oc");
  };

  // Cargar cotizaciones del backend al montar
  useEffect(() => {
    (async () => {
      try {
        const data = await api.get("/quotes");
        if (Array.isArray(data)) setQuotes(data);
      } catch (e) {
        console.error("Failed to load quotes", e);
      }
    })();
  }, []);

  // Auto-sync entre dispositivos: refetch cada 20s y al re-enfocar la ventana.
  // Se pausa mientras el usuario está editando/duplicando para no interrumpir.
  useEffect(() => {
    const isBusy = () => !!editingQuote;
    const fetchLatest = async () => {
      if (isBusy()) return;
      try {
        const data = await api.get("/quotes");
        if (Array.isArray(data)) {
          setQuotes((prev) => {
            // Evita re-render si nada cambió (comparación superficial por id+updated_at)
            const key = (q) => `${q.id}:${q.updated_at || ""}`;
            const prevKey = prev.map(key).sort().join("|");
            const newKey = data.map(key).sort().join("|");
            return prevKey === newKey ? prev : data;
          });
        }
      } catch (e) {
        // silencioso: el usuario verá el error si intenta guardar
      }
    };
    const interval = setInterval(fetchLatest, 20000);
    const onFocus = () => fetchLatest();
    const onVisibility = () => {
      if (document.visibilityState === "visible") fetchLatest();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [editingQuote]);

  const addQuote = async (q, isEditing = false) => {
    setSyncing(true);
    try {
      const saved = await api.post("/quotes", q);
      if (isEditing) {
        setQuotes((p) => p.map((x) => (x.id === saved.id ? saved : x)));
      } else {
        setQuotes((p) => [saved, ...p.filter((x) => x.id !== saved.id)]);
      }
    } catch (e) {
      console.error(e);
    }
    setEditingQuote(null);
    setEditMode(null);
    setSyncing(false);
  };

  const delQuote = async (id) => {
    setQuotes((p) => p.filter((q) => q.id !== id));
    if (editingQuote?.id === id) {
      setEditingQuote(null);
      setEditMode(null);
    }
    try {
      await api.del(`/quotes/${id}`);
    } catch (e) {
      console.error(e);
    }
  };

  // Guarda en el backend un lote de cotizaciones construidas desde artículos del libro.
  // Devuelve { added } para que el widget muestre el resultado.
  const bulkImportArticulos = async (articulos) => {
    if (!articulos || articulos.length === 0) return { added: 0 };
    setSyncing(true);
    const saved = [];
    for (let i = 0; i < articulos.length; i++) {
      const q = articuloAQuote(articulos[i], i + 1);
      try {
        const s = await api.post("/quotes", q);
        if (s?.id) saved.push(s);
      } catch (e) {
        console.error("bulk import falló para", q?.numeroOrden, e);
      }
    }
    if (saved.length > 0) {
      setQuotes((p) => {
        const map = new Map(p.map((x) => [x.id, x]));
        for (const s of saved) map.set(s.id, s);
        return Array.from(map.values()).sort((a, b) => (b.id || 0) - (a.id || 0));
      });
    }
    setSyncing(false);
    return { added: saved.length };
  };

  const startEdit = (quote) => {
    setEditingQuote(quote);
    setEditMode("edit");
    setTab(quote.modo === "Simple" ? "simple" : "detallado");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const startDuplicate = (quote) => {
    setEditingQuote({ ...quote, nombre: (quote.nombre || "") + " (copia)" });
    setEditMode("duplicate");
    setTab(quote.modo === "Simple" ? "simple" : "detallado");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingQuote(null);
    setEditMode(null);
  };

  const handleTabChange = (newTab) => {
    if (editingQuote && newTab !== "simple" && newTab !== "detallado") {
      setEditingQuote(null);
      setEditMode(null);
    }
    // Si el usuario navega a "Nueva OC" desde el menú (no desde "✏️ Editar" en Resumen),
    // que abra en blanco en vez de arrastrar la última orden que se editó.
    if (ocParaEditar && newTab !== "nueva-oc") {
      setOcParaEditar(null);
    }
    setTab(newTab);
  };

  // ─── MENÚ ───────────────────────────────────────────────────────────────────
  // 4 áreas con subpestañas. Los ids internos de `tab` no cambian (los usan
  // Editar OC, Pedidos → Nueva OC, etc.); el menú solo los agrupa.
  // "Detallado" sigue en el código pero fuera del menú: solo se abre al editar
  // una cotización vieja hecha en ese modo.
  const COTIZADOR = [
    { id: "simple", label: "+ Nueva cotización" },
    { id: "historial", label: quotes.length > 0 ? `Mis cotizaciones (${quotes.length})` : "Mis cotizaciones" },
    { id: "comparar", label: "⚖ Comparar" },
    { id: "exportar", label: "⬇ Excel" },
  ];
  const tabsCotizador = ["simple", "detallado", "historial", "comparar", "exportar"];
  const MENU = [
    {
      id: "ventas",
      icon: "🛒",
      label: "Ventas",
      subs: [
        { id: "pedidos", label: "Pedidos" },
        { id: "clientes", label: "Clientes" },
      ],
    },
    { id: "inventario", icon: "📦", label: "Inventario", subs: [{ id: "inventario", label: "Inventario" }] },
    {
      id: "compras",
      icon: "🧾",
      label: "Compras",
      subs: [
        { id: "nueva-oc", label: "Nueva OC" },
        { id: "resumen", label: "Órdenes de compra" },
        { id: "cotizador", label: "Cotizador", badge: quotes.length || null, grupo: tabsCotizador },
      ],
    },
    {
      id: "cxp",
      icon: "💳",
      label: "Cuentas por pagar",
      subs: [
        { id: "cxp", label: "Proveedores" },
        { id: "champtex", label: "Champtex" },
      ],
    },
  ];
  const subActiva = (sub) => (sub.grupo ? sub.grupo.includes(tab) : sub.id === tab);
  const menuActivo = MENU.find((m) => m.subs.some(subActiva)) || MENU[0];
  // Al volver a un área se abre la última subpestaña que se usó en ella.
  const [ultimaSub, setUltimaSub] = useState({});
  useEffect(() => {
    setUltimaSub((u) => (u[menuActivo.id] === tab ? u : { ...u, [menuActivo.id]: tab }));
  }, [tab, menuActivo.id]);
  const abrirSub = (sub) => {
    if (!sub.grupo) return handleTabChange(sub.id);
    const ultima = ultimaSub.compras;
    handleTabChange(sub.grupo.includes(ultima) ? ultima : "simple");
  };
  const abrirMenu = (m) => {
    setMenuMovil(false);
    const ultima = ultimaSub[m.id];
    handleTabChange(m.subs.some((x) => (x.grupo ? x.grupo.includes(ultima) : x.id === ultima)) ? ultima : m.subs[0].id === "cotizador" ? "simple" : m.subs[0].id);
  };
  const [menuMovil, setMenuMovil] = useState(false);

  return (
    <div
      className="min-h-screen bg-slate-50"
      style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}
    >
      {/* Encabezado compacto: logo + menú principal + subpestañas */}
      <div className="sticky top-0 z-20 shadow-sm">
        <div className="bg-black text-white">
          <div className="max-w-[1600px] mx-auto px-3 sm:px-4 h-14 flex items-center gap-2">
            {EMPRESA.logoHorizontal || EMPRESA.logo ? (
              <img
                src={EMPRESA.logoHorizontal || EMPRESA.logo}
                alt={EMPRESA.nombre}
                data-testid="empresa-logo"
                className="h-10 w-auto object-contain shrink-0 mr-2 sm:mr-4"
              />
            ) : (
              <span data-testid="empresa-logo" className="text-lg font-bold tracking-[0.15em] shrink-0 mr-2 sm:mr-4">
                {EMPRESA.nombre}
              </span>
            )}
            <nav className="hidden md:flex items-center gap-1" data-testid="menu-principal">
              {MENU.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => abrirMenu(m)}
                  data-testid={m.subs.length === 1 ? `tab-${m.subs[0].id}` : `menu-${m.id}`}
                  className={`px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors ${
                    menuActivo.id === m.id ? "bg-teal-500 text-white" : "text-slate-300 hover:text-white hover:bg-white/10"
                  }`}
                >
                  <span className="mr-1.5">{m.icon}</span>
                  {m.label}
                </button>
              ))}
            </nav>
            <button
              type="button"
              onClick={() => setMenuMovil((v) => !v)}
              className="md:hidden ml-1 px-3 py-2 rounded-lg text-sm font-semibold bg-white/10 text-white whitespace-nowrap min-w-0 truncate"
              data-testid="menu-movil-btn"
            >
              ☰ {menuActivo.icon} {menuActivo.label}
            </button>
            <div className="ml-auto flex items-center gap-2 text-xs shrink-0">
              {syncing && (
                <span className="text-teal-300 animate-pulse whitespace-nowrap" data-testid="sync-indicator">sincronizando…</span>
              )}
              {user && (
                <>
                  <span className="text-slate-400 truncate hidden lg:inline">{user.email}</span>
                  <button onClick={logout} data-testid="logout-btn" className="text-teal-300 hover:text-teal-100 underline whitespace-nowrap">
                    salir
                  </button>
                </>
              )}
            </div>
          </div>
          {menuMovil && (
            <div className="md:hidden border-t border-white/10 px-3 py-2 grid grid-cols-2 gap-1" data-testid="menu-movil">
              {MENU.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => abrirMenu(m)}
                  className={`px-3 py-2.5 rounded-lg text-sm font-semibold text-left ${
                    menuActivo.id === m.id ? "bg-teal-500 text-white" : "text-slate-200 bg-white/5"
                  }`}
                >
                  <span className="mr-1.5">{m.icon}</span>
                  {m.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Subpestañas del área activa */}
        {menuActivo.subs.length > 1 && (
          <div className="bg-white border-b border-slate-200">
            <div className="max-w-[1600px] mx-auto px-2 sm:px-4 flex overflow-x-auto" data-testid="submenu">
              {menuActivo.subs.map((sub) => (
                <button
                  key={sub.id}
                  type="button"
                  onClick={() => abrirSub(sub)}
                  data-testid={`tab-${sub.id}`}
                  className={`flex-none px-3 py-2.5 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors ${
                    subActiva(sub) ? "border-teal-600 text-teal-700" : "border-transparent text-slate-500 hover:text-slate-700"
                  }`}
                >
                  {sub.label}
                  {sub.badge ? (
                    <span className="ml-1.5 text-[10px] font-semibold bg-slate-100 text-slate-600 rounded-full px-1.5 py-0.5">{sub.badge}</span>
                  ) : null}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Dentro de Cotizador: nueva cotización, lista, comparar y Excel */}
        {tabsCotizador.includes(tab) && (
          <div className="bg-slate-50 border-b border-slate-200">
            <div className="max-w-lg mx-auto px-4 py-2 flex gap-1.5 overflow-x-auto" data-testid="submenu-cotizador">
              {COTIZADOR.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => handleTabChange(c.id)}
                  data-testid={`tab-${c.id}`}
                  className={`flex-none px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap border transition-colors ${
                    tab === c.id || (c.id === "simple" && tab === "detallado")
                      ? "bg-teal-600 border-teal-600 text-white"
                      : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Contenido — Nueva OC y Resumen usan todo el ancho de la pantalla en escritorio
          para poder ver todos los campos de la orden al mismo tiempo; el resto de las
          pestañas conserva la columna centrada angosta. */}
      <div
        className={
          tab === "nueva-oc" || tab === "resumen" || tab === "cxp" || tab === "champtex" ||
          tab === "clientes" || tab === "inventario" || tab === "pedidos"
            ? "px-4 pt-4 max-w-[1600px] mx-auto"
            : "px-4 pt-4 max-w-lg mx-auto"
        }
      >
        {tab === "simple" && (
          <ModoSimple
            onSave={addQuote}
            editingQuote={editingQuote?.modo === "Simple" ? editingQuote : null}
            mode={editMode}
            onCancelEdit={cancelEdit}
            quotes={quotes}
            onBulkImport={bulkImportArticulos}
          />
        )}
        {tab === "detallado" && (
          <ModoDetallado
            onSave={addQuote}
            editingQuote={
              editingQuote?.modo === "Detallado" ? editingQuote : null
            }
            mode={editMode}
            onCancelEdit={cancelEdit}
            quotes={quotes}
            onBulkImport={bulkImportArticulos}
          />
        )}
        {tab === "nueva-oc" && (
          <NuevaOC
            key={ocParaEditar?.noOrden || (ocParaEditar?._pedido ? `ped-${ocParaEditar._pedido.folio}-${ocParaEditar._pedido.proveedor}` : "nueva")}
            ordenExistente={ocParaEditar}
            onVolverPedido={(id) => {
              setPedidoAbrir(id);
              setSlTokenPedidos(ocParaEditar?._pedido?.slToken || null);
              setOcParaEditar(null);
              setTab("pedidos");
            }}
            quotes={quotes}
            onCrearCotizaciones={bulkImportArticulos}
          />
        )}
        {tab === "resumen" && <Resumen quotes={quotes} onEditar={editarOrden} />}
        {tab === "cxp" && <CuentasPorPagar />}
        {tab === "champtex" && <Champtex />}
        {tab === "clientes" && <Clientes />}
        {tab === "pedidos" && <Pedidos onConfirmarOC={confirmarOCPedido} abrirPedidoId={pedidoAbrir} slTokenInicial={slTokenPedidos} />}
        {tab === "inventario" && <Inventario />}
        {tab === "historial" && (
          <Historial
            quotes={quotes}
            onDelete={delQuote}
            onEdit={startEdit}
            onDuplicate={startDuplicate}
          />
        )}
        {tab === "comparar" && <Comparar quotes={quotes} />}
        {tab === "exportar" && <Exportar quotes={quotes} />}
      </div>
    </div>
  );
}
